import React, { Component } from "react";
import SideNavbar from "../components/navbars/SideNavbar";
import NavbarOne from "../components/navbars/NavbarOne";
import Footer from "../../student_module/components/footer/Footer";
import * as Cookies from "es-cookie";
import { Container } from "react-bootstrap";
import { isBrowser } from "react-device-detect";
import { withRouter } from "react-router-dom";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import gql from "graphql-tag";
import * as XLSX from "xlsx";
// Reuse the ERP's existing attendance components (integration, not rebuild):
import StudentAttendanceReportHome from "../components/student_attendance_report/attendanceReport";
// The app's standard checkbox multi-select (same one the students / fee / OMR filter bars use).
import CheckboxDropdown from "../components/common/CheckboxDropdown";
import AttendanceCommandCenter from "../components/attendance_command_center/AttendanceCommandCenter";
import Select from "react-select";
import { attachModuleGuard, AccessDeniedModal } from "../../utils/moduleGuard";
import { canEdit } from "../../utils/modulePermissions";
import {
  getAuthorizedBranchIds,
  getAuthorizedClassIds,
  getAuthorizedSectionIds,
  filterByAuthorizedIds,
} from "../utils/authHelper";
import { scopeBranches, onBranchScopeChange } from "../../utils/branchScope";

// /attendance-regularization → granted by ANY of these three keys (see MODULE_ROUTES in
// moduleAccessGuard — a route is allowed when ANY of its keys is granted).
// module_key ONLY, never module_name: the sidebar group name is shared by sibling
// modules, and can() grants an action when ANY matched row allows it — matching the
// group would let another module's grant leak in.
//
// This page owns the ENTIRE write/delete surface for the route (device CRUD, biometric
// enrolment, log sync, student mapping, manual attendance marking). The report component
// it renders, attendanceReport.jsx, is read-only and only carries the is_view gate.
const ATTENDANCE_REGULARIZATION_MODULE = [
  "Student_Attendance",
  "Student_Attendance_report",
  "Biometric_Attendance",
];

// Compact, branded react-select styling for the header branch filter.
const BRANCH_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minWidth: 190,
    minHeight: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? "#6366f1" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(99,102,241,0.15)" : "none",
    fontSize: 13,
    cursor: "pointer",
    "&:hover": { borderColor: "#6366f1" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  singleValue: (base) => ({ ...base, color: "#1e293b", fontWeight: 700 }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  dropdownIndicator: (base) => ({ ...base, color: "#6366f1", padding: 6 }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    marginTop: 4,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    fontWeight: state.isSelected ? 700 : 500,
    color: state.isSelected ? "#fff" : "#1e293b",
    background: state.isSelected
      ? "#6366f1"
      : state.isFocused
        ? "#eef2ff"
        : "#fff",
    cursor: "pointer",
  }),
};

// Resolve student names/class/section client-side so device-log admission numbers
// show as real students even before the enriched backend is deployed.
const GET_INSTITUTE_STUDENTS = gql`
  query ($institute_id: Int) {
    getNewEducationalStudents(institute_id: $institute_id) {
      student_id
      admission_no
      student_name
      roll_no
      class_name
      class_id
      section_name
      section_id
      branch_id
      branch_name
    }
  }
`;

// Branches of a college — used in the super-admin assign flow (college → branch).
const GET_BRANCHES_QUERY = gql`
  query ($institution_id: Int!) {
    getBranches(institution_id: $institution_id) {
      id
      branch_name
    }
  }
`;

// ─── Holidays ────────────────────────────────────────────────────────────────
// education_mngt.holidays — the same rows the attendance reports read when working out
// working days, so a holiday marked here immediately affects those numbers.
// `date` is unix SECONDS at IST midnight (the resolver normalises whatever it is sent).
const GET_HOLIDAYS_QUERY = gql`
  query ($institute_id: Int!, $branch_ids: String) {
    getAttendanceHolidays(institute_id: $institute_id, branch_ids: $branch_ids) {
      id
      holiday_name
      date
      branch_id
    }
  }
`;
const SAVE_HOLIDAY_MUTATION = gql`
  mutation ($params: AttendanceHolidayInput) {
    saveAttendanceHoliday(params: $params) {
      id
      holiday_name
      date
      branch_id
    }
  }
`;
const DELETE_HOLIDAY_MUTATION = gql`
  mutation ($id: Int!, $institute_id: Int!) {
    deleteAttendanceHoliday(id: $id, institute_id: $institute_id)
  }
`;

/* ═══════════════════════════════════════════════════════════════════════════
 * ATTENDANCE ADMINISTRATION  ·  College Network Console
 * ---------------------------------------------------------------------------
 * College-administration view for a network of branches, each running MULTIPLE
 * biometric devices (College block, Hostel gate, Library …).
 *
 *  • Network Overview   — branch-wise daily attendance, merged across a branch's
 *                         devices, with per-device breakdown + drilldown.
 *  • Branches & Devices — register/monitor every device, grouped by branch.
 *  • Access Management  — per-person × device access matrix INSIDE a branch:
 *                         grant a punch-history ID access to another device,
 *                         which copies their enrollment + pushes the face to
 *                         that device (internal branch face sync).
 *  • Sync Center        — pull/refresh punch logs per device.
 *
 * The previous single-device dashboard is preserved at
 *   AttendanceRegularization.backup.jsx
 * ═══════════════════════════════════════════════════════════════════════════ */

// ─── Date / time utilities ──────────────────────────────────────────────────
const padTwo = (n) => String(n).padStart(2, "0");

// Unix seconds (IST midnight) → "17/07/2026". Shifted by the IST offset rather than read
// in the browser's zone, so a machine west of UTC doesn't render the previous day.
const fmtDMYLocal = (unix) => {
  const n = parseInt(unix);
  if (!n) return "—";
  const d = new Date((n + 19800) * 1000);
  return `${padTwo(d.getUTCDate())}/${padTwo(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};
// "Fri" for the same value — a holiday landing on a Sunday is worth seeing at a glance.
const dowOf = (unix) => {
  const n = parseInt(unix);
  if (!n) return "";
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
    new Date((n + 19800) * 1000).getUTCDay()
  ];
};
const fmt = (d) =>
  `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}`;
const getToday = () => fmt(new Date());
const getLast7 = () => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return fmt(d);
};
const getMonthS = () => {
  const d = new Date();
  return `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-01`;
};

const formatTime = (s) => {
  if (!s) return "—";
  const p = s.split(" ");
  return p.length >= 2 ? p[1].substring(0, 5) : s;
};
const calcDuration = (a, b) => {
  if (!a || !b) return null;
  const parse = (s) => {
    const [dt, t = "00:00:00"] = s.split(" ");
    return new Date(`${dt}T${t}`);
  };
  const ms = parse(b) - parse(a);
  if (isNaN(ms) || ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${padTwo(m)}m`;
};
const durToMins = (dur) => {
  if (!dur) return 0;
  const m = dur.match(/(\d+)h\s+(\d+)m/);
  return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : 0;
};
const minsToStr = (mins) =>
  mins > 0 ? `${Math.floor(mins / 60)}h ${padTwo(mins % 60)}m` : "—";

const QUICK_FILTERS = [
  { key: "today", label: "Today", from: getToday, to: getToday },
  { key: "week", label: "Last 7 Days", from: getLast7, to: getToday },
  { key: "month", label: "This Month", from: getMonthS, to: getToday },
];

// Go-live cutoff: data before this date is the office's test data and is hidden
// everywhere. Captured once (first run) and kept fixed so "today onward" stays
// anchored to the day the console went live for Vignan.
const CUTOFF_LS_KEY = "rizee_attendance_cutoff_v1";
const getCutoff = () => {
  try {
    let c = window.localStorage.getItem(CUTOFF_LS_KEY);
    if (!c) {
      c = getToday();
      window.localStorage.setItem(CUTOFF_LS_KEY, c);
    }
    return c;
  } catch (_) {
    return getToday();
  }
};
const GO_LIVE = getCutoff();
const clampFrom = (d) => (d && d < GO_LIVE ? GO_LIVE : d);

// ─── Shift config ───────────────────────────────────────────────────────────
const SHIFT_START_MIN = 10 * 60;
const SHIFT_END_MIN = 18 * 60;

const timeToMin = (dt) => {
  if (!dt) return null;
  const t = dt.includes(" ") ? dt.split(" ")[1] : dt;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const arrivalStatus = (firstIn) => {
  const min = timeToMin(firstIn);
  if (min === null) return { key: "na", label: "—", color: "#94a3b8" };
  if (min <= SHIFT_START_MIN)
    return { key: "ontime", label: "On Time", color: "#10b981" };
  if (min <= SHIFT_START_MIN + 60)
    return { key: "late", label: "Late", color: "#f59e0b" };
  return { key: "verylate", label: "Very Late", color: "#ef4444" };
};

// ─── Bar visualisation ──────────────────────────────────────────────────────
const BAR_START = 6 * 60;
const BAR_END = 22 * 60;
const BAR_RANGE = BAR_END - BAR_START;
const getBarPos = (firstIn, lastOut) => {
  const inMin = timeToMin(firstIn);
  if (inMin === null) return null;
  const outMin = lastOut ? timeToMin(lastOut) : inMin + 10;
  const left = Math.max(
    0,
    Math.min(100, ((inMin - BAR_START) / BAR_RANGE) * 100),
  );
  const right = Math.max(
    0,
    Math.min(100, ((outMin - BAR_START) / BAR_RANGE) * 100),
  );
  return { left: `${left}%`, width: `${Math.max(1.5, right - left)}%` };
};

// ─── Log grouping (merges punches by date+user across one or many devices) ──
const buildDateGroups = (logs, userMap = {}) => {
  const map = {};
  logs.forEach((l) => {
    const date = l.datetime ? l.datetime.split(" ")[0] : "Unknown";
    const uid = String(l.device_user_id);
    const fallback = userMap[uid] || {};
    if (!map[date]) map[date] = {};
    if (!map[date][uid])
      map[date][uid] = {
        punches: [],
        name: l.name || fallback.name || null,
        employee_type: l.employee_type || fallback.employee_type || null,
        className: l.class_name || null,
        sectionName: l.section_name || null,
      };
    map[date][uid].punches.push(l.datetime);
    if (!map[date][uid].name)
      map[date][uid].name = l.name || fallback.name || null;
    if (!map[date][uid].className && l.class_name)
      map[date][uid].className = l.class_name;
    if (!map[date][uid].sectionName && l.section_name)
      map[date][uid].sectionName = l.section_name;
  });
  return Object.keys(map)
    .sort((a, b) => b.localeCompare(a))
    .map((date) => {
      const users = Object.keys(map[date])
        .sort((a, b) => Number(a) - Number(b))
        .map((uid) => {
          const { punches, name, employee_type, className, sectionName } =
            map[date][uid];
          const sorted = [...punches].sort();
          const firstIn = sorted[0];
          const lastOut = sorted.length > 1 ? sorted[sorted.length - 1] : null;
          return {
            userId: uid,
            name,
            employee_type,
            className,
            sectionName,
            firstIn,
            lastOut,
            duration: calcDuration(firstIn, lastOut),
            count: sorted.length,
          };
        });
      return { date, users };
    });
};

const summariseGroups = (dateGroups) =>
  dateGroups.map(({ date, users }) => {
    let onTime = 0,
      late = 0,
      veryLate = 0,
      totalMins = 0,
      durCnt = 0;
    users.forEach((u) => {
      const s = arrivalStatus(u.firstIn);
      if (s.key === "ontime") onTime++;
      else if (s.key === "late") late++;
      else if (s.key === "verylate") veryLate++;
      const m = durToMins(u.duration);
      if (m > 0) {
        totalMins += m;
        durCnt++;
      }
    });
    return {
      date,
      total: users.length,
      onTime,
      late,
      veryLate,
      avgDur: durCnt > 0 ? minsToStr(Math.round(totalMins / durCnt)) : null,
    };
  });

// ─── Inline SVG charts ──────────────────────────────────────────────────────
const DonutRing = ({ pct, color, label, sub, size = 116 }) => {
  const R = 36,
    C = 2 * Math.PI * R;
  const dash = Math.max(0, Math.min(C, (pct / 100) * C));
  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth="12"
        />
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeDasharray={`${dash} ${C}`}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          style={{ transition: "stroke-dasharray 0.6s ease" }}
        />
        <text
          x="50"
          y="47"
          textAnchor="middle"
          fontSize="19"
          fontWeight="800"
          fill="#1e293b"
        >
          {pct}%
        </text>
        <text x="50" y="60" textAnchor="middle" fontSize="8.5" fill="#94a3b8">
          {sub}
        </text>
      </svg>
      <div style={{ fontSize: 11, fontWeight: 700, color, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
};

const TrendBars = ({ data }) => {
  if (!data || data.length < 1)
    return <div style={S.chartEmpty}>Not enough data</div>;
  const W = 440,
    H = 130,
    PAD = 16,
    LM = 28;
  const CW = W - LM - 8;
  const n = data.length;
  const max = Math.max(...data.map((d) => d.total), 1);
  const yTicks = [0, Math.round(max / 3), Math.round((max * 2) / 3), max];
  const slot = CW / n;
  const barW = Math.max(4, Math.min(24, slot * 0.6));
  const step = n > 14 ? Math.ceil(n / 8) : 1;
  const baseline = PAD + (H - PAD * 2);
  const totalH = H + 26;
  return (
    <svg
      width="100%"
      height={totalH}
      viewBox={`0 0 ${W} ${totalH}`}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="1" />
          <stop offset="100%" stopColor="#818cf8" stopOpacity="0.75" />
        </linearGradient>
      </defs>
      {yTicks.map((val) => {
        const y = PAD + (1 - val / max) * (H - PAD * 2);
        return (
          <g key={val}>
            <line
              x1={LM}
              x2={W - 4}
              y1={y}
              y2={y}
              stroke="#e8ecf3"
              strokeWidth="1"
              strokeDasharray={val === 0 ? "0" : "4 3"}
            />
            <text
              x={LM - 5}
              y={y + 3.5}
              textAnchor="end"
              fontSize="9"
              fill="#94a3b8"
            >
              {val}
            </text>
          </g>
        );
      })}
      <line
        x1={LM}
        x2={W - 4}
        y1={baseline}
        y2={baseline}
        stroke="#e2e8f0"
        strokeWidth="1.5"
      />
      {data.map((d, i) => {
        const cx = LM + slot * i + slot / 2;
        const barH = Math.max(2, (d.total / max) * (H - PAD * 2));
        const barY = baseline - barH;
        const isLast = i === n - 1;
        const showLbl = i % step === 0 || isLast;
        return (
          <g key={i}>
            <rect
              x={cx - barW / 2}
              y={barY}
              width={barW}
              height={barH}
              fill="url(#bg)"
              rx="3"
              ry="3"
            />
            {showLbl && (
              <text
                x={cx}
                y={Math.max(PAD + 1, barY - 5)}
                textAnchor="middle"
                fontSize="9.5"
                fontWeight="700"
                fill="#4f46e5"
              >
                {d.total}
              </text>
            )}
            {showLbl && (
              <text
                x={cx}
                y={H + 20}
                textAnchor="middle"
                fontSize="8.5"
                fill="#94a3b8"
              >
                {d.date.slice(5)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ─── Backend config ─────────────────────────────────────────────────────────
// The attendance REST API lives on the SAME backend as GraphQL, so derive its
// origin from REACT_APP_API_URL (the per-branch backend the deploy injects, e.g.
// the prod backend on college.rizee.in). We strip the trailing "/graphql" to get
// the origin. Falls back to localhost:3008 for local dev (npm start) where the env
// var isn't set. This avoids a deployed HTTPS site calling http://localhost (which
// browsers block via the "Apps on device" / Local Network Access permission).
const _BACKEND_BASE = (
  process.env.REACT_APP_API_URL || "http://localhost:3008/graphql"
).replace(/\/graphql\/?$/, "");
const API_BASE = `${_BACKEND_BASE}/api/v1/attendance`;
const PRIMARY_SERIAL = "QJT3243300480";
const DEVICES_LS_KEY = "rizee_attendance_devices_v2";

const jpost = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

const api = {
  syncedLogs: (deviceId, fromDate, toDate, inst) =>
    jpost(`${API_BASE}/synced-logs`, {
      fromDate,
      toDate,
      device_id: deviceId,
      institute_id: inst,
    }),
  users: (deviceId) =>
    fetch(`${API_BASE}/users?device_id=${encodeURIComponent(deviceId)}`),
  saveUsers: (deviceId, users) =>
    jpost(`${API_BASE}/users/save`, { device_id: deviceId, users }),
  syncUsers: (deviceId, instituteId) =>
    jpost(`${API_BASE}/sync-users`, {
      device_id: deviceId,
      institute_id: instituteId,
    }),
  pullDevice: (deviceId, fromDate, toDate, instituteId, branchId) =>
    jpost(`${API_BASE}/sync`, {
      device_id: deviceId,
      fromDate,
      toDate,
      institute_id: instituteId,
      branch_id: branchId,
    }),
  listDevices: (inst) =>
    fetch(`${API_BASE}/devices${inst ? `?institute_id=${inst}` : ""}`),
  saveDevice: (payload) => jpost(`${API_BASE}/devices`, payload),
  removeDevice: (id) => jpost(`${API_BASE}/devices/delete`, { id }),
  grantAccess: (payload) => jpost(`${API_BASE}/grant-access`, payload),
  revokeAccess: (payload) => jpost(`${API_BASE}/revoke-access`, payload),
  mappingStatus: (deviceId, inst, fromDate, toDate) =>
    fetch(
      `${API_BASE}/mapping-status?device_id=${encodeURIComponent(deviceId)}&institute_id=${inst}${fromDate && toDate ? `&from_date=${fromDate}&to_date=${toDate}` : ""}`,
    ),
  autoMap: (deviceId, inst) =>
    jpost(`${API_BASE}/auto-map`, { device_id: deviceId, institute_id: inst }),
  mapStudent: (payload) => jpost(`${API_BASE}/map-student`, payload),
  studentSearch: (inst, q, branchId) =>
    fetch(
      `${API_BASE}/student-search?institute_id=${inst}&q=${encodeURIComponent(q)}${branchId ? `&branch_id=${branchId}` : ""}`,
    ),
  listInstitutes: () => fetch(`${API_BASE}/institutes`),
  pendingDevices: () => fetch(`${API_BASE}/pending-devices`),
  hostelRoster: (inst, branchId) =>
    fetch(
      `${API_BASE}/hostel-roster?institute_id=${inst}${branchId ? `&branch_id=${branchId}` : ""}`,
    ),
};

// Punches at/after this hour are hostel attendance (Vignan uses one device for both).
const HOSTEL_CUTOFF_HOUR = 18;
const isEveningPunch = (datetime) => {
  const t = (datetime || "").split(" ")[1] || "";
  const h = parseInt(t.split(":")[0], 10);
  return !Number.isNaN(h) && h >= HOSTEL_CUTOFF_HOUR;
};

// The hostel "night" only begins at the 6 PM cutoff, so today's roll-call has no
// meaning until then. Default the hostel view to the most recent COMPLETED night:
//   • before 6 PM  → yesterday (tonight's check-ins haven't started)
//   • at/after 6 PM → today (tonight is now in progress)
const defaultHostelDate = () => {
  const now = new Date();
  if (now.getHours() < HOSTEL_CUTOFF_HOUR) {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return fmt(y);
  }
  return getToday();
};

// Seed registry used until the backend /devices endpoint is reachable. The
// Niampet "College Main" device is the live one (serial QJT3243300480).
const SEED_DEVICES = [
  {
    id: "seed-nizampet-college",
    institute_id: null,
    branch_id: null,
    branch_name: "Nizampet",
    device_serial: PRIMARY_SERIAL,
    device_name: "College Main",
    device_type: "college",
    location: "Vignan, Nizampet",
    is_primary: 1,
    procured_by: "Rizee",
    cost_borne_by: "Vignan",
    approval_status: "approved",
  },
];

const DEVICE_TYPES = [
  { key: "college", label: "College", icon: "🏫" },
  { key: "hostel", label: "Hostel", icon: "🛏" },
  { key: "library", label: "Library", icon: "📚" },
  { key: "lab", label: "Lab", icon: "🔬" },
  { key: "gate", label: "Gate", icon: "🚪" },
  { key: "general", label: "General", icon: "🖥" },
];
const deviceTypeMeta = (t) =>
  DEVICE_TYPES.find((d) => d.key === t) ||
  DEVICE_TYPES[DEVICE_TYPES.length - 1];

const BRANCH_TINTS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#14b8a6",
];
const tintFor = (idx) => BRANCH_TINTS[idx % BRANCH_TINTS.length];

const getInstituteId = () =>
  parseInt(
    Cookies.get("institutionid") || Cookies.get("institute_id") || "0",
    10,
  ) || null;

// institute 51 = Rizee super admin: provisions devices and assigns them to colleges.
const SUPER_ADMIN_INSTITUTE = 51;
const isSuperAdmin = () => getInstituteId() === SUPER_ADMIN_INSTITUTE;

// ─── Unified attendance module config (biometric + manual, per-college adaptive) ──
// A college shows the full biometric view when it has a registered device, else
// manual attendance only. Everything about what shows is controlled here in one
// place — change a list / add a force-mode and the UI follows.
const ALL_TABS = {
  overview: { label: "Network Overview", icon: "📊" },
  reports: { label: "Class / Section Reports", icon: "📑" },
  holidays: { label: "Holidays", icon: "🎌" },
  command: { label: "Command Center", icon: "🛡️" },
  hostel: { label: "Hostel Attendance", icon: "🛏" },
  mapping: { label: "Student Mapping", icon: "🧑‍🎓" },
  onboarding: { label: "Device Onboarding", icon: "🛰" },
  devices: { label: "Branches & Devices", icon: "🖥" },
  access: { label: "Access Management", icon: "🔐" },
  sync: { label: "Sync Center", icon: "🔄" },
};
// Each tab is addressable by its own URL: /attendance-regularization/<key>. The tab keys are
// already URL-safe, so they double as slugs; the route (App.js) declares the same list.
const AR_BASE = "/attendance-regularization";
const AR_TAB_KEYS = Object.keys(ALL_TABS);

// Slugs that carry a SOURCE MODE as well as a tab. "combined" and "manual" both render the
// reports tab, so /reports alone is ambiguous — refreshing on Manual Attendance would come
// back as Consolidated. Manual Attendance therefore gets its own address.
const MODE_ROUTES = {
  "manual-attendance": { tab: "reports", mode: "manual" },
  // Holidays is its own source mode, so the slug has to restore mainSource as well —
  // otherwise a refresh on /holidays renders the tab with mainSource still "combined"
  // and the mode bar highlights the wrong button.
  holidays: { tab: "holidays", mode: "holidays" },
  // Biometric mode lands on the overview tab. "overview" is only reachable in biometric
  // mode, so this slug owns it: a refresh restores mainSource=biometric and the right button.
  "biometric-attendance": { tab: "overview", mode: "biometric" },
};
const slugParam = (props) =>
  props && props.match && props.match.params ? props.match.params.tab : null;
// The tab named in the URL, or null when absent/unknown (→ fall back to the mode's default).
const urlTab = (props) => {
  const t = slugParam(props);
  if (MODE_ROUTES[t]) return MODE_ROUTES[t].tab;
  return AR_TAB_KEYS.indexOf(t) !== -1 ? t : null;
};
// The source mode the URL pins, or null when the slug doesn't imply one.
const urlMode = (props) => {
  const t = slugParam(props);
  return MODE_ROUTES[t] ? MODE_ROUTES[t].mode : null;
};

const ATTENDANCE_CONFIG = {
  // Tabs shown in each mode (order matters; keys must exist in ALL_TABS).
  tabs: {
    // NOTE: this list is NOT read — visibleTabs() hardcodes the per-mode tabs. Kept only
    // because the rest of ATTENDANCE_CONFIG (superAdminTab, forceMode) still is.
    biometric: [
      "overview",
      "reports",
      "command",
      "hostel",
      "mapping",
      "devices",
      "access",
      // "sync" — Sync Center hidden (non-functional); punches sync automatically
      // via the ADMS push path, so a manual pull tab isn't needed.
    ],
    manual: ["reports", "command"],
  },
  // Extra tab injected for the super admin in biometric mode, after this key.
  superAdminTab: { key: "onboarding", after: "mapping" },
  // Force a mode for specific institute ids — overrides auto-detect.
  // e.g. { 200: "manual", 119: "biometric" }
  forceMode: {},
};

// Stable grouping key for a device's branch. Prefer branch_id (unique per branch,
// so branches never collapse when two share a name or a name is blank); fall back
// to the trimmed branch_name, then "Unassigned".
const branchKeyOf = (d) => {
  if (d.branch_id != null && String(d.branch_id).trim() !== "")
    return `bid:${d.branch_id}`;
  if (d.branch_name && d.branch_name.trim()) return d.branch_name.trim();
  return "Unassigned";
};
// Human-readable branch name for display (the key may be "bid:3").
const branchNameOf = (d) =>
  d.branch_name && d.branch_name.trim()
    ? d.branch_name.trim()
    : d.branch_id != null && String(d.branch_id).trim() !== ""
      ? `Branch ${d.branch_id}`
      : "Unassigned";

const isRecent = (lastPunch) => {
  if (!lastPunch) return false;
  const [dt, t = "00:00:00"] = lastPunch.split(" ");
  const when = new Date(`${dt}T${t}`).getTime();
  return Date.now() - when < 24 * 3600 * 1000;
};

const loadLocalDevices = () => {
  try {
    const raw = window.localStorage.getItem(DEVICES_LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length)
        return arr.map((d) =>
          d.is_primary ? { ...d, device_serial: PRIMARY_SERIAL } : d,
        );
    }
  } catch (_) {}
  return SEED_DEVICES;
};
const persistLocalDevices = (arr) => {
  try {
    window.localStorage.setItem(DEVICES_LS_KEY, JSON.stringify(arr));
  } catch (_) {}
};

// Self-contained live clock — owns its own 1s timer so the console doesn't
// re-render every second (which was retriggering the embedded report's fetches).
class LiveClock extends Component {
  constructor(p) {
    super(p);
    this.state = { now: new Date() };
    this.t = null;
  }
  componentDidMount() {
    this.t = setInterval(() => this.setState({ now: new Date() }), 1000);
  }
  componentWillUnmount() {
    clearInterval(this.t);
  }
  render() {
    const now = this.state.now;
    const hh = now.getHours();
    const ampm = hh >= 12 ? "PM" : "AM";
    const clockHM = `${padTwo(hh % 12 || 12)}:${padTwo(now.getMinutes())}`;
    const clockSS = padTwo(now.getSeconds());
    const clockDt = now.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    return (
      <div style={S.clockBox}>
        <div style={S.clockTime}>
          {clockHM}
          <span style={S.clockSec}>:{clockSS}</span>
          <span style={S.clockAmpm}>{ampm}</span>
        </div>
        <div style={S.clockDate}>{clockDt}</div>
      </div>
    );
  }
}

// ═════════════════════════════════════════════════════════════════════════════
class AttendanceRegularization extends Component {
  constructor(props) {
    super(props);
    this.backendDevices = false; // true once GET /devices succeeds
    this.isSuper = isSuperAdmin();
    this.state = {
      toggled: Cookies.get("toggle") || "wrapper",

      mainSource: "combined",
      // Driven by the URL slug (/attendance-regularization/<tab>); falls back to the mode's
      // default once devices are detected (see loadDevicesThenFetch).
      activeTab: urlTab(props) || "overview",
      fromDate: getToday(),
      toDate: getToday(),
      activeFilter: "today",

      devices: loadLocalDevices(),
      // False until the backend device list has been fetched. Until then we don't
      // know whether this college is biometric or manual, so we show a single
      // loading screen instead of flashing the wrong tab set (manual → biometric).
      devicesReady: false,
      deviceData: {}, // serial -> { loading, error, logs, users, dateGroups, totalPresent, totalPunches, userCount, lastPunch }
      loadingAll: false,

      globalBranch: [], // header branch filter (MULTI-SELECT) — [] = All Branches; scopes every tab
      // All the college's branches (from getBranches), so the BRANCH dropdown lists
      // EVERY branch — even those with no biometric device (which show manual view).
      collegeBranches: [], // [{ id, branch_name }]
      // Holidays tab
      holidays: [],
      holidaysLoading: false,
      holidayForm: null, // null = closed; { id?, name, date: "YYYY-MM-DD", branch_id }
      holidaySaving: false,
      holidayError: "",
      holidayPage: 1, // client-side pagination (a full year of Sundays is ~52 rows/branch)
      holidaySearch: "", // filter the holiday list by date / day / name / branch
      kpiModal: null, // KPI drill-down category: total | present | absent | yet
      kpiClass: "",
      kpiSection: "",
      selectedBranch: null, // branchKey for overview detail
      accessBranch: null, // branchKey for access tab
      expandedDates: {},
      peopleFilter: "",

      empModal: null,
      deviceModal: null,
      showIdModal: false,
      idSerial: null,
      idDeviceName: "",
      idRows: [],
      idLoading: false,
      idSaving: false,
      idSaveError: null,

      logSync: {}, // serial -> {status,msg}
      access: {}, // `${serial}:${id}` -> {status,msg}
      bulkCopy: { source: "", target: "", running: false, msg: null },
      notice: null,

      // Super-admin device onboarding
      institutes: [],
      pending: [],
      assignSel: {}, // serial -> { institute, name, busy }

      // Hostel attendance (after-cutoff punches for resident students)
      hostelRoster: [],
      hostelLoading: false,
      hostelClass: "",
      hostelSection: "",
      hostelExpanded: {},
      // Hostel attendance is a per-NIGHT roll-call, not a range: a resident is
      // "present tonight" if they punched at/after 6 PM on this one chosen day.
      // Its own date so the console's multi-day range never collapses many nights
      // into a single misleading "present" flag. Defaults to the most recent
      // completed night: yesterday before 6 PM, today after 6 PM.
      hostelDate: defaultHostelDate(),
      hostelEveningByAdm: {}, // admission → earliest ≥6 PM punch on hostelDate
      hostelDayLoading: false,

      // Student records (admission_no → student) resolved client-side from the ERP
      studentMap: null,
      studentsLoading: false,

      // Student mapping (device biometric IDs ↔ existing students)
      mapDevice: null,
      mapStatus: null, // { rows, mapped, matchable, unmatched, total }
      mapLoading: false,
      mapBusy: false,
      mapFilter: "all", // all | mapped | unmatched
      rowSearch: {}, // device_user_id -> { q, results, loading }

      // Bumped after an auto-sync writes new attendance rows, to remount the
      // embedded report so today's freshly-synced punches appear.
      reportKey: 0,
      accessDeniedShow: false,
    };
    this._autoSyncedToday = false;

    this.guard = attachModuleGuard(this, ATTENDANCE_REGULARIZATION_MODULE);
  }

  componentDidMount() {
    if (Cookies.get("token") === undefined && this.props.history) {
      this.props.history.push("/login");
      return;
    }

    // is_view === 0 → fetch nothing, and never start the 60s poll. Every read here is an
    // imperative client.query()/REST call, so there is no graphql HOC to put a `skip` on;
    // bailing out is what keeps the data off the wire. The embedded report component
    // renders the Access Denied card. Without this the poll would keep hitting the
    // backend every minute behind that card.
    if (!this.guard.canView()) return;

    this.loadStudents();
    this.loadBranches();
    this.loadDevicesThenFetch();
    if (this.isSuper) this.loadOnboarding();

    const isBiometricTab = () => {
      const TABS = this.visibleTabs(this.state.mainSource);
      const tab = TABS.some((t) => t.key === this.state.activeTab)
        ? this.state.activeTab
        : TABS[0] && TABS[0].key;
      return ["overview", "hostel", "mapping", "devices", "access", "sync"].includes(tab);
    };

    // Live console: refetch punches periodically so new punches surface without a
    // manual reload, and immediately whenever the user returns to the tab.
    this._poll = setInterval(() => {
      if (document.visibilityState === "visible" && isBiometricTab()) this.fetchAll();
    }, 60000);
    this._onVisible = () => {
      if (document.visibilityState === "visible" && isBiometricTab()) this.fetchAll();
    };
    document.addEventListener("visibilitychange", this._onVisible);

    // Branch is chosen once in the header (NavbarOne). Re-scope every tab when it changes;
    // the Holidays tab does its own branch-scoped fetch, so nudge it too when it's open.
    this.offBranchScope = onBranchScopeChange(() => {
      this.loadBranches();
      if (this.state.activeTab === "holidays") this.loadHolidays();
    });
  }

  // Keep the tab in step with the URL so back/forward, refresh and pasted links all work —
  // and run that tab's lazy load, which used to only happen on click.
  componentDidUpdate(prevProps) {
    const prev = urlTab(prevProps);
    const curr = urlTab(this.props);
    if (prev !== curr && curr && curr !== this.state.activeTab) {
      // A mode-carrying slug (e.g. manual-attendance) restores its source too, so back/
      // forward and a pasted link land on the right view instead of the mode default.
      const mode = urlMode(this.props);
      const next = { activeTab: curr };
      if (mode && mode !== this.state.mainSource) next.mainSource = mode;
      this.setState(next, () => this.onTabActive(curr));
    }
  }

  componentWillUnmount() {
    if (this._poll) clearInterval(this._poll);
    if (this._onVisible)
      document.removeEventListener("visibilitychange", this._onVisible);
    if (this.offBranchScope) this.offBranchScope();
  }

  // Load ALL of the college's branches so the BRANCH filter lists every branch,
  // not only ones that have a biometric device.
  loadBranches = async () => {
    const inst = getInstituteId();
    if (!inst || !this.props.client) return;
    try {
      const res = await this.props.client.query({
        query: GET_BRANCHES_QUERY,
        variables: { institution_id: Number(inst) },
        fetchPolicy: "network-only",
      });
      // The header's branch selection (NavbarOne) is this page's branch filter.
      const list = scopeBranches((res.data && res.data.getBranches) || []);
      this.setState({ collegeBranches: list }, this.reconcileBranchKeys);
    } catch (_) {
      /* non-fatal — dropdown falls back to device-derived branches */
    }
  };

  // Branch selections are seeded from the primary DEVICE, but every biometric_devices row
  // has branch_id NULL — so branchKeyOf() falls back to the branch NAME ("Nizampet") while
  // the dropdown keys its options by id ("bid:359"). The two never compare equal, so the
  // header read "1 selected" with nothing ticked. Once the branch list is in, rewrite any
  // name-keyed selection to its id key; drop it if no branch matches the name.
  reconcileBranchKeys = () => {
    const list = this.state.collegeBranches || [];
    if (!list.length) return;
    const idByName = new Map(
      list.map((b) => [String(b.branch_name || "").trim(), b.id]),
    );
    const canonical = (key) => {
      if (key == null || /^bid:\d+$/.test(String(key))) return key;
      const id = idByName.get(String(key).trim());
      return id ? `bid:${id}` : null;
    };
    const next = {};
    const globalBranch = (this.state.globalBranch || [])
      .map(canonical)
      .filter(Boolean);
    if (globalBranch.join() !== (this.state.globalBranch || []).join())
      next.globalBranch = globalBranch;
    for (const k of ["selectedBranch", "accessBranch"]) {
      const c = canonical(this.state[k]);
      if (c !== this.state[k]) next[k] = c;
    }
    if (Object.keys(next).length) this.setState(next);
    this.applyDefaultAllBranches();
  };

  // globalBranch simply mirrors the header's branch scope: collegeBranches is already
  // narrowed by scopeBranches(), so every loaded branch is in scope and gets ticked.
  // Re-applied on every reconcile — the header owns the selection now, so there is no
  // local choice to preserve.
  applyDefaultAllBranches = () => {
    const list = this.state.collegeBranches || [];
    if (!list.length) return;
    // Keys stay "bid:<id>" — the device grouping and every selectedBranch* helper key off them.
    this.setState({ globalBranch: list.map((b) => `bid:${b.id}`), expandedDates: {} });
  };

  // Load the institute's students once and key them by admission number.
  loadStudents = async () => {
    const inst = getInstituteId();
    if (!inst || !this.props.client) return;
    this.setState({ studentsLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_INSTITUTE_STUDENTS,
        variables: { institute_id: inst },
        fetchPolicy: "network-only",
      });
      const list = (res.data && res.data.getNewEducationalStudents) || [];
      const authBranchIds = getAuthorizedBranchIds();
      const authClassIds = getAuthorizedClassIds();
      const authSecIds = getAuthorizedSectionIds();

      const map = {};
      list.forEach((s) => {
        if (s.admission_no == null) return;
        if (authBranchIds.length > 0 && s.branch_id && !authBranchIds.includes(String(s.branch_id))) return;
        if (authClassIds.length > 0 && s.class_id && !authClassIds.includes(String(s.class_id))) return;
        if (authSecIds.length > 0 && s.section_id && !authSecIds.includes(String(s.section_id))) return;

        map[String(s.admission_no).trim()] = {
          eduStudentId: s.student_id,
          name: s.student_name,
          rollNo: s.roll_no,
          className: s.class_name,
          sectionName: s.section_name,
          classId: s.class_id,
          sectionId: s.section_id,
          branchId: s.branch_id,
        };
      });
      this.setState(
        { studentMap: map, studentsLoading: false },
        this.reEnrichAll,
      );
    } catch (_) {
      this.setState({ studentsLoading: false });
    }
  };

  // Overlay student name/class/section onto raw device logs (keyed by admission no).
  enrichLogs = (logs) => {
    const sm = this.state.studentMap;
    if (!sm) return logs;
    return logs.map((l) => {
      const stu = sm[String(l.device_user_id).trim()];
      if (!stu) return l;
      return {
        ...l,
        name: l.name || stu.name,
        class_name: l.class_name || stu.className,
        section_name: l.section_name || stu.sectionName,
      };
    });
  };

  // Re-derive every device's groups once student records arrive (handles the
  // case where logs loaded before the student map was ready).
  reEnrichAll = () => {
    this.setState((s) => {
      if (!s.studentMap) return null;
      const dd = { ...s.deviceData };
      Object.keys(dd).forEach((serial) => {
        const d = dd[serial];
        if (d && d.logs && d.logs.length) {
          const elogs = this.enrichLogs(d.logs);
          const userMap = {};
          (d.users || []).forEach((u) => {
            userMap[String(u.biometric_unique_id)] = {
              name: u.name,
              employee_type: u.employee_type,
            };
          });
          dd[serial] = {
            ...d,
            logs: elogs,
            dateGroups: buildDateGroups(elogs, userMap),
            totalPresent: new Set(elogs.map((l) => String(l.device_user_id)))
              .size,
          };
        }
      });
      return { deviceData: dd };
    });
  };

  menuToggler = () => {
    const t = Cookies.get("toggle");
    const next = t === "wrapper" ? "wrapper sidebar-enable" : "wrapper";
    this.setState({ toggled: next });
    Cookies.set("toggle", next);
  };

  // ─── Device registry ──────────────────────────────────────────────────────
  loadDevicesThenFetch = async () => {
    let devices = loadLocalDevices();
    try {
      const res = await api.listDevices(getInstituteId());
      if (res.ok) {
        const d = await res.json();
        if (Array.isArray(d.devices) && d.devices.length) {
          devices = d.devices;
          this.backendDevices = true;
        }
      }
    } catch (_) {}
    const primary = devices.find((d) => d.is_primary) || devices[0];
    const isBio = (devices && devices.length > 0) || this.isSuper;
    // A mode-carrying URL (/manual-attendance) wins over the device-derived default, so a
    // refresh or pasted link stays on Manual Attendance instead of snapping to Consolidated.
    const initialSource = urlMode(this.props) || (isBio ? "combined" : "manual");
    const initialTab = initialSource === "combined" || initialSource === "manual" ? "reports" : "overview";
    this.setState(
      {
        devices,
        devicesReady: true,
        mainSource: initialSource,
        // An explicit tab in the URL wins — otherwise the mode's default would clobber a
        // deep-linked/refreshed tab once device detection finishes.
        activeTab: urlTab(this.props) || initialTab,
        selectedBranch: primary ? branchKeyOf(primary) : null,
        accessBranch: primary ? branchKeyOf(primary) : null,
        // globalBranch is deliberately NOT set here. Devices and branches race, and
        // branches usually land first: assigning it (even to []) would wipe the
        // all-branches default applyDefaultAllBranches() had already applied, and the
        // once-only flag would then stop it being re-applied. It starts [] in the
        // initial state, and the default is owned entirely by that one function.
        mapDevice: primary ? primary.device_serial : null,
      },
      () => {
        // Devices and branches load independently — whichever lands second has to do the
        // reconcile, or a name-keyed seed survives and the checkbox renders unticked.
        this.reconcileBranchKeys();
        this.fetchAll();
        this.autoSyncToday();
      },
    );
  };

  // Pull today's raw device punches into student_attendance the moment an admin
  // opens the console (Cloud Run has no always-on scheduler, so we sync on view).
  // Idempotent + silent; only remounts the report if new rows were actually written.
  autoSyncToday = async () => {
    // This WRITES (api.autoMap inserts attendance rows) and it runs automatically on
    // mount, so it uses the SILENT canEdit() — not requireEdit(), which would pop the
    // Access Denied modal in the user's face the instant they opened the page, for an
    // action they never clicked. A role without is_edit simply doesn't auto-sync.
    if (!canEdit(ATTENDANCE_REGULARIZATION_MODULE)) return;

    if (this._autoSyncedToday) return;
    const inst = getInstituteId();
    const device = (this.state.devices || []).find(
      (d) => Number(d.institute_id) === Number(inst),
    );
    if (!inst || !device || !device.device_serial) return;
    this._autoSyncedToday = true;
    try {
      const res = await api.autoMap(device.device_serial, inst);
      if (!res.ok) return;
      const data = await res.json();
      const inserted = data && data.attendance && data.attendance.inserted;
      if (inserted > 0) {
        // New punches landed → remount the embedded report so today shows.
        this.setState((s) => ({ reportKey: s.reportKey + 1 }));
        this.fetchAll();
      }
    } catch (_) {
      /* best-effort — device/raw-log sync is non-critical to viewing reports */
    }
  };

  applyFilter = (f) => {
    this.setState(
      {
        fromDate: clampFrom(f.from()),
        toDate: f.to(),
        activeFilter: f.key,
        expandedDates: {},
      },
      this.fetchAll,
    );
  };

  fetchAll = async () => {
    const { devices } = this.state;
    this.setState({
      loadingAll: true,
      expandedDates: {},
      deviceData: devices.reduce((acc, d) => {
        acc[d.device_serial] = {
          ...(this.state.deviceData[d.device_serial] || {}),
          loading: true,
        };
        return acc;
      }, {}),
    });
    await Promise.all(devices.map((d) => this.fetchDevice(d.device_serial)));
    this.setState({ loadingAll: false });
  };

  fetchDevice = async (serial) => {
    const { fromDate, toDate } = this.state;
    let logs = [];
    let users = [];
    let error = null;
    const [logsRes, usersRes] = await Promise.allSettled([
      api.syncedLogs(serial, fromDate, toDate, getInstituteId()),
      api.users(serial),
    ]);
    try {
      if (logsRes.status === "fulfilled" && logsRes.value.ok) {
        const d = await logsRes.value.json();
        logs = d.logs || [];
      } else error = "Device not reachable";
    } catch (_) {
      error = "Could not load logs";
    }
    try {
      if (usersRes.status === "fulfilled" && usersRes.value.ok) {
        const d = await usersRes.value.json();
        users = d.users || [];
      }
    } catch (_) {}

    logs = this.enrichLogs(logs);
    const userMap = {};
    users.forEach((u) => {
      userMap[String(u.biometric_unique_id)] = {
        name: u.name,
        employee_type: u.employee_type,
      };
    });
    const dateGroups = buildDateGroups(logs, userMap);
    const totalPresent = new Set(logs.map((l) => String(l.device_user_id)))
      .size;
    const lastPunch = logs.reduce(
      (mx, l) => (l.datetime > mx ? l.datetime : mx),
      "",
    );

    this.setState((s) => ({
      deviceData: {
        ...s.deviceData,
        [serial]: {
          loading: false,
          error: logs.length === 0 ? error : null,
          logs,
          users,
          dateGroups,
          totalPresent,
          totalPunches: logs.length,
          userCount: users.length,
          lastPunch: lastPunch || null,
        },
      },
    }));
  };

  // Pull the device's full enrolled roster (faces) from the device via SOAP and
  // upsert into biometric_users, then refresh that device's card.
  // Pushes device users to the backend → is_edit.
  syncDeviceUsers = async (device) => {
    if (!this.guard.requireEdit()) return;

    const serial = device.device_serial;
    this.setState({ syncingUsers: serial });
    try {
      const res = await api.syncUsers(serial, getInstituteId());
      const data = res.ok ? await res.json() : {};
      if (!res.ok) throw new Error(data.error || "Sync failed");
      await this.fetchDevice(serial);
      this.setState({
        syncingUsers: null,
        notice: {
          type: "info",
          msg: `${serial}: synced ${data.fetched || 0} faces (${data.added || 0} new).`,
        },
      });
    } catch (err) {
      this.setState({
        syncingUsers: null,
        notice: { type: "error", msg: `Sync faces failed: ${err.message}` },
      });
    }
  };

  toggleDate = (date) =>
    this.setState((s) => ({
      expandedDates: { [date]: !s.expandedDates[date] },
    }));

  // ─── Branch / network aggregation ─────────────────────────────────────────
  // "biometric" if this college has a device assigned to it (or forced); else "manual".
  getMode = () => {
    const inst = getInstituteId();
    const forced = ATTENDANCE_CONFIG.forceMode[inst];
    if (forced) return forced;
    // Super admin manages the whole biometric network (and provisions devices for
    // other colleges), so always show the full biometric console even though
    // institute 51 itself has no device assigned to it.
    if (this.isSuper) return "biometric";
    const hasDevice = (this.state.devices || []).some(
      (d) => Number(d.institute_id) === inst,
    );
    return hasDevice ? "biometric" : "manual";
  };

  // Tabs to show for the current mode
  visibleTabs = (mainSource) => {
    if (mainSource === "combined") {
      return [{ key: "reports", label: "Consolidated Reports", icon: "📑" }];
    }
    if (mainSource === "manual") {
      return [
        { key: "reports", label: "Class / Section Reports (Manual)", icon: "📑" }
      ];
    }
    if (mainSource === "command") {
      return [{ key: "command", label: "Command Center", icon: "🛡️" }];
    }
    if (mainSource === "holidays") {
      // Without this it falls through to the biometric list below and renders Network
      // Overview instead of the Holidays view.
      return [{ key: "holidays", label: "Holidays", icon: "🎌" }];
    }
    // biometric
    const keys = ["overview", "hostel", "mapping", "devices", "access"];
    if (this.isSuper) {
      keys.push("onboarding");
    }
    return keys.map((k) => ({
      key: k,
      label: ALL_TABS[k] ? ALL_TABS[k].label : k,
      icon: ALL_TABS[k] ? ALL_TABS[k].icon : "⚙️",
    }));
  };

  groupBranches = () => {
    const { devices, collegeBranches } = this.state;
    // Resolve a device's TRUE branch_id: use its own if set, else match its
    // branch_name against the college branch list. Needed because devices are
    // often tagged only by branch_name (branch_id null), and student scoping +
    // filtering rely on a real branch_id.
    const byName = new Map(
      (collegeBranches || []).map((b) => [String(b.branch_name || "").trim(), b.id]),
    );
    const resolveBranchId = (d) => {
      if (d.branch_id != null && String(d.branch_id).trim() !== "") return Number(d.branch_id);
      const nm = d.branch_name && d.branch_name.trim();
      return nm && byName.has(nm) ? Number(byName.get(nm)) : null;
    };
    const order = [];
    const map = {};
    devices.forEach((d) => {
      const key = branchKeyOf(d);
      if (!map[key]) {
        map[key] = { key, name: branchNameOf(d), branch_id: resolveBranchId(d), devices: [] };
        order.push(key);
      }
      map[key].devices.push(d);
    });
    return order.map((k, i) => ({ ...map[k], tint: tintFor(i) }));
  };

  buildBranch = (branch) => {
    const { deviceData } = this.state;
    const combinedLogs = [];
    const userMap = {};
    let loading = false;
    let anyData = false;
    const deviceRows = branch.devices.map((d) => {
      const dd = deviceData[d.device_serial] || {};
      if (dd.loading) loading = true;
      (dd.logs || []).forEach((l) => combinedLogs.push(l));
      (dd.users || []).forEach((u) => {
        userMap[String(u.biometric_unique_id)] = {
          name: u.name,
          employee_type: u.employee_type,
        };
      });
      if (dd.totalPunches || dd.userCount) anyData = true;
      return {
        device: d,
        present: dd.totalPresent || 0,
        punches: dd.totalPunches || 0,
        userCount: dd.userCount || 0,
        lastPunch: dd.lastPunch || null,
        recent: isRecent(dd.lastPunch),
        loading: !!dd.loading,
        error: dd.error,
      };
    });
    const dateGroups = buildDateGroups(combinedLogs, userMap);
    const summaries = summariseGroups(dateGroups);
    const present = new Set(combinedLogs.map((l) => String(l.device_user_id)))
      .size;
    const punches = combinedLogs.length;
    const onTime = summaries.reduce((s, d) => s + d.onTime, 0);
    const late = summaries.reduce((s, d) => s + d.late + d.veryLate, 0);
    const enrolled = new Set();
    branch.devices.forEach((d) =>
      (deviceData[d.device_serial]?.users || []).forEach((u) =>
        enrolled.add(String(u.biometric_unique_id)),
      ),
    );
    const allMins = dateGroups.flatMap(({ users }) =>
      users.map((u) => durToMins(u.duration)).filter(Boolean),
    );
    const avgHrs = allMins.length
      ? minsToStr(
          Math.round(allMins.reduce((a, c) => a + c, 0) / allMins.length),
        )
      : null;
    const lastPunch = combinedLogs.reduce(
      (mx, l) => (l.datetime > mx ? l.datetime : mx),
      "",
    );
    const recent = isRecent(lastPunch);
    const status = loading
      ? "loading"
      : recent
        ? "active"
        : anyData
          ? "idle"
          : "offline";

    // ── Student metrics: total roster → registered (enrolled) → present ───────
    // Total   = students of this branch.
    // Registered = branch students enrolled on the device (roster or have punched).
    // Present = registered students who punched. Absent = registered − present.
    // Yet to Register = students with no biometric enrollment yet (NOT absent).
    const sm = this.state.studentMap || {};
    // Scope students to THIS branch by its resolved branch_id (groupBranches fills
    // branch.branch_id from the device's branch_id or its branch_name → college id).
    // Fall back to device branch_ids, then to the SELECTED global branch id — so that
    // when a branch is chosen in the header, the class·section breakdown is scoped to
    // it even if the devices have no resolvable branch_id (otherwise ALL classes leak).
    const selBranchId = this.selectedBranchId();   // null for "All Branches"
    const branchBranchIds = new Set(
      [branch.branch_id, ...branch.devices.map((d) => d.branch_id), selBranchId]
        .filter((x) => x != null)
        .map(Number),
    );
    const branchStudentAdms = new Set();
    Object.keys(sm).forEach((adm) => {
      const stu = sm[adm];
      if (!branchBranchIds.size || branchBranchIds.has(Number(stu.branchId)))
        branchStudentAdms.add(String(adm));
    });
    const totalStudents = branchStudentAdms.size;

    const enrolledSet = new Set();
    branch.devices.forEach((d) => {
      const dd = deviceData[d.device_serial] || {};
      (dd.users || []).forEach((u) =>
        enrolledSet.add(String(u.biometric_unique_id)),
      );
      (dd.logs || []).forEach((l) => enrolledSet.add(String(l.device_user_id)));
    });
    let registeredStudents = 0;
    branchStudentAdms.forEach((adm) => {
      if (enrolledSet.has(adm)) registeredStudents++;
    });

    // "Present" is NOT a union over the period (that wrongly approaches 100% — almost
    // everyone shows up some day). Count distinct student punchers per day, then the
    // headline "present" is the AVERAGE daily present over the days with data — a real
    // period attendance figure. For a single day this equals that day's count.
    const studentDayMap = {}; // "YYYY-MM-DD" -> Set(admission)
    combinedLogs.forEach((l) => {
      const id = String(l.device_user_id);
      if (!branchStudentAdms.has(id)) return;
      const day = (l.datetime || "").split(" ")[0];
      if (!day) return;
      (studentDayMap[day] = studentDayMap[day] || new Set()).add(id);
    });
    const studentDays = Object.keys(studentDayMap).sort();
    const numPresentDays = studentDays.length;
    const latestStudentDay = numPresentDays
      ? studentDays[numPresentDays - 1]
      : null;
    // Days-present per student → period attendance RATE = present-days / possible.
    const daysPresentByAdm = {};
    studentDays.forEach((d) =>
      studentDayMap[d].forEach((adm) => {
        daysPresentByAdm[adm] = (daysPresentByAdm[adm] || 0) + 1;
      }),
    );
    const totalPresentDayHits = Object.values(daysPresentByAdm).reduce(
      (a, c) => a + c,
      0,
    );
    const possibleDayHits = registeredStudents * numPresentDays;
    const attendanceRate = possibleDayHits
      ? Math.round((totalPresentDayHits / possibleDayHits) * 100)
      : 0;
    // Present/Absent COUNTS = the latest day — a real headcount that never shrinks
    // when the range widens. The selected period shows up in `attendanceRate` (the
    // % across all days) and in the daily trend.
    const presentStudentIds = latestStudentDay
      ? studentDayMap[latestStudentDay]
      : new Set();
    const studentsPresent = presentStudentIds.size;
    const studentsAbsent = Math.max(0, registeredStudents - studentsPresent);
    const yetToRegister = Math.max(0, totalStudents - registeredStudents);
    let studentsLate = 0;
    dateGroups.forEach(({ users }) =>
      users.forEach((u) => {
        if (!branchStudentAdms.has(String(u.userId))) return;
        const st = arrivalStatus(u.firstIn);
        if (st.key === "late" || st.key === "verylate") studentsLate++;
      }),
    );

    return {
      ...branch,
      deviceRows,
      dateGroups,
      summaries,
      present,
      punches,
      onTime,
      late,
      enrolledCount: enrolled.size,
      avgHrs,
      lastPunch: lastPunch || null,
      status,
      loading,
      totalStudents,
      registeredStudents,
      studentsPresent,
      studentsAbsent,
      yetToRegister,
      studentsLate,
      // Admission-number sets so the branch detail can build a class/section view.
      branchAdms: [...branchStudentAdms],
      presentAdms: [...presentStudentIds],
      enrolledAdms: [...enrolledSet],
      // The most recent day with data (used to label present/absent counts).
      presentDay: latestStudentDay,
      // Period attendance rate (% across all days) + per-class rate inputs.
      attendanceRate,
      numPresentDays,
      daysPresentByAdm,
      // Raw rate inputs so the aggregate ("All Branches") rate is exact.
      presentDayHits: totalPresentDayHits,
      possibleDayHits,
    };
  };

  // Does a device-derived branch group (from groupBranches) match the currently
  // selected dropdown branch? The dropdown value is "bid:<id>" (college branch),
  // but device groups may be keyed by name (branch_id null), so match id OR name.
  // The selected college branch id (from the "bid:<id>" dropdown value), or null
  // for "All Branches". Passed to the embedded attendance report so it syncs.
  // ── Branch filter is MULTI-SELECT ────────────────────────────────────────
  // state.globalBranch is an ARRAY of branch keys ("bid:<id>", or a device-derived
  // branch name). An EMPTY array means "All Branches" — that's the single source of
  // truth every tab reads through the helpers below.
  // True only when EVERY branch is ticked (the default). An empty selection means NONE
  // selected — not "all". That flipped when the filter started defaulting to all ticked:
  // with a full default, clearing the box is a deliberate "show me nothing", and treating
  // it as "everything" left the class/section pickers populated with no branch chosen.
  isAllBranches = () => {
    const gb = this.state.globalBranch || [];
    const all = this.state.collegeBranches || [];
    return gb.length > 0 && all.length > 0 && gb.length === all.length;
  };

  // One branch key → its numeric college branch id (or null).
  branchKeyToId = (key) => {
    const m = String(key).match(/^bid:(\d+)$/);
    if (m) return Number(m[1]);
    // device-derived key (branch name) → resolve via collegeBranches by name
    const b = (this.state.collegeBranches || []).find(
      (x) => String(x.branch_name || "").trim() === key,
    );
    return b ? b.id : null;
  };

  // Every selected branch id. Empty = all branches (no filter).
  selectedBranchIds = () =>
    (this.state.globalBranch || [])
      .map(this.branchKeyToId)
      .filter((id) => id != null);

  // CSV for the report queries' branch_ids arg; null = all branches.
  selectedBranchCsv = () => {
    const ids = this.selectedBranchIds();
    return ids.length ? ids.join(",") : null;
  };

  // Legacy single-branch consumers: the only selected id, else null (all / multiple).
  selectedBranchId = () => {
    const ids = this.selectedBranchIds();
    return ids.length === 1 ? ids[0] : null;
  };

  // Labels for the KPI modal / Excel export header.
  selectedBranchLabel = () => {
    const gb = this.state.globalBranch || [];
    const all = this.state.collegeBranches || [];
    if (!gb.length) return "No branch selected";
    // Every branch ticked: say "All Branches" rather than listing all 7 names into export
    // headers and KPI captions.
    if (all.length && gb.length === all.length) return "All Branches";
    const names = gb.map((k) => {
      const b = (this.state.collegeBranches || []).find((x) => `bid:${x.id}` === k);
      return b ? b.branch_name : String(k).replace(/^bid:/, "Branch ");
    });
    return names.join(", ");
  };

  branchMatchesSelected = (b) => {
    const gb = this.state.globalBranch || [];
    if (!gb.length) return false; // nothing ticked = nothing in scope
    return gb.some((key) => {
      if (b.key === key) return true;
      const selB = (this.state.collegeBranches || []).find((x) => `bid:${x.id}` === key);
      if (selB && b.branch_id != null && Number(b.branch_id) === Number(selB.id)) return true;
      const selName = selB ? String(selB.branch_name || "").trim() : null;
      if (selName && String(b.name || "").trim() === selName) return true;
      return false;
    });
  };

  computeNetwork = () => {
    const allBranches = this.groupBranches().map((b) => this.buildBranch(b));
    // Always filter: every branch ticked matches everything anyway, and none ticked must
    // match nothing. Short-circuiting on "empty = all" is what showed data with no branch.
    const branches = allBranches.filter(this.branchMatchesSelected);
    const totals = branches.reduce(
      (a, b) => {
        a.present += b.present;
        a.punches += b.punches;
        a.onTime += b.onTime;
        a.late += b.late;
        a.enrolled += b.enrolledCount;
        a.devices += b.devices.length;
        a.activeDevices += b.deviceRows.filter((r) => r.recent).length;
        a.totalStudents += b.totalStudents;
        a.registeredStudents += b.registeredStudents;
        a.studentsPresent += b.studentsPresent;
        a.studentsAbsent += b.studentsAbsent;
        a.yetToRegister += b.yetToRegister;
        a.studentsLate += b.studentsLate;
        a.presentDayHits += b.presentDayHits || 0;
        a.possibleDayHits += b.possibleDayHits || 0;
        return a;
      },
      {
        present: 0,
        punches: 0,
        onTime: 0,
        late: 0,
        enrolled: 0,
        devices: 0,
        activeDevices: 0,
        totalStudents: 0,
        registeredStudents: 0,
        studentsPresent: 0,
        studentsAbsent: 0,
        yetToRegister: 0,
        studentsLate: 0,
        presentDayHits: 0,
        possibleDayHits: 0,
      },
    );
    // Student COUNTS must be DISTINCT across branches — a student whose admission is
    // scoped to two overlapping branch groups would otherwise be summed twice, making
    // the consolidated total exceed the true headcount. Recompute the headcounts from
    // a union of per-branch admission sets (registered/present intersect enrolled).
    const uAll = new Set(), uReg = new Set(), uPresent = new Set(), uEnrolled = new Set();
    branches.forEach((b) => {
      (b.branchAdms || []).forEach((a) => uAll.add(String(a)));
      (b.enrolledAdms || []).forEach((a) => uEnrolled.add(String(a)));
    });
    branches.forEach((b) => {
      const enr = new Set(b.enrolledAdms || []);
      (b.branchAdms || []).forEach((a) => { if (enr.has(String(a))) uReg.add(String(a)); });
      (b.presentAdms || []).forEach((a) => uPresent.add(String(a)));
    });
    totals.totalStudents = uAll.size;
    totals.registeredStudents = uReg.size;
    totals.studentsPresent = uPresent.size;
    totals.studentsAbsent = Math.max(0, uReg.size - uPresent.size);
    totals.yetToRegister = Math.max(0, uAll.size - uReg.size);

    return { branches, totals, allBranches };
  };

  // Student-level roster for the KPI drill-down, scoped to the header branch.
  // Each student is categorised: present / absent / yet (not enrolled).
  studentRoster = () => {
    const sm = this.state.studentMap || {};
    const { deviceData } = this.state;
    const groups = this.groupBranches();
    const visible = groups.filter(this.branchMatchesSelected);
    const branchBranchIds = new Set();
    const serials = [];
    // Selected header branch ids — scope the roster to them even when devices have no
    // resolvable branch_id (mirrors buildBranch, so the KPI drill-down respects the filter).
    // Multi-select: add EVERY selected branch, not just one.
    this.selectedBranchIds().forEach((id) => branchBranchIds.add(Number(id)));
    visible.forEach((b) => {
      if (b.branch_id != null) branchBranchIds.add(Number(b.branch_id));
      b.devices.forEach((d) => {
        if (d.branch_id != null) branchBranchIds.add(Number(d.branch_id));
        serials.push(d.device_serial);
      });
    });
    const enrolledSet = new Set();
    const dayMap = {}; // "YYYY-MM-DD" -> Set(admission)
    const lastPunchByAdm = {};
    serials.forEach((serial) => {
      const dd = deviceData[serial] || {};
      (dd.users || []).forEach((u) =>
        enrolledSet.add(String(u.biometric_unique_id)),
      );
      (dd.logs || []).forEach((l) => {
        const id = String(l.device_user_id);
        enrolledSet.add(id);
        const day = (l.datetime || "").split(" ")[0];
        if (day) (dayMap[day] = dayMap[day] || new Set()).add(id);
        if (!lastPunchByAdm[id] || l.datetime > lastPunchByAdm[id])
          lastPunchByAdm[id] = l.datetime;
      });
    });
    // Period attendance per student: days present out of the working days in range.
    const rosterDays = Object.keys(dayMap).sort();
    const numDays = rosterDays.length;
    const daysPresentByAdm = {};
    rosterDays.forEach((d) =>
      dayMap[d].forEach((adm) => {
        daysPresentByAdm[adm] = (daysPresentByAdm[adm] || 0) + 1;
      }),
    );
    const roster = [];
    Object.keys(sm).forEach((adm) => {
      const stu = sm[adm];
      if (branchBranchIds.size && !branchBranchIds.has(Number(stu.branchId))) return;
      const registered = enrolledSet.has(adm);
      const daysPresent = daysPresentByAdm[adm] || 0;
      const attendancePct =
        registered && numDays ? Math.round((daysPresent / numDays) * 100) : 0;
      const absences = registered ? Math.max(0, numDays - daysPresent) : 0;
      roster.push({
        admission: adm,
        name: stu.name,
        className: stu.className || "Unassigned",
        sectionName: stu.sectionName || "—",
        rollNo: stu.rollNo,
        registered,
        numDays,
        daysPresent,
        attendancePct,
        absences,
        category: !registered ? "yet" : "present",
        checkIn: lastPunchByAdm[adm] || null,
      });
    });
    return roster;
  };

  buildMatrix = (branches) => {
    const dateSet = new Set();
    branches.forEach((b) => b.summaries.forEach((s) => dateSet.add(s.date)));
    const dates = [...dateSet].sort((a, b) => b.localeCompare(a));
    const lookup = {};
    branches.forEach((b) => {
      lookup[b.key] = {};
      b.summaries.forEach((s) => (lookup[b.key][s.date] = s.total));
    });
    const max = Math.max(
      1,
      ...branches.flatMap((b) => b.summaries.map((s) => s.total)),
    );
    return { dates, lookup, max };
  };

  hexA = (hex, a) => {
    const h = hex.replace("#", "");
    return `rgba(${parseInt(h.substring(0, 2), 16)},${parseInt(h.substring(2, 4), 16)},${parseInt(h.substring(4, 6), 16)},${a})`;
  };

  statusPill(status, isPrimary) {
    const map = {
      active: { t: "Active", c: "#15803d", bg: "#f0fdf4" },
      idle: { t: "Idle", c: "#b45309", bg: "#fffbeb" },
      offline: { t: "Offline", c: "#64748b", bg: "#f1f5f9" },
      loading: { t: "…", c: "#6366f1", bg: "#eef2ff" },
    };
    const m = map[status] || map.offline;
    return (
      <span style={{ ...S.statusPill, color: m.c, background: m.bg }}>
        <span
          style={{
            ...S.statusDot2,
            background: m.c,
            animation: status === "active" ? "pulse 2s infinite" : "none",
          }}
        />
        {m.t}
      </span>
    );
  }

  // ─── Employee report modal ────────────────────────────────────────────────
  openEmpModal = (branch, userId, name, employee_type) => {
    const records = [];
    branch.dateGroups.forEach(({ date, users }) => {
      const u = users.find((x) => x.userId === userId);
      if (u) records.push({ date, ...u });
    });
    this.setState({
      empModal: {
        userId,
        name: name || `ID ${userId}`,
        employee_type,
        branchName: branch.name,
        records,
      },
    });
  };

  // ─── Device editor ────────────────────────────────────────────────────────
  // Modal openers for device create/edit. Gated before the modal opens, so a role
  // without is_edit gets Access Denied instead of a form it could never save. The
  // buttons stay visible and clickable; saveDevice re-checks at the write.
  openAddDevice = (branchName = "") => {
    if (!this.guard.requireEdit()) return;

    this.setState({
      deviceModal: {
        mode: "add",
        device_name: "",
        device_serial: "",
        device_type: "general",
        branch_name: branchName,
        location: "",
        procured_by: "Rizee",
        cost_borne_by: "Vignan",
        approval_status: "pending",
        approval_ref: "",
      },
    });
  };

  openEditDevice = (d) => {
    if (!this.guard.requireEdit()) return;

    this.setState({
      deviceModal: {
        mode: "edit",
        id: d.id,
        device_name: d.device_name,
        device_serial: d.device_serial,
        device_type: d.device_type || "general",
        branch_name: d.branch_name || "",
        location: d.location || "",
        is_primary: d.is_primary,
        procured_by: d.procured_by || "Rizee",
        cost_borne_by: d.cost_borne_by || "Vignan",
        approval_status: d.approval_status || "pending",
        approval_ref: d.approval_ref || "",
      },
    });
  };

  updateDeviceField = (field, value) =>
    this.setState((s) => ({
      deviceModal: { ...s.deviceModal, [field]: value },
    }));

  saveDevice = async () => {
    // Re-checked at save, not only when the modal opened: the modal could already be
    // open when a grant changes, and this is the last gate before the POST.
    if (!this.guard.requireEdit()) {
      this.setState({ deviceModal: null });
      return;
    }

    const m = this.state.deviceModal;
    if (!m) return;
    const device_name = (m.device_name || "").trim();
    const device_serial = (m.device_serial || "").trim();
    const branch_name = (m.branch_name || "").trim() || "Unassigned";
    if (!device_name || !device_serial) return;

    const payload = {
      id: m.id,
      institute_id: getInstituteId(),
      branch_name,
      device_serial: m.is_primary ? PRIMARY_SERIAL : device_serial,
      device_name,
      device_type: m.device_type || "general",
      location: (m.location || "").trim(),
      is_primary: m.is_primary ? 1 : 0,
      procured_by: (m.procured_by || "").trim() || "Rizee",
      cost_borne_by: (m.cost_borne_by || "").trim() || "Vignan",
      approval_status: m.approval_status || "pending",
      approval_ref: (m.approval_ref || "").trim(),
    };

    if (this.backendDevices) {
      try {
        const res = await api.saveDevice(payload);
        if (!res.ok) throw new Error((await res.json()).error || "Save failed");
        this.setState({ deviceModal: null }, this.loadDevicesThenFetch);
        return;
      } catch (err) {
        this.setState({ notice: { type: "error", msg: err.message } });
        return;
      }
    }
    // localStorage fallback
    this.setState((s) => {
      let devices;
      if (m.mode === "add") {
        const id = `loc_${Date.now()}_${s.devices.length}`;
        devices = [...s.devices, { ...payload, id }];
      } else {
        devices = s.devices.map((d) =>
          d.id === m.id ? { ...d, ...payload } : d,
        );
      }
      persistLocalDevices(devices);
      return { devices, deviceModal: null };
    }, this.fetchAll);
  };

  // The only delete on this route → is_delete.
  removeDevice = async (d) => {
    if (!this.guard.requireDelete()) return;

    if (d.is_primary) return;
    if (this.backendDevices) {
      try {
        const res = await api.removeDevice(d.id);
        if (!res.ok)
          throw new Error((await res.json()).error || "Remove failed");
        this.setState({ deviceModal: null }, this.loadDevicesThenFetch);
        return;
      } catch (err) {
        this.setState({ notice: { type: "error", msg: err.message } });
        return;
      }
    }
    this.setState((s) => {
      const devices = s.devices.filter((x) => x.id !== d.id);
      persistLocalDevices(devices);
      return { devices, deviceModal: null };
    }, this.fetchAll);
  };

  // ─── ID manager (per device) ──────────────────────────────────────────────
  // Opens the EDITABLE biometric-ID roster. It loads rows to edit and its Save writes
  // them back, so it's an is_edit action despite starting with a read.
  openIdModal = async (device) => {
    if (!this.guard.requireEdit()) return;

    this.setState({
      showIdModal: true,
      idLoading: true,
      idSaveError: null,
      idSerial: device.device_serial,
      idDeviceName: `${device.device_name} · ${branchNameOf(device)}`,
      idRows: [],
    });
    try {
      const res = await api.users(device.device_serial);
      const data = await res.json();
      const saved = new Map(
        (data.users || []).map((u) => [String(u.biometric_unique_id), u]),
      );
      const dd = this.state.deviceData[device.device_serial] || {};
      const sm = this.state.studentMap || {};
      const matchOf = (id) => {
        const stu = sm[String(id).trim()];
        return stu
          ? {
              name: stu.name,
              className: stu.className,
              sectionName: stu.sectionName,
              eduStudentId: stu.eduStudentId,
            }
          : null;
      };
      const seenIds = new Set();
      (dd.dateGroups || []).forEach(({ users }) =>
        users.forEach((u) => seenIds.add(u.userId)),
      );
      const rows = [...seenIds]
        // Hide office staff (employee_type 'employee') from the device's ID list.
        .filter((id) => (saved.get(id) || {}).employee_type !== "employee")
        .sort((a, b) => Number(a) - Number(b))
        .map((id) => {
          const sv = saved.get(id);
          const student = matchOf(id);
          return {
            id,
            name: student ? student.name : sv?.name || "",
            type: student ? "student" : sv?.employee_type || "student",
            status: sv ? "saved" : "new",
            student,
          };
        });
      // Only people who punched in the current period are shown — enrolled-but-
      // idle entries (e.g. old office-employee data) are intentionally excluded.
      this.setState({ idRows: rows, idLoading: false });
    } catch (err) {
      this.setState({ idLoading: false, idSaveError: err.message });
    }
  };
  updateIdRow = (id, field, value) =>
    this.setState((s) => ({
      idRows: s.idRows.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]: value,
              status: r.status === "saved" ? "dirty" : r.status,
            }
          : r,
      ),
    }));
  saveIdRows = async () => {
    // Last gate before the biometric user rows are POSTed.
    if (!this.guard.requireEdit()) return;

    const { idRows, idSerial } = this.state;
    const toSave = idRows.filter(
      (r) => r.name.trim() && (r.status === "new" || r.status === "dirty"),
    );
    if (!toSave.length) return;
    this.setState({ idSaving: true, idSaveError: null });
    try {
      const res = await api.saveUsers(
        idSerial,
        toSave.map((r) => ({
          biometric_unique_id: r.id,
          name: r.name.trim(),
          employee_type: r.type,
        })),
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      this.setState((s) => ({
        idSaving: false,
        idRows: s.idRows.map((r) => ({
          ...r,
          status: r.name.trim() ? "saved" : r.status,
        })),
      }));
      this.fetchDevice(idSerial);
    } catch (err) {
      this.setState({ idSaving: false, idSaveError: err.message });
    }
  };

  // ─── Punch-log sync ───────────────────────────────────────────────────────
  // Pulls punches from the device and WRITES them into attendance → is_edit.
  runLogSync = async (device) => {
    if (!this.guard.requireEdit()) return;

    const serial = device.device_serial;
    this.setState((s) => ({
      logSync: {
        ...s.logSync,
        [serial]: { status: "running", msg: "Pulling…" },
      },
    }));
    try {
      const res = await api.pullDevice(
        serial,
        this.state.fromDate,
        this.state.toDate,
        getInstituteId(),
        device.branch_id,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Sync failed");
      const punches = data.punches ? data.punches.inserted : 0;
      this.setState((s) => ({
        logSync: {
          ...s.logSync,
          [serial]: { status: "done", msg: `${punches} new punch(es)` },
        },
      }));
      this.fetchDevice(serial);
    } catch (err) {
      this.setState((s) => ({
        logSync: {
          ...s.logSync,
          [serial]: {
            status: "error",
            msg: err.message || "Device unreachable",
          },
        },
      }));
    }
  };

  // ─── Access management (intra-branch face sync) ───────────────────────────
  buildPeople = (branch) => {
    const { deviceData } = this.state;
    const people = {};
    branch.devices.forEach((d) => {
      const dd = deviceData[d.device_serial] || {};
      (dd.logs || []).forEach((l) => {
        const id = String(l.device_user_id);
        if (!people[id])
          people[id] = {
            id,
            name: null,
            type: null,
            enrolled: new Set(),
            punched: new Set(),
          };
        people[id].punched.add(d.device_serial);
        if (l.name && !people[id].name) {
          people[id].name = l.name;
          people[id].type = l.employee_type || people[id].type;
        }
      });
      (dd.users || []).forEach((u) => {
        const id = String(u.biometric_unique_id);
        if (!people[id])
          people[id] = {
            id,
            name: null,
            type: null,
            enrolled: new Set(),
            punched: new Set(),
          };
        people[id].enrolled.add(d.device_serial);
        if (u.name) {
          people[id].name = u.name;
          people[id].type = u.employee_type || people[id].type;
        }
      });
    });
    return Object.values(people).sort((a, b) => Number(a.id) - Number(b.id));
  };

  homeSerialFor = (person, branch, targetSerial) => {
    // Prefer a device where the person is enrolled; else where they punched.
    const enrolled = branch.devices.find(
      (d) =>
        d.device_serial !== targetSerial &&
        person.enrolled.has(d.device_serial),
    );
    if (enrolled) return enrolled.device_serial;
    const punched = branch.devices.find(
      (d) =>
        d.device_serial !== targetSerial && person.punched.has(d.device_serial),
    );
    return punched ? punched.device_serial : null;
  };

  setAccess = (serial, id, patch) =>
    this.setState((s) => ({
      access: { ...s.access, [`${serial}:${id}`]: patch },
    }));

  // grantAccess / revokeAccess are internal helpers — the UI reaches them through
  // toggleAccess, which is where the Access Denied modal is raised. They carry a SILENT
  // canEdit() backstop instead of requireEdit() so a bulk loop can't fire the modal once
  // per person; they still can't write without the grant.
  grantAccess = async (person, branch, targetSerial) => {
    if (!canEdit(ATTENDANCE_REGULARIZATION_MODULE)) return;

    const source = this.homeSerialFor(person, branch, targetSerial);
    this.setAccess(targetSerial, person.id, {
      status: "working",
      msg: "Granting…",
    });
    try {
      let res = await api.grantAccess({
        source_device_id: source,
        target_device_id: targetSerial,
        biometric_user_ids: [person.id],
        institute_id: getInstituteId(),
      });
      if (!res.ok) {
        // Fallback for backends without the new endpoint: copy the roster row.
        res = await api.saveUsers(targetSerial, [
          {
            biometric_unique_id: person.id,
            name: person.name || `ID ${person.id}`,
            employee_type: person.type || "student",
          },
        ]);
        if (!res.ok)
          throw new Error((await res.json()).error || "Grant failed");
      }
      this.setAccess(targetSerial, person.id, {
        status: "done",
        msg: "Access granted",
      });
      await this.fetchDevice(targetSerial);
    } catch (err) {
      this.setAccess(targetSerial, person.id, {
        status: "error",
        msg: err.message,
      });
    }
  };

  revokeAccess = async (person, targetSerial) => {
    if (!canEdit(ATTENDANCE_REGULARIZATION_MODULE)) return;

    this.setAccess(targetSerial, person.id, {
      status: "working",
      msg: "Revoking…",
    });
    try {
      const res = await api.revokeAccess({
        target_device_id: targetSerial,
        biometric_user_ids: [person.id],
      });
      if (!res.ok)
        throw new Error(
          (await res.json()).error || "Revoke needs backend deploy",
        );
      this.setAccess(targetSerial, person.id, {
        status: "done",
        msg: "Access revoked",
      });
      await this.fetchDevice(targetSerial);
    } catch (err) {
      this.setAccess(targetSerial, person.id, {
        status: "error",
        msg: err.message,
      });
    }
  };

  // The enrolment toggle — the UI entry point for grant/revoke. Enrolling or revoking a
  // person on a device is a write either way → is_edit.
  toggleAccess = (person, branch, serial) => {
    if (!this.guard.requireEdit()) return;

    if (person.enrolled.has(serial)) this.revokeAccess(person, serial);
    else this.grantAccess(person, branch, serial);
  };

  // BULK: copies every enrolment from a source device to a target → is_edit.
  runBulkCopy = async (branch) => {
    if (!this.guard.requireEdit()) return;

    const { source, target } = this.state.bulkCopy;
    if (!source || !target || source === target) return;
    const sd = this.state.deviceData[source] || {};
    const ids = (sd.users || []).map((u) => String(u.biometric_unique_id));
    if (!ids.length) {
      this.setState((s) => ({
        bulkCopy: {
          ...s.bulkCopy,
          msg: "Source device has no enrolled people.",
        },
      }));
      return;
    }
    this.setState((s) => ({
      bulkCopy: {
        ...s.bulkCopy,
        running: true,
        msg: `Copying ${ids.length} person(s)…`,
      },
    }));
    try {
      let res = await api.grantAccess({
        source_device_id: source,
        target_device_id: target,
        biometric_user_ids: ids,
        institute_id: getInstituteId(),
      });
      if (!res.ok) {
        res = await api.saveUsers(
          target,
          (sd.users || []).map((u) => ({
            biometric_unique_id: u.biometric_unique_id,
            name: u.name || `ID ${u.biometric_unique_id}`,
            employee_type: u.employee_type || "student",
          })),
        );
        if (!res.ok) throw new Error((await res.json()).error || "Copy failed");
      }
      this.setState((s) => ({
        bulkCopy: {
          ...s.bulkCopy,
          running: false,
          msg: `✓ ${ids.length} person(s) copied to target device.`,
        },
      }));
      await this.fetchDevice(target);
    } catch (err) {
      this.setState((s) => ({
        bulkCopy: { ...s.bulkCopy, running: false, msg: `⚠ ${err.message}` },
      }));
    }
  };

  // ─── Student mapping (device biometric IDs ↔ existing students) ───────────
  // Switch tab by navigating — the URL is the source of truth. onTabActive() does the
  // per-tab lazy loading, driven from the URL sync so deep links and back/forward load too.
  // The URL slug for a tab in the CURRENT mode — manual's reports tab has its own address.
  slugFor = (key) => {
    const entry = Object.entries(MODE_ROUTES).find(
      ([, v]) => v.tab === key && v.mode === this.state.mainSource,
    );
    return entry ? entry[0] : key;
  };

  selectTab = (key) => {
    const slug = this.slugFor(key);
    if (this.props.history && slugParam(this.props) !== slug) {
      this.props.history.push(`${AR_BASE}/${slug}`);
      return;
    }
    this.setState({ activeTab: key }, () => this.onTabActive(key));
  };

  onTabActive = (key) => {
    if (key === "mapping" && !this.state.mapStatus && !this.state.mapLoading) this.loadMapping();
    if (key === "onboarding") this.loadOnboarding();
    if (key === "hostel") this.loadHostelRoster();
    if (key === "holidays") this.loadHolidays();
  };

  // ─── Holidays ─────────────────────────────────────────────────────────────
  // Scoped by the header branch filter, like every other tab: no branch ticked = nothing.
  loadHolidays = async () => {
    const inst = getInstituteId();
    if (!inst || !this.props.client) return;
    this.setState({ holidaysLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_HOLIDAYS_QUERY,
        variables: { institute_id: inst, branch_ids: this.selectedBranchCsv() },
        fetchPolicy: "network-only",
      });
      this.setState({
        holidays: (res.data && res.data.getAttendanceHolidays) || [],
        holidayPage: 1, // reset to first page whenever the branch/scope changes
        holidaysLoading: false,
      });
    } catch (e) {
      this.setState({ holidays: [], holidaysLoading: false, holidayError: e.message });
    }
  };

  // The stored value is unix seconds at IST midnight; <input type="date"> wants YYYY-MM-DD.
  // Convert through the IST offset rather than the browser's zone, or a machine west of
  // UTC renders the previous day.
  holidayDateToInput = (unix) => {
    const n = parseInt(unix);
    if (!n) return "";
    return new Date((n + 19800) * 1000).toISOString().slice(0, 10);
  };
  holidayInputToUnix = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = String(ymd).split("-").map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / 1000) - 19800; // IST midnight
  };

  openHolidayForm = (row) => {
    const branchIds = this.selectedBranchIds();
    this.setState({
      holidayError: "",
      holidayForm: row
        ? {
            id: row.id,
            name: row.holiday_name || "",
            date: this.holidayDateToInput(row.date),
            branch_id: row.branch_id,
          }
        : {
            name: "",
            date: "",
            // Default to the only ticked branch when there's exactly one — otherwise make
            // the user choose, since a holiday applies to ONE branch.
            branch_id: branchIds.length === 1 ? branchIds[0] : "",
          },
    });
  };

  saveHoliday = async () => {
    const f = this.state.holidayForm;
    const inst = getInstituteId();
    if (!f || !inst) return;
    if (!f.branch_id) return this.setState({ holidayError: "Please select a branch" });
    if (!f.date) return this.setState({ holidayError: "Please select a date" });
    this.setState({ holidaySaving: true, holidayError: "" });
    try {
      await this.props.client.mutate({
        mutation: SAVE_HOLIDAY_MUTATION,
        variables: {
          params: {
            id: f.id || null,
            holiday_name: f.name || "",
            date: String(this.holidayInputToUnix(f.date)),
            branch_id: parseInt(f.branch_id),
            institute_id: inst,
          },
        },
      });
      this.setState({ holidayForm: null, holidaySaving: false }, this.loadHolidays);
    } catch (e) {
      // Surface the resolver's message (e.g. the duplicate-date rule) instead of failing mute.
      this.setState({
        holidaySaving: false,
        holidayError: String(e.message || "").replace(/^GraphQL error:\s*/, ""),
      });
    }
  };

  deleteHoliday = async (row) => {
    const inst = getInstituteId();
    if (!inst) return;
    if (!window.confirm(`Remove holiday on ${fmtDMYLocal(row.date)}?`)) return;
    try {
      await this.props.client.mutate({
        mutation: DELETE_HOLIDAY_MUTATION,
        variables: { id: row.id, institute_id: inst },
      });
      this.loadHolidays();
    } catch (e) {
      this.setState({ holidayError: e.message });
    }
  };

  loadHostelRoster = async () => {
    const inst = getInstituteId();
    if (!inst) return;
    this.setState({ hostelLoading: true });
    try {
      const res = await api.hostelRoster(inst);
      const data = res.ok ? await res.json() : { residents: [] };
      this.setState({
        hostelRoster: data.residents || [],
        hostelLoading: false,
      });
    } catch (_) {
      this.setState({ hostelLoading: false });
    }
    // Evening punches for the selected hostel night (independent of the console range).
    this.loadHostelDay();
  };

  // Fetch the chosen night's evening (≥ 6 PM) punches across every device so the
  // hostel roll-call reflects exactly ONE day — not whatever range the console
  // filter happens to be on. Keyed by admission → earliest evening time.
  loadHostelDay = async () => {
    const inst = getInstituteId();
    const day = this.state.hostelDate;
    if (!inst || !day) return;
    this.setState({ hostelDayLoading: true });
    const serials = (this.state.devices || []).map((d) => d.device_serial);
    const eveningByAdm = {};
    try {
      await Promise.all(
        serials.map(async (serial) => {
          try {
            const res = await api.syncedLogs(serial, day, day, inst);
            if (!res.ok) return;
            const d = await res.json();
            (d.logs || []).forEach((l) => {
              if (!isEveningPunch(l.datetime)) return;
              const id = String(l.device_user_id);
              if (!eveningByAdm[id] || l.datetime < eveningByAdm[id])
                eveningByAdm[id] = l.datetime;
            });
          } catch (_) {
            /* one device failing shouldn't blank the whole night */
          }
        }),
      );
      this.setState({
        hostelEveningByAdm: eveningByAdm,
        hostelDayLoading: false,
      });
    } catch (_) {
      this.setState({ hostelDayLoading: false });
    }
  };

  setHostelDate = (day) =>
    this.setState({ hostelDate: day, hostelExpanded: {} }, this.loadHostelDay);

  loadMapping = async () => {
    const serial = this.state.mapDevice;
    const inst = getInstituteId();
    if (!serial) return;
    if (!inst) {
      this.setState({
        notice: {
          type: "error",
          msg: "No institute id found in session — cannot match students.",
        },
      });
      return;
    }
    this.setState({ mapLoading: true, rowSearch: {} });
    try {
      const res = await api.mappingStatus(
        serial,
        inst,
        this.state.fromDate,
        this.state.toDate,
      );
      if (res.ok) {
        const data = await res.json();
        this.setState({ mapLoading: false, mapStatus: data });
        return;
      }
    } catch (_) {}
    // Backend not deployed — compute the view from client-loaded student records.
    this.setState({
      mapLoading: false,
      mapStatus: this.computeClientMapping(serial),
    });
  };

  computeClientMapping = (serial) => {
    const dd = this.state.deviceData[serial] || {};
    const sm = this.state.studentMap || {};
    const counts = {};
    const employeeIds = new Set();
    (dd.logs || []).forEach((l) => {
      const id = String(l.device_user_id);
      counts[id] = (counts[id] || 0) + 1;
      if (l.employee_type === "employee") employeeIds.add(id); // hide office staff
    });
    const rows = Object.keys(counts)
      .filter((id) => !employeeIds.has(id))
      .map((id) => {
        const stu = sm[id.trim()];
        return {
          device_user_id: id,
          punches: counts[id],
          status: stu ? "mapped" : "unmatched",
          student: stu
            ? {
                edu_student_id: stu.eduStudentId,
                student_name: stu.name,
                roll_no: stu.rollNo,
                admission_no: id,
                class_name: stu.className,
                section_name: stu.sectionName,
              }
            : null,
        };
      })
      .sort(
        (a, b) =>
          b.punches - a.punches ||
          Number(a.device_user_id) - Number(b.device_user_id),
      );
    return {
      device_id: serial,
      institute_id: getInstituteId(),
      total: rows.length,
      mapped: rows.filter((r) => r.status === "mapped").length,
      matchable: 0,
      unmatched: rows.filter((r) => r.status === "unmatched").length,
      rows,
      clientComputed: true,
    };
  };

  changeMapDevice = (serial) =>
    this.setState(
      { mapDevice: serial, mapStatus: null, rowSearch: {} },
      this.loadMapping,
    );

  // BULK: auto-maps device biometric users to students → is_edit.
  runAutoMap = async () => {
    if (!this.guard.requireEdit()) return;

    const serial = this.state.mapDevice;
    const inst = getInstituteId();
    if (!serial || !inst) return;
    this.setState({ mapBusy: true, notice: null });
    try {
      const res = await api.autoMap(serial, inst);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Auto-match failed");
      const att = data.attendance
        ? ` · ${data.attendance.inserted || 0} attendance row(s) written`
        : "";
      this.setState({
        mapBusy: false,
        notice: {
          type: "info",
          msg: `Auto-match: ${data.linked} student(s) linked · ${data.unmatched_count} still unmatched${att}`,
        },
      });
      this.loadMapping();
      this.fetchDevice(serial);
    } catch (err) {
      this.setState({
        mapBusy: false,
        notice: { type: "error", msg: err.message },
      });
    }
  };

  rowSearchChange = async (id, q) => {
    this.setState((s) => ({
      rowSearch: {
        ...s.rowSearch,
        [id]: { ...(s.rowSearch[id] || {}), q, loading: q.trim().length > 0 },
      },
    }));
    if (q.trim().length < 1) {
      this.setState((s) => ({
        rowSearch: { ...s.rowSearch, [id]: { q, results: [], loading: false } },
      }));
      return;
    }
    try {
      const res = await api.studentSearch(getInstituteId(), q.trim());
      const data = await res.json();
      // Ignore stale responses if the query changed meanwhile
      if ((this.state.rowSearch[id] || {}).q !== q) return;
      this.setState((s) => ({
        rowSearch: {
          ...s.rowSearch,
          [id]: { q, results: data.students || [], loading: false },
        },
      }));
    } catch (_) {
      this.setState((s) => ({
        rowSearch: { ...s.rowSearch, [id]: { q, results: [], loading: false } },
      }));
    }
  };

  // Maps one biometric ID to a student → is_edit.
  assignStudent = async (id, student) => {
    if (!this.guard.requireEdit()) return;

    const serial = this.state.mapDevice;
    const inst = getInstituteId();
    this.setState((s) => ({
      rowSearch: {
        ...s.rowSearch,
        [id]: { ...(s.rowSearch[id] || {}), assigning: true },
      },
    }));
    try {
      const res = await api.mapStudent({
        device_id: serial,
        institute_id: inst,
        biometric_unique_id: id,
        edu_student_id: student.edu_student_id,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Assign failed");
      this.setState((s) => {
        const rs = { ...s.rowSearch };
        delete rs[id];
        return { rowSearch: rs };
      });
      this.loadMapping();
      this.fetchDevice(serial);
    } catch (err) {
      this.setState((s) => ({
        rowSearch: {
          ...s.rowSearch,
          [id]: { ...(s.rowSearch[id] || {}), assigning: false },
        },
        notice: { type: "error", msg: err.message },
      }));
    }
  };

  // ─── Super-admin device onboarding ────────────────────────────────────────
  loadOnboarding = async () => {
    try {
      const [iRes, pRes] = await Promise.all([
        api.listInstitutes(),
        api.pendingDevices(),
      ]);
      const institutes = iRes.ok ? (await iRes.json()).institutes || [] : [];
      const pending = pRes.ok ? (await pRes.json()).pending || [] : [];
      this.setState({ institutes, pending });
    } catch (_) {}
  };

  setAssign = (serial, field, value) =>
    this.setState((s) => ({
      assignSel: {
        ...s.assignSel,
        [serial]: { ...(s.assignSel[serial] || {}), [field]: value },
      },
    }));

  // College picked → load that college's branches for the branch dropdown.
  onAssignCollege = (serial, instituteId) => {
    this.setState(
      (s) => ({
        assignSel: {
          ...s.assignSel,
          [serial]: {
            ...(s.assignSel[serial] || {}),
            institute: instituteId,
            branchId: "",
            branchName: "",
            branches: [],
            branchesLoading: !!instituteId,
          },
        },
      }),
      () => {
        if (instituteId) this.loadBranchesForAssign(serial, instituteId);
      },
    );
  };

  loadBranchesForAssign = async (serial, instituteId) => {
    if (!this.props.client) {
      this.setAssign(serial, "branchesLoading", false);
      return;
    }
    try {
      const res = await this.props.client.query({
        query: GET_BRANCHES_QUERY,
        variables: { institution_id: Number(instituteId) },
        fetchPolicy: "network-only",
      });
      const branches = (res.data && res.data.getBranches) || [];
      this.setState((s) => ({
        assignSel: {
          ...s.assignSel,
          [serial]: {
            ...(s.assignSel[serial] || {}),
            branches,
            branchesLoading: false,
          },
        },
      }));
    } catch (_) {
      this.setState((s) => ({
        assignSel: {
          ...s.assignSel,
          [serial]: { ...(s.assignSel[serial] || {}), branchesLoading: false },
        },
      }));
    }
  };

  onAssignBranch = (serial, branchId) => {
    const sel = this.state.assignSel[serial] || {};
    const b = (sel.branches || []).find(
      (x) => String(x.id) === String(branchId),
    );
    this.setState((s) => ({
      assignSel: {
        ...s.assignSel,
        [serial]: {
          ...(s.assignSel[serial] || {}),
          branchId,
          branchName: b ? b.branch_name : "",
        },
      },
    }));
  };

  // Ready to assign: college chosen, and a branch chosen if the college has any.
  canAssign = (sel) => {
    if (!sel || !sel.institute || sel.busy) return false;
    if (sel.branches && sel.branches.length > 0 && !sel.branchId) return false;
    return true;
  };

  // Assign a pending serial to a college+branch (upsert) or reassign one.
  // opts: { name, branchId, branchName } — when omitted (reassign), branch is preserved.
  // Onboarding: assigns a pending device to an institute/branch → is_edit.
  assignDevice = async (device, instituteId, opts = {}) => {
    if (!this.guard.requireEdit()) return;

    if (!instituteId) return;
    const serial = device.device_serial;
    const bidProvided = opts.branchId !== undefined;
    this.setState((s) => ({
      assignSel: {
        ...s.assignSel,
        [serial]: { ...(s.assignSel[serial] || {}), busy: true },
      },
    }));
    try {
      const res = await api.saveDevice({
        id: device.id, // present for reassign, undefined for a brand-new pending serial
        device_serial: serial,
        device_name: opts.name || device.device_name || serial,
        device_type: device.device_type || "general",
        branch_id: bidProvided
          ? opts.branchId
            ? Number(opts.branchId)
            : null
          : device.branch_id || null,
        branch_name:
          opts.branchName !== undefined
            ? opts.branchName
            : device.branch_name || "",
        location: device.location || "",
        institute_id: Number(instituteId),
        approval_status: device.approval_status || "approved",
      });
      if (!res.ok) throw new Error((await res.json()).error || "Assign failed");
      this.setState((s) => ({
        assignSel: { ...s.assignSel, [serial]: {} },
        notice: {
          type: "info",
          msg: `Device ${serial} assigned to its college/branch.`,
        },
      }));
      this.loadOnboarding();
      this.loadDevicesThenFetch();
    } catch (err) {
      this.setState((s) => ({
        assignSel: {
          ...s.assignSel,
          [serial]: { ...(s.assignSel[serial] || {}), busy: false },
        },
        notice: { type: "error", msg: err.message },
      }));
    }
  };

  // ════════════════════════════════════════════════════════════════════════
  render() {
    const {
      toggled,
      activeTab,
      fromDate,
      toDate,
      activeFilter,
      loadingAll,
      notice,
      devicesReady,
    } = this.state;

    // Until the backend device list resolves we can't tell biometric from manual,
    // so show ONLY a loading screen (with the usual sidebar/header chrome) rather
    // than briefly flashing the manual tabs and then swapping to the biometric set.
    if (!devicesReady) {
      return (
        <div className={toggled}>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div className="header-area">
            <NavbarOne onClick={() => this.menuToggler()} />
          </div>
          <div className="main-wrapper">
            <div
              id="sidebar"
              className={
                isBrowser
                  ? "sidebar-menu d-none d-xl-block d-lg-block"
                  : "sidebar-menu d-block d-xl-none d-lg-none"
              }
            >
              <SideNavbar onClick={() => this.menuToggler()} />
            </div>
            <div
              className="student-overlay"
              onClick={() => this.menuToggler()}
            />
            <div className="content-wrapper pt-0">
              <Container fluid>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "60vh",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      border: "4px solid #e0e7ff",
                      borderTopColor: "#6366f1",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  <div
                    style={{ color: "#64748b", fontSize: 14, fontWeight: 600 }}
                  >
                    Loading attendance…
                  </div>
                </div>
              </Container>
            </div>
            <Footer />
          </div>
        </div>
      );
    }

    const mode = this.getMode(); // "biometric" | "manual"



    const net = this.computeNetwork();
    // Does the SELECTED branch have a biometric device? The dropdown value is
    // "bid:<id>" (from the college branch list), but a device may be tagged only by
    // branch_name (branch_id null) — so match a device to the selected branch by
    // branch_id OR branch_name. When none matches, show the MANUAL view.
    // Multi-select: "all" is now an EMPTY selection. A branch "has a device" if ANY
    // selected branch group matches and actually has devices — reuse branchMatchesSelected.
    const allBranchesSel = this.isAllBranches();
    const branchHasDevice = allBranchesSel
      ? (this.state.devices || []).length > 0
      : net.allBranches.some(
          (b) => this.branchMatchesSelected(b) && b.devices && b.devices.length > 0,
        );
    // Only fall back to the manual view when NONE of the selected branches has a device.
    const showManualForBranch = mode === "biometric" && !allBranchesSel && !branchHasDevice;
    const selectedBranchName = this.selectedBranchLabel();

    const TABS = this.visibleTabs(this.state.mainSource);
    // Effective active tab — fall back to the first allowed tab (e.g. manual mode
    // has no Overview, or devices are still loading).
    const tab = TABS.some((t) => t.key === activeTab)
      ? activeTab
      : TABS[0] && TABS[0].key;

    return (
      <div className={toggled}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
        <div className="header-area">
          <NavbarOne onClick={() => this.menuToggler()} />
        </div>
        <div className="main-wrapper">
          <div
            id="sidebar"
            className={
              isBrowser
                ? "sidebar-menu d-none d-xl-block d-lg-block"
                : "sidebar-menu d-block d-xl-none d-lg-none"
            }
          >
            <SideNavbar onClick={() => this.menuToggler()} />
          </div>
          <div className="student-overlay" onClick={() => this.menuToggler()} />
          <div className="content-wrapper pt-0">
            <Container fluid>
              <div style={S.page}>
                {/* HEADER */}
                <div style={S.pageHdr}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 14 }}
                  >
                    <div style={S.brandMark}>🎓</div>
                    <div>
                      <h4 style={S.pageTitle}>Attendance Administration</h4>
                      <p style={S.pageSub}>
                        {mode === "biometric" ? (
                          <>
                            {(() => {
                              const bc = (this.state.collegeBranches || []).length || net.allBranches.length;
                              return `Biometric + Manual · ${bc} branch${bc !== 1 ? "es" : ""} · ${net.totals.devices} device${net.totals.devices !== 1 ? "s" : ""}`;
                            })()}
                          </>
                        ) : (
                          "Manual attendance · no biometric device for this college"
                        )}
                      </p>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    {/* No BRANCH filter — the header (NavbarOne) scopes every tab on this page. */}
                    <LiveClock />
                  </div>
                </div>

                {/* Tier-1 Main Source Selector */}
                <div style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "16px",
                    marginBottom: "20px",
                    background: "#f1f5f9",
                    padding: "6px",
                    borderRadius: "12px",
                    width: "fit-content"
                }}>
                    {[
                        { key: "combined", label: "Consolidated (Combined)", icon: "fas fa-layer-group", visible: mode === "biometric" },
                        { key: "biometric", label: "Biometric Attendance", icon: "fas fa-fingerprint", visible: mode === "biometric" },
                        { key: "manual", label: "Manual Attendance", icon: "fas fa-user-edit", visible: true },
                        // Holidays sits in this bar (not the tab row) because visibleTabs()
                        // gives combined/manual a single tab each, so the tab row never
                        // renders for them — a tab here would have been unreachable.
                        { key: "holidays", label: "Holidays", icon: "far fa-calendar-alt", visible: true },
                        { key: "command", label: "Command Center", icon: "fas fa-shield-alt", visible: true }
                    ].filter(x => x.visible).map(src => (
                        <button
                            key={src.key}
                            type="button"
                            onClick={() => {
                                const defaultTab =
                                    src.key === "biometric" ? "overview" :
                                    src.key === "command" ? "command" :
                                    src.key === "holidays" ? "holidays" : "reports";
                                // Switch source, then navigate — selectTab/onTabActive handle
                                // that tab's lazy load (overview still needs its own fetch).
                                this.setState({ mainSource: src.key }, () => {
                                    this.selectTab(defaultTab);
                                    if (defaultTab === "overview") this.fetchAll();
                                });
                            }}
                            style={{
                                border: "none",
                                borderRadius: "8px",
                                padding: "8px 16px",
                                fontSize: "14px",
                                fontWeight: "700",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                transition: "all 0.2s",
                                background: this.state.mainSource === src.key ? "#ffffff" : "transparent",
                                color: this.state.mainSource === src.key ? "#4f46e5" : "#475569",
                                boxShadow: this.state.mainSource === src.key ? "0 2px 4px rgba(0,0,0,0.06)" : "none",
                                cursor: "pointer"
                            }}
                        >
                            <i className={src.icon} style={{ fontSize: "14px", color: this.state.mainSource === src.key ? "#4f46e5" : "#64748b" }}></i>
                            {src.label}
                        </button>
                    ))}
                </div>

                {/* TABS + filters */}
                {TABS.length > 1 && (
                  <div style={S.tabBar}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {TABS.map((t) => (
                        <button
                          key={t.key}
                          style={{
                            ...S.tab,
                            ...(tab === t.key ? S.tabActive : {}),
                          }}
                          onClick={() => this.selectTab(t.key)}
                        >
                          <span style={{ marginRight: 6 }}>{t.icon}</span>
                          {t.label}
                        </button>
                      ))}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {tab !== "reports" && (
                        <>
                          {QUICK_FILTERS.map((f) => (
                            <button
                              key={f.key}
                              style={{
                                ...S.pill,
                                ...(activeFilter === f.key ? S.pillActive : {}),
                              }}
                              onClick={() => this.applyFilter(f)}
                              disabled={loadingAll}
                            >
                              {f.label}
                            </button>
                          ))}
                          <button
                            style={{
                              ...S.pill,
                              ...(activeFilter === "custom" ? S.pillActive : {}),
                            }}
                            onClick={() =>
                              this.setState({ activeFilter: "custom" })
                            }
                          >
                            Custom
                          </button>
                        </>
                      )}
                      {tab === "reports" && (
                        <span
                          style={{
                            fontSize: 11.5,
                            color: "#94a3b8",
                            fontWeight: 600,
                          }}
                        >
                          Use the report's own Year / Class / Section / Date
                          filters below
                        </span>
                      )}
                      <button
                        style={S.refreshBtn}
                        onClick={this.fetchAll}
                        disabled={loadingAll}
                      >
                        {loadingAll ? "…" : "↻"}
                      </button>
                    </div>
                  </div>
                )}

                {tab !== "reports" && activeFilter === "custom" && (
                  <div style={S.customRow}>
                    <label style={S.customLbl}>From</label>
                    <input
                      type="date"
                      value={fromDate}
                      min={GO_LIVE}
                      style={S.dateIn}
                      onChange={(e) =>
                        this.setState({ fromDate: clampFrom(e.target.value) })
                      }
                    />
                    <span style={{ color: "#cbd5e1" }}>→</span>
                    <label style={S.customLbl}>To</label>
                    <input
                      type="date"
                      value={toDate}
                      min={GO_LIVE}
                      style={S.dateIn}
                      onChange={(e) =>
                        this.setState({ toDate: e.target.value })
                      }
                    />
                    <button
                      style={S.applyBtn}
                      onClick={this.fetchAll}
                      disabled={loadingAll}
                    >
                      Fetch
                    </button>
                  </div>
                )}

                {notice && (
                  <div
                    style={{
                      ...S.errBar,
                      marginBottom: 12,
                      background:
                        notice.type === "error" ? "#fef2f2" : "#eff6ff",
                      color: notice.type === "error" ? "#dc2626" : "#1d4ed8",
                      borderColor:
                        notice.type === "error" ? "#fecaca" : "#bfdbfe",
                    }}
                  >
                    {notice.type === "error" ? "⚠ " : "ℹ "}
                    {notice.msg}
                    <button
                      style={S.noticeClose}
                      onClick={() => this.setState({ notice: null })}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Selected branch has no biometric device → manual attendance.
                    Command Center, Reports, Onboarding and Devices still work; the
                    device-centric tabs (overview/hostel/mapping/access) show the
                    manual report scoped note instead of empty biometric panels. */}
                {showManualForBranch &&
                  ["overview", "hostel", "mapping", "access"].includes(tab) && (
                    <div style={{
                      ...S.card,
                      border: "1px dashed #bfdbfe",
                      background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                      padding: "24px",
                      borderRadius: "20px",
                      boxShadow: "0 8px 24px rgba(99,102,241,0.04)"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                        <span style={{ fontSize: 28 }}>📝</span>
                        <div>
                          <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b", fontSize: 16 }}>
                            Manual Attendance Mode &mdash; {selectedBranchName}
                          </h5>
                          <span style={{ fontSize: 13, color: "#64748b" }}>
                            No biometric device is assigned to this branch. All logs and attendance for this branch are managed manually.
                          </span>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: 16 }}>
                        <button
                          onClick={() => this.setState({ mainSource: "manual" }, () => this.selectTab("reports"))}
                          style={{
                            background: "linear-gradient(135deg, #4f46e5, #6366f1)",
                            border: "none",
                            borderRadius: "10px",
                            padding: "10px 20px",
                            color: "#fff",
                            fontWeight: "700",
                            fontSize: "13px",
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(99,102,241,0.2)"
                          }}
                        >
                          Go to Manual Reports
                        </button>
                        <button
                          onClick={() => this.setState({ mainSource: "combined" }, () => this.selectTab("reports"))}
                          style={{
                            background: "#fff",
                            border: "1.5px solid #dbeafe",
                            borderRadius: "10px",
                            padding: "9px 20px",
                            color: "#4f46e5",
                            fontWeight: "700",
                            fontSize: "13px",
                            cursor: "pointer"
                          }}
                        >
                          Switch to Consolidated View
                        </button>
                      </div>
                    </div>
                  )}

                {tab === "overview" && !showManualForBranch && this.renderOverview(net)}
                {tab === "reports" && this.renderReports()}
                {tab === "holidays" && this.renderHolidays()}
                {tab === "command" && (
                  <AttendanceCommandCenter
                    branchId={this.selectedBranchIds()[0] || null}
                    branchIds={this.selectedBranchCsv()}
                  />
                )}
                {tab === "hostel" && !showManualForBranch && this.renderHostel(net)}
                {tab === "mapping" && !showManualForBranch && this.renderMapping(net)}
                {tab === "onboarding" && this.renderOnboarding()}
                {tab === "devices" && this.renderDevices(net)}
                {tab === "access" && !showManualForBranch && this.renderAccess(net)}
                {tab === "sync" && this.renderSyncCenter(net)}
              </div>
            </Container>
          </div>
          <Footer />
        </div>

        {this.renderKpiModal()}
        {this.renderEmpModal()}
        {this.renderIdModal()}
        {this.renderDeviceModal()}
        {/* Shown instead of the intercepted action when the grant is missing. One modal
            serves every write on this page (device CRUD, biometric enrolment, log sync,
            student mapping, manual marking) and the single delete. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }

  periodLabel() {
    const f = this.state.activeFilter;
    return f === "today"
      ? "Today"
      : f === "week"
        ? "Last 7 Days"
        : f === "month"
          ? "This Month"
          : "Custom";
  }

  // ═══════════════ OVERVIEW ════════════════════════════════════════════════
  renderOverview(net) {
    const { branches, totals } = net;
    const { selectedBranch } = this.state;
    const periodLbl = this.periodLabel();
    const isMultiDay = periodLbl !== "Today";
    const selected =
      branches.find((b) => b.key === selectedBranch) || branches[0];
    // The branch-wise table is a multi-branch comparison. With a single branch
    // selected it just repeats the KPI numbers, so only show it for "All Branches"
    // when there's more than one branch to compare.
    // Multi-select: the comparison table earns its place whenever more than one branch is
    // in view — i.e. no filter (all), or several picked.
    const showBranchTable = branches.length > 1;
    // ONE consistent attendance metric everywhere: present ÷ registered (biometric-
    // enrolled) students. Present/Absent are DISTINCT headcounts (deduped across
    // branches in computeNetwork), so Attendance% and Absences agree with the counts.
    const presentPct =
      totals.registeredStudents > 0
        ? Math.round((totals.studentsPresent / totals.registeredStudents) * 100)
        : 0;
    const periodRate = presentPct;   // alias kept for any downstream reference
    const periodAbsences = totals.studentsAbsent || 0;
    const KPIS = [
      {
        label: "Total Students",
        val: totals.totalStudents || "—",
        color: "#6366f1",
        icon: "🎓",
        sub: `${totals.registeredStudents} registered`,
        cat: "total",
      },
      {
        label: "Attendance",
        val: `${presentPct}%`,
        color: "#10b981",
        icon: "✅",
        sub: `${totals.studentsPresent} / ${totals.registeredStudents} present`,
        cat: "present",
      },
      {
        label: "Absences",
        val: periodAbsences,
        color: "#ef4444",
        icon: "🚫",
        sub: `${totals.studentsAbsent} not present`,
        cat: "absent",
      },
      {
        label: "Yet to Register",
        val: totals.yetToRegister || 0,
        color: "#f59e0b",
        icon: "🪪",
        sub: "no face enrolled yet",
        cat: "yet",
      },
    ];

    return (
      <>
        <div style={S.kpiRow}>
          {KPIS.map(({ label, val, color, icon, sub, cat }) => (
            <div
              key={label}
              style={{ ...S.kpiCard, cursor: "pointer" }}
              title={`Click to view ${label.toLowerCase()}`}
              onClick={() =>
                this.setState({ kpiModal: cat, kpiClass: "", kpiSection: "" })
              }
            >
              <div style={{ ...S.kpiIcon, background: color + "15" }}>
                {icon}
              </div>
              <div>
                <div style={{ ...S.kpiVal, color }}>{val}</div>
                <div style={S.kpiLabel}>{label}</div>
                <div style={S.kpiSub}>{sub}</div>
              </div>
            </div>
          ))}
        </div>

        {isMultiDay && (
          <div
            style={{
              fontSize: 12,
              color: "#94a3b8",
              margin: "-4px 0 14px 2px",
            }}
          >
            ℹ Everything below covers <b>{periodLbl.toLowerCase()}</b> ·
            attendance % = present-days ÷ possible-days · absences = total
            missed student-days (they grow over a longer range).
          </div>
        )}

        {/* Branch-wise report — comparison view, only across multiple branches */}
        {showBranchTable && (
          <div style={S.card}>
            <div style={S.cardHead}>
              <div>
                <div style={S.cardTitle}>Branch-wise Attendance Report</div>
                <div style={S.cardSub}>
                  {periodLbl} · merged across each branch's devices · click to
                  drill in
                </div>
              </div>
              <span style={S.acePill}>
                {presentPct}% present · {totals.studentsPresent}/
                {totals.registeredStudents} registered · {totals.yetToRegister}{" "}
                yet to register
              </span>
            </div>

            <div style={S.bwHead}>
              <span style={{ ...S.bwh, flex: "0 0 210px" }}>BRANCH</span>
              <span style={{ ...S.bwh, flex: "0 0 80px" }}>STATUS</span>
              <span style={{ ...S.bwh, flex: "0 0 80px", textAlign: "center" }}>
                STUDENTS
              </span>
              <span style={{ ...S.bwh, flex: "0 0 75px", textAlign: "center" }}>
                PRESENT
              </span>
              <span style={{ ...S.bwh, flex: "0 0 75px", textAlign: "center" }}>
                ABSENT
              </span>
              <span style={{ ...S.bwh, flex: "0 0 95px", textAlign: "center" }}>
                YET TO REG
              </span>
              <span style={{ ...S.bwh, flex: 1 }}>% PRESENT</span>
            </div>

            {branches.map((b) => {
              const isSel = b.key === selectedBranch;
              return (
                <div key={b.key}>
                  <div
                    style={{
                      ...S.bwRow,
                      background: isSel ? "#f5f6ff" : "#fff",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      this.setState({
                        selectedBranch: b.key,
                        expandedDates: {},
                      })
                    }
                  >
                    <div
                      style={{
                        flex: "0 0 210px",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <span style={{ ...S.branchDot, background: b.tint }}>
                        {b.name.slice(0, 2)}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.branchName}>{b.name}</div>
                        <div style={S.branchSerial}>
                          {b.devices
                            .map((d) => deviceTypeMeta(d.device_type).icon)
                            .join(" ")}{" "}
                          {b.devices.length} device
                          {b.devices.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <span style={{ flex: "0 0 80px" }}>
                      {this.statusPill(b.status)}
                    </span>
                    <span
                      style={{
                        ...S.bwCell,
                        flex: "0 0 80px",
                        fontWeight: 800,
                        color: "#1e293b",
                      }}
                    >
                      {b.totalStudents || "—"}
                    </span>
                    <span style={{ ...S.bwCell, flex: "0 0 75px" }}>
                      <span
                        style={{
                          ...S.miniTag,
                          background: "#f0fdf4",
                          color: "#15803d",
                        }}
                      >
                        {b.studentsPresent}
                      </span>
                    </span>
                    <span style={{ ...S.bwCell, flex: "0 0 75px" }}>
                      <span
                        style={{
                          ...S.miniTag,
                          background:
                            b.studentsAbsent > 0 ? "#fef2f2" : "#f8fafc",
                          color: b.studentsAbsent > 0 ? "#dc2626" : "#94a3b8",
                        }}
                      >
                        {b.studentsAbsent}
                      </span>
                    </span>
                    <span style={{ ...S.bwCell, flex: "0 0 95px" }}>
                      <span
                        style={{
                          ...S.miniTag,
                          background:
                            b.yetToRegister > 0 ? "#fffbeb" : "#f8fafc",
                          color: b.yetToRegister > 0 ? "#b45309" : "#94a3b8",
                        }}
                        title="Students with no biometric enrollment yet"
                      >
                        {b.yetToRegister}
                      </span>
                    </span>
                    <span
                      style={{
                        ...S.bwCell,
                        flex: 1,
                        justifyContent: "flex-start",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 3,
                      }}
                    >
                      {(() => {
                        const pct =
                          b.registeredStudents > 0
                            ? Math.round(
                                (b.studentsPresent / b.registeredStudents) *
                                  100,
                              )
                            : 0;
                        return (
                          <>
                            <div style={S.accBarWrap}>
                              <div
                                style={{
                                  ...S.accBarFill,
                                  width: `${pct}%`,
                                  background:
                                    pct >= 80
                                      ? "#10b981"
                                      : pct >= 50
                                        ? "#f59e0b"
                                        : "#ef4444",
                                }}
                              />
                            </div>
                            <span style={{ fontSize: 10, color: "#94a3b8" }}>
                              {pct}% present
                            </span>
                          </>
                        );
                      })()}
                    </span>
                  </div>
                  {/* per-device breakdown when selected */}
                  {isSel && (
                    <div style={S.deviceBreak}>
                      {b.deviceRows.map((dr) => (
                        <div
                          key={dr.device.device_serial}
                          style={S.deviceBreakRow}
                        >
                          <span style={{ fontSize: 15 }}>
                            {deviceTypeMeta(dr.device.device_type).icon}
                          </span>
                          <span
                            style={{
                              flex: "0 0 150px",
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: "#334155",
                            }}
                          >
                            {dr.device.device_name}
                          </span>
                          <span
                            style={{
                              flex: "0 0 130px",
                              fontFamily: "monospace",
                              fontSize: 11,
                              color: "#94a3b8",
                            }}
                          >
                            {dr.device.device_serial}
                          </span>
                          <span style={{ flex: "0 0 80px" }}>
                            {this.statusPill(
                              dr.loading
                                ? "loading"
                                : dr.recent
                                  ? "active"
                                  : dr.userCount || dr.punches
                                    ? "idle"
                                    : "offline",
                            )}
                          </span>
                          <span
                            style={{
                              flex: "0 0 90px",
                              fontSize: 12,
                              color: "#64748b",
                            }}
                          >
                            👥 {dr.present} present
                          </span>
                          <span
                            style={{
                              flex: "0 0 90px",
                              fontSize: 12,
                              color: "#64748b",
                            }}
                          >
                            🖐 {dr.punches}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              fontSize: 11,
                              color: "#94a3b8",
                              textAlign: "right",
                            }}
                          >
                            {dr.lastPunch
                              ? `last ${formatTime(dr.lastPunch)}`
                              : "no data"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {selected && this.renderBranchDetail(selected, periodLbl)}
      </>
    );
  }

  renderBranchDetail(b, periodLbl) {
    const sm = this.state.studentMap || {};
    const presentSet = new Set(b.presentAdms || []); // latest day
    const enrolledSet = new Set(b.enrolledAdms || []);
    const daysPresentByAdm = b.daysPresentByAdm || {};
    const numDays = b.numPresentDays || 0;
    const isMultiDay = periodLbl !== "Today";
    // Headline donut = present ÷ registered (the SAME metric as the network KPI),
    // so the branch detail and overview show one consistent attendance %.
    const ratePct = b.registeredStudents > 0
      ? Math.round((b.studentsPresent / b.registeredStudents) * 100)
      : 0;
    // Latest day — used to label the present/absent counts.
    const presentDayLabel = b.presentDay
      ? new Date(b.presentDay + "T00:00:00").toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        })
      : null;

    // Class · Section breakdown:
    //  • PRESENT / ABSENT = the latest day — a real headcount, never shrinks.
    //  • % = attendance rate over the selected period (present-days / possible).
    const groups = {};
    (b.branchAdms || []).forEach((adm) => {
      const stu = sm[adm];
      // Don't drop admissions missing from studentMap — bucket them as Unassigned so
      // the sum of Class·Section rows EQUALS the branch total (no 2–3 discrepancy).
      const cls = (stu && stu.className) || "Unassigned";
      const sec = (stu && stu.sectionName) || "—";
      const key = `${cls}||${sec}`;
      const row = groups[key] || {
        cls,
        sec,
        total: 0,
        registered: 0,
        present: 0,
        presentDays: 0,
      };
      row.total++;
      if (enrolledSet.has(adm)) row.registered++;
      if (presentSet.has(adm)) row.present++; // latest day
      row.presentDays += daysPresentByAdm[adm] || 0; // period
      groups[key] = row;
    });
    const classRows = Object.values(groups)
      .map((r) => {
        // Consistent with the headline: present ÷ registered for this class·section,
        // and absent = registered − present (a real headcount, not missed-day-hits).
        return {
          ...r,
          ratePct: r.registered ? Math.round((r.present / r.registered) * 100) : 0,
          absences: Math.max(0, r.registered - r.present),
        };
      })
      .sort(
        (a, c) =>
          String(a.cls).localeCompare(String(c.cls), undefined, {
            numeric: true,
          }) ||
          String(a.sec).localeCompare(String(c.sec), undefined, {
            numeric: true,
          }),
      );

    // Students present per day (from device punch groups, students only).
    const branchAdmSet = new Set(b.branchAdms || []);
    const trend = (b.summaries || []).map((ds, idx) => {
      const users =
        b.dateGroups && b.dateGroups[idx] ? b.dateGroups[idx].users : [];
      const cnt = users.filter((u) =>
        branchAdmSet.has(String(u.userId)),
      ).length;
      return { date: ds.date, total: cnt };
    });
    const hasTrend = trend.some((t) => t.total > 0);

    return (
      <div style={S.card}>
        <div style={S.cardHead}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ ...S.branchDot, background: b.tint }}>
              {b.name.slice(0, 2)}
            </span>
            <div>
              <div style={S.cardTitle}>{b.name} — Attendance Detail</div>
              <div style={S.cardSub}>
                {b.devices.length} device{b.devices.length !== 1 ? "s" : ""} ·{" "}
                {b.totalStudents} students · {b.registeredStudents} registered ·{" "}
                {periodLbl}
              </div>
            </div>
          </div>
          <button
            style={S.ghostBtn}
            onClick={() =>
              this.setState({ accessBranch: b.key }, () => this.selectTab("access"))
            }
          >
            Manage Access →
          </button>
        </div>

        {/* Device status strip — keeps live device info visible without repeating
            the KPI numbers shown at the top of the page. */}
        {(b.deviceRows || []).length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            {b.deviceRows.map((dr) => (
              <div
                key={dr.device.device_serial}
                style={{
                  flex: "1 1 240px",
                  border: "1px solid #eef2f7",
                  borderRadius: 12,
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "#fff",
                }}
              >
                <span style={{ fontSize: 18 }}>
                  {deviceTypeMeta(dr.device.device_type).icon}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#334155",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dr.device.device_name}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    👥 {dr.present} present · 🖐 {dr.punches} ·{" "}
                    {dr.lastPunch
                      ? `last ${formatTime(dr.lastPunch)}`
                      : "no data"}
                  </div>
                </div>
                {this.statusPill(
                  dr.loading
                    ? "loading"
                    : dr.recent
                      ? "active"
                      : dr.userCount || dr.punches
                        ? "idle"
                        : "offline",
                )}
              </div>
            ))}
          </div>
        )}

        <div style={S.chartsRow}>
          <div
            style={{
              ...S.chartCard,
              flex: "0 0 250px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={S.chartHead}>
              <span style={S.chartTitle}>Attendance</span>
              <span style={S.chartSub}>
                {isMultiDay
                  ? `${periodLbl} · ${numDays} day${numDays !== 1 ? "s" : ""}`
                  : presentDayLabel || "today"}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <DonutRing
                pct={ratePct}
                color="#10b981"
                label="Attendance"
                sub={
                  isMultiDay
                    ? `${numDays} day${numDays !== 1 ? "s" : ""}`
                    : `${b.studentsPresent} / ${b.registeredStudents}`
                }
              />
            </div>
          </div>
          <div
            style={{
              ...S.chartCard,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={S.chartHead}>
              <span style={S.chartTitle}>Daily Attendance Trend</span>
              <span style={S.chartSub}>students present / day</span>
            </div>
            {hasTrend ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <TrendBars data={[...trend].reverse()} />
              </div>
            ) : (
              <div style={{ ...S.chartEmpty, flex: 1 }}>No data</div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: "#1e293b",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Class · Section Attendance
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#15803d",
                background: "#f0fdf4",
                padding: "2px 8px",
                borderRadius: 20,
              }}
            >
              {periodLbl}
            </span>
          </div>
          <div style={S.bwHead}>
            <span style={{ ...S.bwh, flex: "0 0 130px" }}>CLASS</span>
            <span style={{ ...S.bwh, flex: "0 0 130px" }}>SECTION</span>
            <span style={{ ...S.bwh, flex: "0 0 90px", textAlign: "center" }}>
              STUDENTS
            </span>
            <span style={{ ...S.bwh, flex: "0 0 100px", textAlign: "center" }}>
              ABSENCES
            </span>
            <span style={{ ...S.bwh, flex: 1 }}>% ATTENDANCE</span>
          </div>
          {classRows.length === 0 ? (
            <div style={S.centerBox}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>📋</div>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>
                No students mapped for {b.name} yet.
              </div>
            </div>
          ) : (
            classRows.map((r, i) => {
              const cpct = r.ratePct; // attendance rate over the period
              return (
                <div
                  key={`${r.cls}-${r.sec}-${i}`}
                  style={{
                    ...S.bwRow,
                    background: i % 2 === 0 ? "#fff" : "#fafbff",
                  }}
                >
                  <span
                    style={{
                      ...S.bwCell,
                      flex: "0 0 130px",
                      justifyContent: "flex-start",
                      fontWeight: 700,
                      color: "#4f46e5",
                    }}
                  >
                    {r.cls}
                  </span>
                  <span
                    style={{
                      ...S.bwCell,
                      flex: "0 0 130px",
                      justifyContent: "flex-start",
                    }}
                  >
                    {r.sec}
                  </span>
                  <span
                    style={{
                      ...S.bwCell,
                      flex: "0 0 90px",
                      fontWeight: 800,
                      color: "#1e293b",
                    }}
                  >
                    {r.total}
                  </span>
                  <span style={{ ...S.bwCell, flex: "0 0 100px" }}>
                    <span
                      style={{
                        ...S.miniTag,
                        background: r.absences > 0 ? "#fef2f2" : "#f8fafc",
                        color: r.absences > 0 ? "#dc2626" : "#94a3b8",
                      }}
                    >
                      {r.absences}
                    </span>
                  </span>
                  <span
                    style={{
                      ...S.bwCell,
                      flex: 1,
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: 3,
                    }}
                  >
                    <div style={S.accBarWrap}>
                      <div
                        style={{
                          ...S.accBarFill,
                          width: `${cpct}%`,
                          background:
                            cpct >= 80
                              ? "#10b981"
                              : cpct >= 50
                                ? "#f59e0b"
                                : "#ef4444",
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>
                      {cpct}% attendance
                    </span>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ═══════════════ DEVICES ═════════════════════════════════════════════════
  renderDevices(net) {
    const { branches } = net;
    return (
      <div style={S.card}>
        <div style={S.cardHead}>
          <div>
            <div style={S.cardTitle}>Branches & Devices</div>
            <div style={S.cardSub}>
              Register and monitor every biometric device, grouped by branch
              {!this.backendDevices && (
                <span style={S.localTag}>local mode</span>
              )}
            </div>
          </div>
          <button style={S.applyBtn} onClick={() => this.openAddDevice("")}>
            + Add Device
          </button>
        </div>

        {branches.map((b) => (
          <div key={b.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
            <div style={S.branchHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ ...S.branchDot, background: b.tint }}>
                  {b.name.slice(0, 2)}
                </span>
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}
                  >
                    {b.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    {b.devices.length} device{b.devices.length !== 1 ? "s" : ""}{" "}
                    · {b.enrolledCount} enrolled · {b.present} present (
                    {this.periodLabel().toLowerCase()})
                  </div>
                </div>
              </div>
              <button
                style={S.ghostBtn}
                onClick={() => this.openAddDevice(b.name)}
              >
                + Add to {b.name}
              </button>
            </div>
            <div style={S.deviceGrid}>
              {b.deviceRows.map((dr) => {
                const d = dr.device;
                const meta = deviceTypeMeta(d.device_type);
                const st = dr.loading
                  ? "loading"
                  : dr.recent
                    ? "active"
                    : dr.userCount || dr.punches
                      ? "idle"
                      : "offline";
                return (
                  <div
                    key={d.device_serial}
                    style={{
                      ...S.deviceCard,
                      borderTop: `3px solid ${b.tint}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span style={S.deviceIcon}>{meta.icon}</span>
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 800,
                              color: "#1e293b",
                            }}
                          >
                            {d.device_name}
                            {d.is_primary ? (
                              <span style={S.curBadge}>LIVE</span>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>
                            {meta.label} · {d.location || "—"}
                          </div>
                        </div>
                      </div>
                      {this.statusPill(st)}
                    </div>
                    <div style={S.serialBox}>
                      <span
                        style={{
                          fontSize: 10,
                          color: "#94a3b8",
                          fontWeight: 700,
                        }}
                      >
                        DEVICE SERIAL
                      </span>
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 800,
                          color: "#4f46e5",
                          fontSize: 13,
                        }}
                      >
                        {d.device_serial}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <span style={S.govChip}>
                        Rizee-deployed · cost: {d.cost_borne_by || "Vignan"}
                      </span>
                      <span
                        style={{
                          ...S.govChip,
                          background:
                            d.approval_status === "approved"
                              ? "#f0fdf4"
                              : "#fffbeb",
                          color:
                            d.approval_status === "approved"
                              ? "#15803d"
                              : "#b45309",
                          borderColor:
                            d.approval_status === "approved"
                              ? "#bbf7d0"
                              : "#fde68a",
                        }}
                        title={
                          d.approval_ref
                            ? `Approval ref: ${d.approval_ref}`
                            : "No approval reference on file"
                        }
                      >
                        {d.approval_status === "approved"
                          ? "✓ Approved"
                          : "⏳ Approval pending"}
                      </span>
                    </div>
                    <div style={S.devStatsRow}>
                      <div style={S.devStat}>
                        <div style={{ ...S.devStatVal, color: b.tint }}>
                          {dr.userCount}
                        </div>
                        <div style={S.devStatLbl}>Faces</div>
                      </div>
                      <div style={S.devStat}>
                        <div style={{ ...S.devStatVal, color: "#0ea5e9" }}>
                          {dr.present}
                        </div>
                        <div style={S.devStatLbl}>Present</div>
                      </div>
                      <div style={S.devStat}>
                        <div style={{ ...S.devStatVal, color: "#f59e0b" }}>
                          {dr.punches}
                        </div>
                        <div style={S.devStatLbl}>Punches</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      Last punch:{" "}
                      <b style={{ color: "#475569" }}>
                        {dr.lastPunch
                          ? `${dr.lastPunch.split(" ")[0]} ${formatTime(dr.lastPunch)}`
                          : "—"}
                      </b>
                    </div>
                    <div style={S.devActions}>
                      <button
                        style={S.ghostBtn}
                        onClick={() =>
                          this.setState({ selectedBranch: b.key }, () => this.selectTab("overview"))
                        }
                      >
                        Reports
                      </button>
                      <button
                        style={S.ghostBtn}
                        onClick={() => this.openIdModal(d)}
                      >
                        Manage IDs
                      </button>
                      <button
                        style={S.ghostBtn}
                        disabled={this.state.syncingUsers === d.device_serial}
                        onClick={() => this.syncDeviceUsers(d)}
                      >
                        {this.state.syncingUsers === d.device_serial
                          ? "Syncing…"
                          : "Sync faces"}
                      </button>
                      <button
                        style={S.ghostBtn}
                        onClick={() => this.openEditDevice(d)}
                      >
                        Edit
                      </button>
                      {!d.is_primary && (
                        <button
                          style={{
                            ...S.ghostBtn,
                            color: "#dc2626",
                            borderColor: "#fecaca",
                          }}
                          onClick={() => this.removeDevice(d)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ═══════════════ ACCESS MANAGEMENT ═══════════════════════════════════════
  renderAccess(net) {
    const { branches } = net;
    const { accessBranch, peopleFilter, access, bulkCopy } = this.state;
    const branch = branches.find((b) => b.key === accessBranch) || branches[0];
    if (!branch)
      return (
        <div style={S.card}>
          <div style={S.centerBox}>
            <div style={{ color: "#94a3b8" }}>No branches configured.</div>
          </div>
        </div>
      );

    const people = this.buildPeople(branch);
    const filtered = peopleFilter.trim()
      ? people.filter(
          (p) =>
            p.id.includes(peopleFilter.trim()) ||
            (p.name || "")
              .toLowerCase()
              .includes(peopleFilter.trim().toLowerCase()),
        )
      : people;
    const devices = branch.devices;
    const multiDevice = devices.length > 1;

    return (
      <>
        <div style={S.card}>
          <div style={S.cardHead}>
            <div>
              <div style={S.cardTitle}>
                🔐 Access Management — Internal Branch Face Sync
              </div>
              <div style={S.cardSub}>
                Grant a person access to another device in the branch · copies
                their enrollment + pushes the face to that device
              </div>
            </div>
          </div>

          {/* branch picker */}
          <div style={S.accessToolbar}>
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: "#94a3b8",
                  marginRight: 4,
                }}
              >
                BRANCH
              </span>
              {branches.map((b) => (
                <button
                  key={b.key}
                  style={{
                    ...S.pill,
                    ...(b.key === branch.key ? S.pillActive : {}),
                  }}
                  onClick={() => this.setState({ accessBranch: b.key })}
                >
                  {b.name} ({b.devices.length})
                </button>
              ))}
            </div>
            <input
              style={S.searchInput}
              placeholder="🔍 Filter by ID or name…"
              value={peopleFilter}
              onChange={(e) => this.setState({ peopleFilter: e.target.value })}
            />
          </div>

          {/* quick device → device copy */}
          {multiDevice && (
            <div style={S.bulkRow}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>
                Quick copy:
              </span>
              <select
                style={S.smallSelect}
                value={bulkCopy.source}
                onChange={(e) =>
                  this.setState((s) => ({
                    bulkCopy: { ...s.bulkCopy, source: e.target.value },
                  }))
                }
              >
                <option value="">From device…</option>
                {devices.map((d) => (
                  <option key={d.device_serial} value={d.device_serial}>
                    {d.device_name}
                  </option>
                ))}
              </select>
              <span style={{ color: "#94a3b8" }}>→</span>
              <select
                style={S.smallSelect}
                value={bulkCopy.target}
                onChange={(e) =>
                  this.setState((s) => ({
                    bulkCopy: { ...s.bulkCopy, target: e.target.value },
                  }))
                }
              >
                <option value="">To device…</option>
                {devices.map((d) => (
                  <option key={d.device_serial} value={d.device_serial}>
                    {d.device_name}
                  </option>
                ))}
              </select>
              <button
                style={{
                  ...S.applyBtn,
                  opacity:
                    bulkCopy.running ||
                    !bulkCopy.source ||
                    !bulkCopy.target ||
                    bulkCopy.source === bulkCopy.target
                      ? 0.5
                      : 1,
                }}
                disabled={
                  bulkCopy.running ||
                  !bulkCopy.source ||
                  !bulkCopy.target ||
                  bulkCopy.source === bulkCopy.target
                }
                onClick={() => this.runBulkCopy(branch)}
              >
                {bulkCopy.running ? "Copying…" : "Copy enrolled people"}
              </button>
              {bulkCopy.msg && (
                <span
                  style={{
                    fontSize: 12,
                    color: bulkCopy.msg.startsWith("⚠") ? "#dc2626" : "#15803d",
                    fontWeight: 600,
                  }}
                >
                  {bulkCopy.msg}
                </span>
              )}
            </div>
          )}

          {/* legend */}
          <div style={S.legend}>
            <span>
              <span style={{ ...S.legendDot, background: "#10b981" }} /> Has
              access (enrolled)
            </span>
            <span>
              <span style={{ ...S.legendDot, background: "#f59e0b" }} />{" "}
              Punched, not enrolled
            </span>
            <span>
              <span style={{ ...S.legendDot, background: "#cbd5e1" }} /> No
              access — click to grant
            </span>
          </div>

          {/* matrix */}
          {!multiDevice ? (
            <div style={S.centerBox}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>🖥</div>
              <div style={{ fontWeight: 700, color: "#1e293b" }}>
                {branch.name} has a single device
              </div>
              <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                Add another device to this branch (Hostel, Library…) to manage
                cross-device access.
              </div>
              <button
                style={{ ...S.applyBtn, marginTop: 12 }}
                onClick={() => this.openAddDevice(branch.name)}
              >
                + Add Device to {branch.name}
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={S.centerBox}>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>
                No punch-history IDs in this branch for the selected period.
              </div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={S.matrix}>
                <thead>
                  <tr>
                    <th
                      style={{
                        ...S.mxTh,
                        textAlign: "left",
                        minWidth: 180,
                        position: "sticky",
                        left: 0,
                        background: "#f8fafc",
                      }}
                    >
                      PERSON
                    </th>
                    {devices.map((d) => (
                      <th
                        key={d.device_serial}
                        style={{ ...S.mxTh, minWidth: 110 }}
                      >
                        <div>
                          {deviceTypeMeta(d.device_type).icon} {d.device_name}
                        </div>
                        {d.is_primary ? (
                          <div style={{ fontSize: 8, color: "#4f46e5" }}>
                            LIVE
                          </div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id}>
                      <td style={{ ...S.mxPersonCell }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: p.name ? "#1e293b" : "#94a3b8",
                          }}
                        >
                          {p.name || `ID ${p.id}`}
                          {p.type && <span style={S.personType}>{p.type}</span>}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: "#94a3b8",
                            fontFamily: "monospace",
                          }}
                        >
                          ID: {p.id}
                        </div>
                      </td>
                      {devices.map((d) => this.renderAccessCell(p, branch, d))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={S.syncFootNote}>
            Granting copies the person's biometric ID → name → type record to
            the target device and calls the device's remote-enroll API to push
            the face template. People are sourced from punch history; assign
            names via <b>Manage IDs</b> on any device.
          </div>
        </div>
      </>
    );
  }

  renderAccessCell(person, branch, device) {
    const serial = device.device_serial;
    const work = this.state.access[`${serial}:${person.id}`];
    const enrolled = person.enrolled.has(serial);
    const punched = person.punched.has(serial);

    if (work && work.status === "working")
      return (
        <td key={serial} style={S.mxCell}>
          <span style={{ fontSize: 11, color: "#6366f1", fontWeight: 700 }}>
            ⟳
          </span>
        </td>
      );

    let bg, color, label, title;
    if (enrolled) {
      bg = "#f0fdf4";
      color = "#15803d";
      label = "✓ Access";
      title = "Has access — click to revoke";
    } else if (punched) {
      bg = "#fff7ed";
      color = "#c2410c";
      label = "+ Grant";
      title = "Punched here but not enrolled — click to grant access";
    } else {
      bg = "#f8fafc";
      color = "#94a3b8";
      label = "+ Grant";
      title = "No access — click to grant";
    }
    return (
      <td key={serial} style={S.mxCell}>
        <button
          style={{ ...S.accessChip, background: bg, color }}
          title={title}
          onClick={() => this.toggleAccess(person, branch, serial)}
        >
          {label}
        </button>
        {work && work.status === "error" && (
          <div style={{ fontSize: 9, color: "#dc2626", marginTop: 2 }}>
            {work.msg}
          </div>
        )}
      </td>
    );
  }

  // ═══════════════ SYNC CENTER ═════════════════════════════════════════════
  renderSyncCenter(net) {
    const { branches } = net;
    const { logSync } = this.state;
    return (
      <div style={S.card}>
        <div style={S.cardHead}>
          <div>
            <div style={S.cardTitle}>⏱ Punch-Log Sync</div>
            <div style={S.cardSub}>
              Pull the latest punches from each device into the database for the
              selected period
            </div>
          </div>
          <button style={S.ghostBtn} onClick={this.fetchAll}>
            Refresh all from DB
          </button>
        </div>
        <div style={S.bwHead}>
          <span style={{ ...S.bwh, flex: "0 0 230px" }}>DEVICE</span>
          <span style={{ ...S.bwh, flex: "0 0 130px" }}>BRANCH</span>
          <span style={{ ...S.bwh, flex: "0 0 90px", textAlign: "center" }}>
            PUNCHES
          </span>
          <span style={{ ...S.bwh, flex: 1 }}>SYNC RESULT</span>
          <span style={{ ...S.bwh, flex: "0 0 120px", textAlign: "right" }}>
            ACTION
          </span>
        </div>
        {branches.map((b) =>
          b.deviceRows.map((dr) => {
            const d = dr.device;
            const ls = logSync[d.device_serial] || { status: "idle" };
            return (
              <div
                key={d.device_serial}
                style={{ ...S.bwRow, background: "#fff" }}
              >
                <div
                  style={{
                    flex: "0 0 230px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span style={S.deviceIcon}>
                    {deviceTypeMeta(d.device_type).icon}
                  </span>
                  <div>
                    <div style={S.branchName}>{d.device_name}</div>
                    <div style={S.branchSerial}>{d.device_serial}</div>
                  </div>
                </div>
                <span
                  style={{
                    ...S.bwCell,
                    flex: "0 0 130px",
                    justifyContent: "flex-start",
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  {b.name}
                </span>
                <span
                  style={{
                    ...S.bwCell,
                    flex: "0 0 90px",
                    fontWeight: 700,
                    color: "#64748b",
                  }}
                >
                  {dr.punches || "—"}
                </span>
                <span
                  style={{ ...S.bwCell, flex: 1, justifyContent: "flex-start" }}
                >
                  {ls.status === "running" && (
                    <span style={{ color: "#6366f1", fontSize: 12 }}>
                      ⟳ {ls.msg}
                    </span>
                  )}
                  {ls.status === "done" && (
                    <span style={{ color: "#15803d", fontSize: 12 }}>
                      ✓ {ls.msg}
                    </span>
                  )}
                  {ls.status === "error" && (
                    <span style={{ color: "#dc2626", fontSize: 12 }}>
                      ⚠ {ls.msg}
                    </span>
                  )}
                  {ls.status === "idle" && (
                    <span style={{ color: "#cbd5e1", fontSize: 12 }}>
                      Not synced this session
                    </span>
                  )}
                </span>
                <span
                  style={{
                    ...S.bwCell,
                    flex: "0 0 120px",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    style={{ ...S.ghostBtn, padding: "5px 12px" }}
                    disabled={ls.status === "running"}
                    onClick={() => this.runLogSync(d)}
                  >
                    {ls.status === "running" ? "…" : "Pull Logs"}
                  </button>
                </span>
              </div>
            );
          }),
        )}
        <div style={S.syncFootNote}>
          Pull reaches the device over the local network (or the on-site sync
          agent). From a remote network, use “Refresh all from DB” to load
          punches already collected by the on-site agent. Face enrollment is
          managed in <b>Access Management</b>.
        </div>
      </div>
    );
  }

  // ═══════════════ STUDENT MAPPING ═════════════════════════════════════════
  renderMapping(net) {
    const {
      devices,
      mapDevice,
      mapStatus,
      mapLoading,
      mapBusy,
      mapFilter,
      rowSearch,
    } = this.state;
    const inst = getInstituteId();
    const device =
      devices.find((d) => d.device_serial === mapDevice) || devices[0];

    const rows = mapStatus ? mapStatus.rows || [] : [];
    const visible =
      mapFilter === "mapped"
        ? rows.filter((r) => r.status === "mapped")
        : mapFilter === "unmatched"
          ? rows.filter((r) => r.status !== "mapped")
          : rows;

    return (
      <div style={S.card}>
        <div style={S.cardHead}>
          <div>
            <div style={S.cardTitle}>
              🧑‍🎓 Student Mapping — Admission No ↔ Students
            </div>
            <div style={S.cardSub}>
              Institute #{inst || "—"} · device punches carry the admission
              number, matched directly to students · only unknown admission
              numbers need faculty attention
            </div>
          </div>
          <button
            style={{
              ...S.markBtn,
              opacity:
                mapBusy || (mapStatus && mapStatus.clientComputed) ? 0.5 : 1,
            }}
            onClick={this.runAutoMap}
            disabled={mapBusy || !mapStatus || mapStatus.clientComputed}
            title={
              mapStatus && mapStatus.clientComputed
                ? "Deploy the backend to write links + backfill attendance"
                : ""
            }
          >
            {mapBusy ? "Matching…" : "⚡ Auto-match & sync"}
          </button>
        </div>

        {/* device picker + summary */}
        <div style={S.accessToolbar}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}>
              DEVICE
            </span>
            <select
              style={S.smallSelect}
              value={mapDevice || ""}
              onChange={(e) => this.changeMapDevice(e.target.value)}
            >
              {devices.map((d) => (
                <option key={d.device_serial} value={d.device_serial}>
                  {deviceTypeMeta(d.device_type).icon} {d.device_name} ·{" "}
                  {branchNameOf(d)} · {d.device_serial}
                </option>
              ))}
            </select>
            <button
              style={S.ghostBtn}
              onClick={this.loadMapping}
              disabled={mapLoading}
            >
              {mapLoading ? "…" : "↻ Reload"}
            </button>
          </div>
          {mapStatus && !mapStatus.unavailable && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { k: "all", label: `All ${mapStatus.total}`, c: "#475569" },
                {
                  k: "mapped",
                  label: `✓ Mapped ${mapStatus.mapped}`,
                  c: "#15803d",
                },
                {
                  k: "unmatched",
                  label: `⚠ To fix ${mapStatus.matchable + mapStatus.unmatched}`,
                  c: "#c2410c",
                },
              ].map((f) => (
                <button
                  key={f.k}
                  style={{
                    ...S.pill,
                    ...(mapFilter === f.k
                      ? {
                          background: f.c,
                          border: `1px solid ${f.c}`,
                          color: "#fff",
                        }
                      : { color: f.c }),
                  }}
                  onClick={() => this.setState({ mapFilter: f.k })}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {mapLoading ? (
          <div style={S.centerBox}>
            <div style={S.spinner} />
            <div style={{ color: "#64748b", fontSize: 14, marginTop: 12 }}>
              Loading mapping…
            </div>
          </div>
        ) : !mapStatus ? (
          <div style={S.centerBox}>
            <div style={{ color: "#94a3b8", fontSize: 13 }}>
              Select a device and reload.
            </div>
          </div>
        ) : mapStatus.unavailable ? (
          <div style={S.centerBox}>
            <div style={{ fontSize: 30, marginBottom: 6 }}>🛠</div>
            <div style={{ fontWeight: 700, color: "#1e293b" }}>
              Student mapping needs the updated backend
            </div>
            <div
              style={{
                color: "#94a3b8",
                fontSize: 13,
                marginTop: 4,
                maxWidth: 460,
                textAlign: "center",
              }}
            >
              Deploy the new <b>/attendance/mapping-status</b>, <b>/auto-map</b>{" "}
              and <b>/map-student</b> endpoints to enable matching device IDs to
              students for institute #{inst}.
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div style={S.centerBox}>
            <div style={{ color: "#94a3b8", fontSize: 13 }}>
              No device IDs in this view yet. IDs appear automatically once the
              device pushes punch data.
            </div>
          </div>
        ) : (
          <>
            <div style={S.mapHead}>
              <span style={{ ...S.bwh, flex: "0 0 110px" }}>ADMISSION NO</span>
              <span style={{ ...S.bwh, flex: "0 0 80px", textAlign: "center" }}>
                PUNCHES
              </span>
              <span style={{ ...S.bwh, flex: 1 }}>
                STUDENT · CLASS / SECTION
              </span>
              <span style={{ ...S.bwh, flex: "0 0 150px" }}>STATUS</span>
            </div>
            {visible.map((r) => this.renderMapRow(r, device))}
            <div style={S.syncFootNote}>
              Punches resolve to students directly by <b>admission number</b>{" "}
              (institute #{inst}). An unmatched admission number means it isn't
              in the student database yet — add the student in the ERP.
              {mapStatus && mapStatus.clientComputed && (
                <>
                  {" "}
                  <b>Read-only view</b> from student records — deploy the
                  backend to write the links and backfill attendance.
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  renderMapRow(r, device) {
    const rs = this.state.rowSearch[r.device_user_id] || {};
    const st = r.student;
    const clientMode =
      this.state.mapStatus && this.state.mapStatus.clientComputed;
    const statusMeta = {
      mapped: { t: "✓ Mapped", c: "#15803d", bg: "#f0fdf4" },
      matchable: { t: "● Auto-matchable", c: "#1d4ed8", bg: "#eff6ff" },
      unmatched: { t: "⚠ Unmatched", c: "#c2410c", bg: "#fff7ed" },
    }[r.status];

    return (
      <div key={r.device_user_id} style={S.mapRow}>
        <span
          style={{
            flex: "0 0 110px",
            fontFamily: "monospace",
            fontWeight: 800,
            color: "#4f46e5",
            fontSize: 13,
          }}
        >
          {r.device_user_id}
        </span>
        <span
          style={{
            flex: "0 0 80px",
            textAlign: "center",
            fontWeight: 700,
            color: "#64748b",
            fontSize: 13,
          }}
        >
          {r.punches}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {r.status !== "unmatched" && st ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                {st.student_name || `Student #${st.edu_student_id}`}
              </div>
              <div style={{ fontSize: 10.5, color: "#94a3b8" }}>
                {st.class_name
                  ? `${st.class_name}${st.section_name ? " · " + st.section_name : ""}`
                  : ""}
                {st.roll_no ? ` · Roll ${st.roll_no}` : ""}
                {st.admission_no ? ` · Adm ${st.admission_no}` : ""}
              </div>
            </div>
          ) : clientMode ? (
            <div style={{ fontSize: 12, color: "#c2410c" }}>
              Admission <b>{r.device_user_id}</b> not found in student records —
              add the student in the ERP.
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              <input
                style={S.searchInput}
                placeholder="🔍 Search student by name / roll / admission…"
                value={rs.q || ""}
                onChange={(e) =>
                  this.rowSearchChange(r.device_user_id, e.target.value)
                }
              />
              {rs.q && (rs.results || []).length > 0 && (
                <div style={S.searchDrop}>
                  {rs.results.slice(0, 8).map((stu) => (
                    <div
                      key={stu.edu_student_id}
                      style={S.searchItem}
                      onClick={() => this.assignStudent(r.device_user_id, stu)}
                    >
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                      >
                        {stu.student_name}
                      </div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>
                        {stu.class_name
                          ? `${stu.class_name}${stu.section_name ? " · " + stu.section_name : ""} · `
                          : ""}
                        Roll {stu.roll_no || "—"} · Adm{" "}
                        {stu.admission_no || "—"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {rs.loading && (
                <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                  Searching…
                </div>
              )}
              {rs.assigning && (
                <div style={{ fontSize: 10, color: "#6366f1", marginTop: 2 }}>
                  Assigning…
                </div>
              )}
            </div>
          )}
        </div>
        <span style={{ flex: "0 0 150px" }}>
          <span
            style={{
              ...S.statusPill,
              color: statusMeta.c,
              background: statusMeta.bg,
            }}
          >
            {statusMeta.t}
          </span>
        </span>
      </div>
    );
  }

  // ═══════════════ DEVICE ONBOARDING (super admin) ═════════════════════════
  renderOnboarding() {
    const { institutes, pending, devices, assignSel } = this.state;
    // Registered devices grouped by college (super-admin GET /devices returns all).
    const byCollege = {};
    devices.forEach((d) => {
      const key = d.institute_id
        ? d.institute_name || `Institute #${d.institute_id}`
        : "⚠ Unassigned";
      if (!byCollege[key]) byCollege[key] = [];
      byCollege[key].push(d);
    });
    const collegeKeys = Object.keys(byCollege).sort((a, b) =>
      a.startsWith("⚠") ? -1 : b.startsWith("⚠") ? 1 : a.localeCompare(b),
    );

    return (
      <>
        {/* Pending / unassigned devices */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <div>
              <div style={S.cardTitle}>🛰 Device Onboarding — Super Admin</div>
              <div style={S.cardSub}>
                Devices that pushed data but aren't assigned to a college yet ·
                assign each to its college
              </div>
            </div>
            <button style={S.ghostBtn} onClick={this.loadOnboarding}>
              ↻ Reload
            </button>
          </div>
          {pending.length === 0 ? (
            <div style={S.centerBox}>
              <div style={{ fontSize: 30, marginBottom: 6 }}>✅</div>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>
                No unassigned devices — every device that pushed data is mapped
                to a college.
              </div>
            </div>
          ) : (
            <>
              <div style={S.bwHead}>
                <span style={{ ...S.bwh, flex: "0 0 160px" }}>
                  DEVICE SERIAL
                </span>
                <span
                  style={{ ...S.bwh, flex: "0 0 80px", textAlign: "center" }}
                >
                  PUNCHES
                </span>
                <span style={{ ...S.bwh, flex: "0 0 150px" }}>LAST SEEN</span>
                <span style={{ ...S.bwh, flex: 1 }}>ASSIGN TO COLLEGE</span>
              </div>
              {pending.map((p) => {
                const sel = assignSel[p.device_serial] || {};
                return (
                  <div
                    key={p.device_serial}
                    style={{ ...S.bwRow, background: "#fffdf5" }}
                  >
                    <span
                      style={{
                        flex: "0 0 160px",
                        fontFamily: "monospace",
                        fontWeight: 800,
                        color: "#4f46e5",
                        fontSize: 13,
                      }}
                    >
                      {p.device_serial}
                    </span>
                    <span
                      style={{
                        flex: "0 0 80px",
                        textAlign: "center",
                        fontWeight: 700,
                        color: "#64748b",
                      }}
                    >
                      {p.punches}
                    </span>
                    <span
                      style={{
                        flex: "0 0 150px",
                        fontSize: 12,
                        color: "#94a3b8",
                      }}
                    >
                      {p.last_seen
                        ? `${p.last_seen.split(" ")[0]} ${formatTime(p.last_seen)}`
                        : "—"}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <select
                        style={S.smallSelect}
                        value={sel.institute || ""}
                        onChange={(e) =>
                          this.onAssignCollege(p.device_serial, e.target.value)
                        }
                      >
                        <option value="">Select college…</option>
                        {institutes.map((i) => (
                          <option key={i.institute_id} value={i.institute_id}>
                            {i.name} (#{i.institute_id})
                          </option>
                        ))}
                      </select>
                      {sel.institute ? (
                        <select
                          style={S.smallSelect}
                          value={sel.branchId || ""}
                          disabled={sel.branchesLoading}
                          onChange={(e) =>
                            this.onAssignBranch(p.device_serial, e.target.value)
                          }
                        >
                          <option value="">
                            {sel.branchesLoading
                              ? "Loading branches…"
                              : (sel.branches || []).length
                                ? "Select branch…"
                                : "No branches"}
                          </option>
                          {(sel.branches || []).map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.branch_name}
                            </option>
                          ))}
                        </select>
                      ) : null}
                      <input
                        style={{ ...S.searchInput, minWidth: 110, width: 130 }}
                        placeholder="Device name"
                        value={sel.name || ""}
                        onChange={(e) =>
                          this.setAssign(
                            p.device_serial,
                            "name",
                            e.target.value,
                          )
                        }
                      />
                      <button
                        style={{
                          ...S.applyBtn,
                          opacity: this.canAssign(sel) ? 1 : 0.5,
                        }}
                        disabled={!this.canAssign(sel)}
                        onClick={() =>
                          this.assignDevice(
                            { device_serial: p.device_serial },
                            sel.institute,
                            {
                              name: sel.name,
                              branchId: sel.branchId,
                              branchName: sel.branchName,
                            },
                          )
                        }
                      >
                        {sel.busy ? "Assigning…" : "Assign"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </>
          )}
          {institutes.length === 0 && (
            <div style={S.syncFootNote}>
              ⚠ Couldn't load the college list — the super-admin endpoints may
              not be deployed yet. Until then, assign devices from each
              college's own login.
            </div>
          )}
        </div>

        {/* All registered devices, grouped by college */}
        <div style={S.card}>
          <div style={S.cardHead}>
            <div>
              <div style={S.cardTitle}>All Devices by College</div>
              <div style={S.cardSub}>
                {devices.length} device(s) across the network
              </div>
            </div>
          </div>
          {collegeKeys.length === 0 ? (
            <div style={S.centerBox}>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>
                No devices registered yet.
              </div>
            </div>
          ) : (
            collegeKeys.map((college) => (
              <div key={college}>
                <div style={S.branchHeader}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: college.startsWith("⚠") ? "#c2410c" : "#1e293b",
                    }}
                  >
                    {college}
                  </div>
                  <div style={{ fontSize: 11, color: "#94a3b8" }}>
                    {byCollege[college].length} device(s)
                  </div>
                </div>
                {byCollege[college].map((d) => (
                  <div
                    key={d.device_serial}
                    style={{ ...S.bwRow, background: "#fff" }}
                  >
                    <span
                      style={{
                        flex: "0 0 200px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span style={S.deviceIcon}>
                        {deviceTypeMeta(d.device_type).icon}
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.branchName}>{d.device_name}</div>
                        <div style={S.branchSerial}>{d.device_serial}</div>
                      </div>
                    </span>
                    <span
                      style={{
                        ...S.bwCell,
                        flex: "0 0 130px",
                        justifyContent: "flex-start",
                        color: "#64748b",
                        fontSize: 12,
                      }}
                    >
                      {d.branch_name || "—"}
                    </span>
                    <span
                      style={{
                        ...S.bwCell,
                        flex: 1,
                        justifyContent: "flex-start",
                        color: "#94a3b8",
                        fontSize: 12,
                      }}
                    >
                      institute #{d.institute_id || "—"} ·{" "}
                      {d.approval_status === "approved"
                        ? "✓ approved"
                        : "⏳ pending"}
                    </span>
                    <span
                      style={{
                        flex: "0 0 150px",
                        justifyContent: "flex-end",
                        display: "flex",
                      }}
                    >
                      <select
                        style={S.smallSelect}
                        value={d.institute_id || ""}
                        onChange={(e) =>
                          this.assignDevice(d, e.target.value, {})
                        }
                      >
                        <option value="">Reassign…</option>
                        {institutes.map((i) => (
                          <option key={i.institute_id} value={i.institute_id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </>
    );
  }

  // ═══════════════ HOSTEL ATTENDANCE ═══════════════════════════════════════
  // Human-readable hostel night label, e.g. "Wed, 25 Jun 2026".
  hostelDateLabel = () => {
    const day = this.state.hostelDate;
    if (!day) return "";
    const d = new Date(`${day}T00:00:00`);
    if (isNaN(d.getTime())) return day;
    return d.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Download the current night's hostel roll-call as Excel: a summary block on
  // top (date + totals + %) and a per-student detail sheet management can read.
  exportHostel = (filtered, summary) => {
    const dateLbl = this.hostelDateLabel();
    const wb = XLSX.utils.book_new();

    // Sheet 1 — Summary (plain rows, no jargon).
    const summaryRows = [
      ["Hostel Attendance Report"],
      ["Date (night)", dateLbl],
      ["Class filter", this.state.hostelClass || "All classes"],
      ["Section filter", this.state.hostelSection || "All sections"],
      [],
      ["Total Hostel Residents", summary.total],
      ["Present (checked in after 6 PM)", summary.present],
      ["Absent (no evening check-in)", summary.absent],
      ["Attendance %", `${summary.pct}%`],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
    ws1["!cols"] = [{ wch: 34 }, { wch: 26 }];
    XLSX.utils.book_append_sheet(wb, ws1, "Summary");

    // Sheet 2 — Student detail.
    const detail = filtered
      .slice()
      .sort(
        (a, b) =>
          String(a.className).localeCompare(String(b.className), undefined, {
            numeric: true,
          }) ||
          String(a.sectionName).localeCompare(
            String(b.sectionName),
            undefined,
            { numeric: true },
          ) ||
          String(a.roll_no).localeCompare(String(b.roll_no), undefined, {
            numeric: true,
          }),
      )
      .map((h, i) => ({
        "S.No": i + 1,
        Class: h.className,
        Section: h.sectionName,
        Roll: h.roll_no || "",
        Student: h.student_name || "",
        Admission: h.admission_no || "",
        Room: h.room || "",
        "Check-in Time": h.checkIn ? formatTime(h.checkIn) : "",
        Status: h.checkIn ? "Present" : "Absent",
      }));
    const ws2 = XLSX.utils.json_to_sheet(detail);
    ws2["!cols"] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 26 },
      { wch: 16 },
      { wch: 12 },
      { wch: 14 },
      { wch: 10 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Students");

    XLSX.writeFile(wb, `hostel_attendance_${this.state.hostelDate}.xlsx`);
  };

  // Residential students (student_type 2). After-6 PM punch = hostel present.
  // Organised branch / class / section-wise, consistent with the reports.
  renderHolidays() {
    const { holidays, holidaysLoading, holidayForm, holidaySaving, holidayError } = this.state;
    const branchOptions = (this.state.collegeBranches || []).map((b) => ({
      value: b.id,
      label: b.branch_name,
    }));
    const branchName = (id) => {
      const b = (this.state.collegeBranches || []).find((x) => +x.id === +id);
      return b ? b.branch_name : `Branch ${id}`;
    };
    // Search filters by date (17/07/2026, 17-07, 2026…), day-of-week, holiday name or branch.
    const q = String(this.state.holidaySearch || "").trim().toLowerCase();
    const filtered = !q
      ? holidays
      : holidays.filter((h) => {
          const hay = [
            fmtDMYLocal(h.date),
            dowOf(h.date),
            h.holiday_name || "",
            branchName(h.branch_id),
          ].join(" ").toLowerCase();
          return hay.includes(q);
        });
    // Client-side pagination — the whole list is already fetched (one branch-year is small).
    const HOLIDAYS_PER_PAGE = 12;
    const totalPages = Math.max(1, Math.ceil(filtered.length / HOLIDAYS_PER_PAGE));
    const page = Math.min(this.state.holidayPage || 1, totalPages);
    const pageStart = (page - 1) * HOLIDAYS_PER_PAGE;
    const pageRows = filtered.slice(pageStart, pageStart + HOLIDAYS_PER_PAGE);
    const noBranch = !this.selectedBranchIds().length;
    const inputStyle = {
      height: 38,
      borderRadius: 8,
      border: "1.5px solid #e5e7eb",
      padding: "0 12px",
      fontSize: 13.5,
      width: "100%",
      background: "#fff",
    };
    const btn = (bg, color) => ({
      border: "none",
      borderRadius: 8,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      background: bg,
      color,
      cursor: "pointer",
    });

    return (
      <div style={S.card}>
        <div style={S.cardHead}>
          <div>
            <div style={S.cardTitle}>🎌 Holidays</div>
            <div style={S.cardSub}>
              Days excluded from working-day counts in the attendance reports · {this.selectedBranchLabel()}
            </div>
          </div>
          {/* No branch picker here — the page-header BRANCH dropdown already scopes this tab
              via globalBranch (loadHolidays reads selectedBranchCsv()). */}
          <button
            type="button"
            style={{ ...btn("#4f46e5", "#fff"), opacity: noBranch ? 0.5 : 1 }}
            disabled={noBranch}
            onClick={() => this.openHolidayForm(null)}
          >
            + Add Holiday
          </button>
        </div>

        {holidayForm && (
          <div
            style={{
              padding: "16px 24px",
              background: "#f8fafc",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              gap: 12,
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 190 }}>
              <div style={S.branchFilterLbl}>BRANCH</div>
              <select
                style={inputStyle}
                value={holidayForm.branch_id || ""}
                onChange={(e) =>
                  this.setState({ holidayForm: { ...holidayForm, branch_id: e.target.value } })
                }
              >
                <option value="">Select branch</option>
                {branchOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 160 }}>
              <div style={S.branchFilterLbl}>DATE</div>
              <input
                type="date"
                style={inputStyle}
                value={holidayForm.date}
                onChange={(e) =>
                  this.setState({ holidayForm: { ...holidayForm, date: e.target.value } })
                }
              />
            </div>
            <div style={{ minWidth: 220, flex: 1 }}>
              <div style={S.branchFilterLbl}>NAME (OPTIONAL)</div>
              <input
                type="text"
                maxLength={45}
                placeholder="e.g. Independence Day"
                style={inputStyle}
                value={holidayForm.name}
                onChange={(e) =>
                  this.setState({ holidayForm: { ...holidayForm, name: e.target.value } })
                }
              />
            </div>
            <button
              type="button"
              style={{ ...btn("#10b981", "#fff"), opacity: holidaySaving ? 0.6 : 1 }}
              disabled={holidaySaving}
              onClick={this.saveHoliday}
            >
              {holidaySaving ? "Saving…" : holidayForm.id ? "Update" : "Save"}
            </button>
            <button
              type="button"
              style={btn("#e5e7eb", "#374151")}
              onClick={() => this.setState({ holidayForm: null, holidayError: "" })}
            >
              Cancel
            </button>
            {holidayError && (
              <div style={{ color: "#b91c1c", fontSize: 12.5, fontWeight: 600, width: "100%" }}>
                {holidayError}
              </div>
            )}
          </div>
        )}

        {!noBranch && !holidaysLoading && holidays.length > 0 && (
          <div style={{ padding: "12px 24px", borderBottom: "1px solid #f1f5f9" }}>
            <input
              type="text"
              placeholder="Search date, day, name or branch…"
              value={this.state.holidaySearch}
              onChange={(e) => this.setState({ holidaySearch: e.target.value, holidayPage: 1 })}
              style={{ ...inputStyle, maxWidth: 340 }}
            />
          </div>
        )}

        <div style={{ display: "flex", padding: "0 20px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ ...S.th, flex: "0 0 130px" }}>Date</div>
          <div style={{ ...S.th, flex: "0 0 70px" }}>Day</div>
          <div style={{ ...S.th, flex: 1 }}>Holiday</div>
          <div style={{ ...S.th, flex: "0 0 170px" }}>Branch</div>
          <div style={{ ...S.th, flex: "0 0 130px", justifyContent: "flex-end" }}>Actions</div>
        </div>

        {noBranch ? (
          <div style={{ padding: "28px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            Select a branch at the top to view its holidays.
          </div>
        ) : holidaysLoading ? (
          <div style={{ padding: "28px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            Loading…
          </div>
        ) : !holidays.length ? (
          <div style={{ padding: "28px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            No holidays marked. Reports will count every non-Sunday as a working day.
          </div>
        ) : !filtered.length ? (
          <div style={{ padding: "28px 24px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
            No holidays match “{this.state.holidaySearch}”.
          </div>
        ) : (
          pageRows.map((h) => {
            const dow = dowOf(h.date);
            return (
              <div key={h.id} style={{ ...S.summaryRow, cursor: "default" }}>
                <div style={{ ...S.td, flex: "0 0 130px", fontWeight: 700, color: "#1e293b" }}>
                  {fmtDMYLocal(h.date)}
                </div>
                <div style={{ ...S.td, flex: "0 0 70px", color: dow === "Sun" ? "#b91c1c" : "#64748b" }}>
                  {dow}
                </div>
                <div style={{ ...S.td, flex: 1, color: h.holiday_name ? "#1e293b" : "#94a3b8" }}>
                  {h.holiday_name || "(unnamed)"}
                </div>
                <div style={{ ...S.td, flex: "0 0 170px", color: "#64748b" }}>
                  {branchName(h.branch_id)}
                </div>
                <div style={{ ...S.td, flex: "0 0 130px", justifyContent: "flex-end", gap: 8 }}>
                  <button
                    type="button"
                    style={{ ...btn("#eef2ff", "#4f46e5"), padding: "5px 12px" }}
                    onClick={() => this.openHolidayForm(h)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    style={{ ...btn("#fee2e2", "#b91c1c"), padding: "5px 12px" }}
                    onClick={() => this.deleteHoliday(h)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}

        {!noBranch && !holidaysLoading && filtered.length > HOLIDAYS_PER_PAGE && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 24px",
              borderTop: "1px solid #f1f5f9",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12.5, color: "#64748b" }}>
              {pageStart + 1}–{Math.min(pageStart + HOLIDAYS_PER_PAGE, filtered.length)} of {filtered.length}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                type="button"
                style={{ ...btn("#eef2ff", "#4f46e5"), padding: "5px 12px", opacity: page <= 1 ? 0.4 : 1 }}
                disabled={page <= 1}
                onClick={() => this.setState({ holidayPage: page - 1 })}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 12.5, color: "#374151", fontWeight: 700 }}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                style={{ ...btn("#eef2ff", "#4f46e5"), padding: "5px 12px", opacity: page >= totalPages ? 0.4 : 1 }}
                disabled={page >= totalPages}
                onClick={() => this.setState({ holidayPage: page + 1 })}
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderHostel() {
    const {
      hostelRoster,
      hostelLoading,
      hostelDayLoading,
      hostelEveningByAdm,
      studentMap,
      hostelClass,
      hostelSection,
      hostelExpanded,
      hostelDate,
    } = this.state;
    const sm = studentMap || {};
    const dateLbl = this.hostelDateLabel();
    // The latest night that has any meaning: today once 6 PM has passed, else
    // yesterday. This caps "Next" navigation and labels the default night.
    const latestNight = defaultHostelDate();
    const beforeCutoff = new Date().getHours() < HOSTEL_CUTOFF_HOUR;
    const onLatest = hostelDate >= latestNight;

    // Evening (≥ 6 PM) punches for the ONE selected night — fetched per-day so the
    // present/absent counts mean "present this night", never "present some day in a range".
    const eveningByAdm = hostelEveningByAdm || {};

    // Enrich each hosteller with class/section from the student records.
    const all = hostelRoster.map((r) => {
      const stu = sm[String(r.admission_no)] || {};
      return {
        eduStudentId: r.edu_student_id,
        admission_no: r.admission_no,
        student_name: r.student_name || stu.name,
        roll_no: r.roll_no || stu.rollNo,
        className: stu.className || "Unassigned",
        sectionName: stu.sectionName || "—",
        room:
          r.room_name || (r.hostel_room_id ? `Room ${r.hostel_room_id}` : null),
        checkIn: eveningByAdm[String(r.admission_no)] || null,
      };
    });

    const classOptions = [...new Set(all.map((h) => h.className))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true }),
    );
    const sectionOptions = [
      ...new Set(
        all
          .filter((h) => !hostelClass || h.className === hostelClass)
          .map((h) => h.sectionName),
      ),
    ].sort();

    let filtered = all;
    if (hostelClass)
      filtered = filtered.filter((h) => h.className === hostelClass);
    if (hostelSection)
      filtered = filtered.filter((h) => h.sectionName === hostelSection);

    const totalResidents = filtered.length;
    const presentCount = filtered.filter((h) => h.checkIn).length;
    const absentCount = Math.max(0, totalResidents - presentCount);
    const presentPct =
      totalResidents > 0
        ? Math.round((presentCount / totalResidents) * 100)
        : 0;

    // Group by class · section.
    const groups = {};
    filtered.forEach((h) => {
      const key = `${h.className}|${h.sectionName}`;
      if (!groups[key])
        groups[key] = {
          key,
          className: h.className,
          sectionName: h.sectionName,
          students: [],
        };
      groups[key].students.push(h);
    });
    const groupRows = Object.values(groups)
      .map((g) => ({
        ...g,
        total: g.students.length,
        present: g.students.filter((s) => s.checkIn).length,
      }))
      .sort(
        (a, b) =>
          a.className.localeCompare(b.className, undefined, {
            numeric: true,
          }) ||
          a.sectionName.localeCompare(b.sectionName, undefined, {
            numeric: true,
          }),
      );

    const KPIS = [
      {
        label: "Hostel Residents",
        val: totalResidents || "—",
        color: "#6366f1",
        icon: "🛏",
        sub: "residential students",
      },
      {
        label: "Present Tonight",
        val: presentCount,
        color: "#10b981",
        icon: "✅",
        sub: `${presentPct}% · scanned after 6 PM`,
      },
      {
        label: "Absent Tonight",
        val: absentCount,
        color: "#ef4444",
        icon: "🚫",
        sub: "no evening scan",
      },
      {
        label: "Sections",
        val: groupRows.length,
        color: "#8b5cf6",
        icon: "📚",
        sub: "class · section groups",
      },
    ];

    return (
      <>
        <div style={S.kpiRow}>
          {KPIS.map(({ label, val, color, icon, sub }) => (
            <div key={label} style={S.kpiCard}>
              <div style={{ ...S.kpiIcon, background: color + "15" }}>
                {icon}
              </div>
              <div>
                <div style={{ ...S.kpiVal, color }}>{val}</div>
                <div style={S.kpiLabel}>{label}</div>
                <div style={S.kpiSub}>{sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={S.cardHead}>
            <div>
              <div style={S.cardTitle}>🛏 Hostel Night Attendance</div>
              <div style={S.cardSub}>
                One night at a time · a resident is “Present” if they scanned
                at/after 6 PM on the chosen date
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                style={{
                  ...S.ghostBtn,
                  opacity: filtered.length === 0 ? 0.5 : 1,
                }}
                disabled={filtered.length === 0}
                onClick={() =>
                  this.exportHostel(filtered, {
                    total: totalResidents,
                    present: presentCount,
                    absent: absentCount,
                    pct: presentPct,
                  })
                }
              >
                ⬇ Download Report
              </button>
              <button
                style={S.ghostBtn}
                onClick={this.loadHostelRoster}
                disabled={hostelLoading || hostelDayLoading}
              >
                {hostelLoading || hostelDayLoading ? "…" : "↻ Reload"}
              </button>
            </div>
          </div>

          {/* Date banner — states exactly which night is shown, no ambiguity. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              background: "#eef2ff",
              border: "1px solid #e0e7ff",
              borderRadius: 10,
              padding: "10px 14px",
              margin: "4px 0 14px",
            }}
          >
            <span style={{ fontSize: 13, color: "#3730a3", fontWeight: 700 }}>
              🌙 Showing hostel attendance for the night of <b>{dateLbl}</b>
              {onLatest ? (
                <span style={{ color: "#6366f1", fontWeight: 600 }}>
                  {" "}
                  (latest night)
                </span>
              ) : null}
              {hostelDayLoading ? (
                <span style={{ color: "#6366f1", fontWeight: 600 }}>
                  {" "}
                  · loading this night…
                </span>
              ) : null}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: "auto",
                flexWrap: "wrap",
              }}
            >
              <button
                style={{ ...S.ghostBtn, padding: "5px 10px" }}
                onClick={() => {
                  const d = new Date(`${hostelDate}T00:00:00`);
                  d.setDate(d.getDate() - 1);
                  this.setHostelDate(fmt(d));
                }}
              >
                ◀ Prev
              </button>
              <input
                type="date"
                value={hostelDate}
                max={latestNight}
                onChange={(e) =>
                  e.target.value && this.setHostelDate(e.target.value)
                }
                style={{ ...S.smallSelect, padding: "6px 10px" }}
              />
              <button
                style={{
                  ...S.ghostBtn,
                  padding: "5px 10px",
                  opacity: onLatest ? 0.5 : 1,
                }}
                disabled={onLatest}
                onClick={() => {
                  const d = new Date(`${hostelDate}T00:00:00`);
                  d.setDate(d.getDate() + 1);
                  this.setHostelDate(fmt(d));
                }}
              >
                Next ▶
              </button>
              {!onLatest && (
                <button
                  style={{ ...S.ghostBtn, padding: "5px 10px" }}
                  onClick={() => this.setHostelDate(latestNight)}
                >
                  Latest
                </button>
              )}
            </div>
          </div>

          {/* Before-6 PM hint: tonight hasn't started, so we show last night. */}
          {beforeCutoff && onLatest && (
            <div
              style={{
                fontSize: 12,
                color: "#92400e",
                background: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: 8,
                padding: "8px 12px",
                margin: "-6px 0 14px",
              }}
            >
              ⏰ Tonight’s hostel check-ins begin at 6 PM — until then this
              shows <b>last night</b> ({dateLbl}). After 6 PM it switches to
              today automatically.
            </div>
          )}

          {/* Class / Section filters */}
          {!hostelLoading && all.length > 0 && (
            <div style={S.accessToolbar}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}
                >
                  CLASS
                </span>
                <select
                  style={S.smallSelect}
                  value={hostelClass}
                  onChange={(e) =>
                    this.setState({
                      hostelClass: e.target.value,
                      hostelSection: "",
                      hostelExpanded: {},
                    })
                  }
                >
                  <option value="">All classes</option>
                  {classOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <span
                  style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}
                >
                  SECTION
                </span>
                <select
                  style={S.smallSelect}
                  value={hostelSection}
                  onChange={(e) =>
                    this.setState({
                      hostelSection: e.target.value,
                      hostelExpanded: {},
                    })
                  }
                >
                  <option value="">All sections</option>
                  {sectionOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {(hostelClass || hostelSection) && (
                  <button
                    style={S.ghostBtn}
                    onClick={() =>
                      this.setState({
                        hostelClass: "",
                        hostelSection: "",
                        hostelExpanded: {},
                      })
                    }
                  >
                    Clear
                  </button>
                )}
              </div>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {presentCount}/{totalResidents} present this night
              </span>
            </div>
          )}

          {hostelLoading ? (
            <div style={S.centerBox}>
              <div style={S.spinner} />
              <div style={{ color: "#64748b", fontSize: 14, marginTop: 12 }}>
                Loading hostel residents…
              </div>
            </div>
          ) : all.length === 0 ? (
            <div style={S.centerBox}>
              <div style={{ fontSize: 34, marginBottom: 8 }}>🛏</div>
              <div style={{ fontWeight: 700, color: "#1e293b" }}>
                No hostellers (residential students) yet
              </div>
              <div
                style={{
                  color: "#94a3b8",
                  fontSize: 13,
                  marginTop: 4,
                  maxWidth: 460,
                  textAlign: "center",
                }}
              >
                Hostellers are students with <b>student_type = 2</b>. Once set
                in the records, their after-6 PM punches appear here.
              </div>
            </div>
          ) : (
            <>
              {/* class · section summary, expandable to students */}
              <div style={S.bwHead}>
                <span style={{ ...S.bwh, flex: "0 0 220px" }}>
                  CLASS · SECTION
                </span>
                <span
                  style={{ ...S.bwh, flex: "0 0 90px", textAlign: "center" }}
                >
                  HOSTELLERS
                </span>
                <span
                  style={{ ...S.bwh, flex: "0 0 80px", textAlign: "center" }}
                >
                  PRESENT
                </span>
                <span
                  style={{ ...S.bwh, flex: "0 0 80px", textAlign: "center" }}
                >
                  ABSENT
                </span>
                <span style={{ ...S.bwh, flex: 1 }}>% PRESENT</span>
                <span style={{ ...S.bwh, flex: "0 0 40px" }} />
              </div>
              {groupRows.map((g) => {
                const open = !!hostelExpanded[g.key];
                const gPct =
                  g.total > 0 ? Math.round((g.present / g.total) * 100) : 0;
                return (
                  <div key={g.key}>
                    <div
                      style={{
                        ...S.bwRow,
                        background: open ? "#f8f9ff" : "#fff",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        this.setState((s) => ({
                          hostelExpanded: {
                            ...s.hostelExpanded,
                            [g.key]: !s.hostelExpanded[g.key],
                          },
                        }))
                      }
                    >
                      <span
                        style={{
                          flex: "0 0 220px",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                      >
                        {g.className}{" "}
                        <span style={{ color: "#94a3b8", fontWeight: 600 }}>
                          · {g.sectionName}
                        </span>
                      </span>
                      <span
                        style={{
                          ...S.bwCell,
                          flex: "0 0 90px",
                          fontWeight: 800,
                          color: "#1e293b",
                        }}
                      >
                        {g.total}
                      </span>
                      <span style={{ ...S.bwCell, flex: "0 0 80px" }}>
                        <span
                          style={{
                            ...S.miniTag,
                            background: "#f0fdf4",
                            color: "#15803d",
                          }}
                        >
                          {g.present}
                        </span>
                      </span>
                      <span style={{ ...S.bwCell, flex: "0 0 80px" }}>
                        <span
                          style={{
                            ...S.miniTag,
                            background:
                              g.total - g.present > 0 ? "#fef2f2" : "#f8fafc",
                            color:
                              g.total - g.present > 0 ? "#dc2626" : "#94a3b8",
                          }}
                        >
                          {g.total - g.present}
                        </span>
                      </span>
                      <span
                        style={{
                          ...S.bwCell,
                          flex: 1,
                          justifyContent: "flex-start",
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 3,
                        }}
                      >
                        <div style={S.accBarWrap}>
                          <div
                            style={{
                              ...S.accBarFill,
                              width: `${gPct}%`,
                              background:
                                gPct >= 80
                                  ? "#10b981"
                                  : gPct >= 50
                                    ? "#f59e0b"
                                    : "#ef4444",
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>
                          {gPct}%
                        </span>
                      </span>
                      <span
                        style={{
                          ...S.bwCell,
                          flex: "0 0 40px",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          style={{
                            ...S.chevron,
                            transform: open ? "rotate(180deg)" : "none",
                          }}
                        >
                          ▾
                        </span>
                      </span>
                    </div>
                    {open && (
                      <div style={S.detailWrap}>
                        <div style={S.detailHead}>
                          <span style={{ ...S.dth, flex: 1, minWidth: 140 }}>
                            STUDENT
                          </span>
                          <span style={{ ...S.dth, flex: "0 0 120px" }}>
                            ADMISSION
                          </span>
                          <span style={{ ...S.dth, flex: "0 0 90px" }}>
                            ROOM
                          </span>
                          <span style={{ ...S.dth, flex: "0 0 90px" }}>
                            CHECK-IN
                          </span>
                          <span style={{ ...S.dth, flex: "0 0 80px" }}>
                            STATUS
                          </span>
                        </div>
                        {g.students
                          .slice()
                          .sort(
                            (a, b) =>
                              (b.checkIn ? 1 : 0) - (a.checkIn ? 1 : 0) ||
                              String(a.roll_no).localeCompare(
                                String(b.roll_no),
                                undefined,
                                { numeric: true },
                              ),
                          )
                          .map((st, i) => (
                            <div
                              key={st.eduStudentId}
                              style={{
                                ...S.detailRow,
                                background: i % 2 === 0 ? "#fafbff" : "#f3f4f8",
                              }}
                            >
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 140,
                                  fontSize: 12.5,
                                  fontWeight: 700,
                                  color: "#1e293b",
                                }}
                              >
                                {st.student_name}
                                {st.roll_no ? (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      color: "#94a3b8",
                                      marginLeft: 6,
                                    }}
                                  >
                                    Roll {st.roll_no}
                                  </span>
                                ) : null}
                              </span>
                              <span
                                style={{
                                  flex: "0 0 120px",
                                  fontFamily: "monospace",
                                  fontSize: 11.5,
                                  color: "#64748b",
                                }}
                              >
                                {st.admission_no}
                              </span>
                              <span
                                style={{
                                  flex: "0 0 90px",
                                  fontSize: 11.5,
                                  color: st.room ? "#475569" : "#cbd5e1",
                                }}
                              >
                                {st.room || "—"}
                              </span>
                              <span
                                style={{
                                  flex: "0 0 90px",
                                  fontSize: 11.5,
                                  color: st.checkIn ? "#059669" : "#cbd5e1",
                                  fontWeight: st.checkIn ? 700 : 400,
                                }}
                              >
                                {st.checkIn ? formatTime(st.checkIn) : "—"}
                              </span>
                              <span style={{ flex: "0 0 80px" }}>
                                <span
                                  style={{
                                    ...S.miniTag,
                                    fontSize: 11,
                                    background: st.checkIn
                                      ? "#f0fdf4"
                                      : "#fef2f2",
                                    color: st.checkIn ? "#15803d" : "#dc2626",
                                  }}
                                >
                                  {st.checkIn ? "Present" : "Absent"}
                                </span>
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div style={S.syncFootNote}>
                This is <b>one night’s</b> roll-call for <b>{dateLbl}</b>. A
                resident is <b>Present</b> if they scanned at/after <b>6 PM</b>{" "}
                that day (the same scan before 6 PM counts as college
                attendance). Use the date picker above to view another night,
                and <b>Download Report</b> for an Excel copy. Click a class ·
                section row to see each student. Rooms show “—” until allotted.
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  // ═══════════════ REPORTS (ERP integration) ═══════════════════════════════
  // Reuses the ERP's full Class/Monthly/Student/Consolidated/Period-wise report
  // (branch / class / section · daily / weekly / monthly), fed by biometric punches.
  renderReports() {
    const { mainSource } = this.state;
    return (
      <div style={S.reportWrap}>
        <StudentAttendanceReportHome
          key={`${this.state.reportKey}-${mainSource}-${this.selectedBranchCsv() || "all"}`}
          branchId={this.selectedBranchId()}
          branchIds={this.selectedBranchCsv()}
          mainSource={mainSource}
        />
      </div>
    );
  }

  // ═══════════════ MODALS ══════════════════════════════════════════════════
  // Export the KPI drill-down's currently-filtered student list to Excel: a small
  // summary block (category, branch, period, filters, totals) plus a per-student
  // sheet — so the same list the admin sees in the modal downloads as-is.
  exportKpiList = (list, meta, kpiModal) => {
    const { kpiClass, kpiSection } = this.state;
    const wb = XLSX.utils.book_new();

    const summaryRows = [
      ["Attendance Report"],
      ["Category", meta.title],
      ["Branch", this.selectedBranchLabel()],
      ["Period", this.periodLabel()],
      ["Class filter", kpiClass || "All classes"],
      ["Section filter", kpiSection || "All sections"],
      [],
      ["Total students", list.length],
    ];
    if (kpiModal !== "yet") {
      const reg = list.filter((r) => r.registered);
      const avg = reg.length
        ? Math.round(
            reg.reduce((a, r) => a + (r.attendancePct || 0), 0) / reg.length,
          )
        : 0;
      summaryRows.push(["Average attendance %", `${avg}%`]);
      summaryRows.push([
        "With absences",
        list.filter((r) => r.registered && r.absences > 0).length,
      ]);
    }
    const ws1 = XLSX.utils.aoa_to_sheet(summaryRows);
    ws1["!cols"] = [{ wch: 22 }, { wch: 26 }];
    XLSX.utils.book_append_sheet(wb, ws1, "Summary");

    const detail = list.map((r, i) => {
      const row = {
        "S.No": i + 1,
        Class: r.className,
        Section: r.sectionName,
        Roll: r.rollNo || "",
        Student: r.name || `Adm ${r.admission}`,
        Admission: r.admission,
      };
      if (kpiModal === "yet" || !r.registered) {
        row.Status = "Yet to register";
      } else {
        row["Attendance %"] = r.attendancePct;
        row["Days Present"] = r.daysPresent;
        row["Working Days"] = r.numDays;
        row.Absences = r.absences;
        row["Last Check-in"] = r.checkIn ? formatTime(r.checkIn) : "";
      }
      return row;
    });
    const ws2 = XLSX.utils.json_to_sheet(detail);
    ws2["!cols"] = [
      { wch: 6 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 26 },
      { wch: 14 },
      { wch: 13 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb, ws2, "Students");

    const tag = (meta.title || "attendance")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_");
    XLSX.writeFile(wb, `attendance_${kpiModal}_${tag}_${getToday()}.xlsx`);
  };

  // KPI drill-down: clicking a card opens the matching student list + class/section filters.
  renderKpiModal() {
    const { kpiModal, kpiClass, kpiSection } = this.state;
    if (!kpiModal) return null;
    const kpiIsMultiDay = this.periodLabel() !== "Today";
    const META = {
      total: { title: "All Students", color: "#6366f1" },
      present: { title: "Attendance", color: "#10b981" },
      absent: { title: "Students with Absences", color: "#ef4444" },
      yet: { title: "Yet to Register", color: "#f59e0b" },
    };
    const meta = META[kpiModal] || META.total;
    const roster = this.studentRoster();
    let inCat;
    if (kpiModal === "total") inCat = roster;
    else if (kpiModal === "yet") inCat = roster.filter((r) => !r.registered);
    else if (kpiModal === "absent")
      inCat = roster.filter((r) => r.registered && r.absences > 0);
    else inCat = roster.filter((r) => r.registered); // present / attendance
    const classOptions = [...new Set(inCat.map((r) => r.className))].sort(
      (a, b) => a.localeCompare(b, undefined, { numeric: true }),
    );
    const sectionOptions = [
      ...new Set(
        inCat
          .filter((r) => !kpiClass || r.className === kpiClass)
          .map((r) => r.sectionName),
      ),
    ].sort();
    let list = inCat;
    if (kpiClass) list = list.filter((r) => r.className === kpiClass);
    if (kpiSection) list = list.filter((r) => r.sectionName === kpiSection);
    list = list
      .slice()
      .sort(
        (a, b) =>
          a.className.localeCompare(b.className, undefined, {
            numeric: true,
          }) ||
          a.sectionName.localeCompare(b.sectionName, undefined, {
            numeric: true,
          }) ||
          String(a.rollNo || "").localeCompare(
            String(b.rollNo || ""),
            undefined,
            { numeric: true },
          ),
      );
    const badge = (cat) => {
      const m = {
        present: { t: "Present", c: "#15803d", bg: "#f0fdf4" },
        absent: { t: "Absent", c: "#dc2626", bg: "#fef2f2" },
        yet: { t: "Yet to reg", c: "#b45309", bg: "#fffbeb" },
      }[cat];
      return (
        <span
          style={{ ...S.miniTag, fontSize: 11, background: m.bg, color: m.c }}
        >
          {m.t}
        </span>
      );
    };

    return (
      <div
        style={S.modalOverlay}
        onClick={() => this.setState({ kpiModal: null })}
      >
        <div
          style={{ ...S.modalCard, width: "min(780px, 96vw)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={S.modalHead}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  ...S.kpiIcon,
                  background: meta.color + "18",
                  width: 40,
                  height: 40,
                }}
              >
                🎓
              </div>
              <div>
                <div style={S.modalTitle}>{meta.title}</div>
                <div style={S.modalSub}>
                  {list.length} student{list.length !== 1 ? "s" : ""}
                  {this.isAllBranches() ? "" : ` · ${this.selectedBranchLabel()}`}
                  {kpiIsMultiDay
                    ? ` · ${this.periodLabel().toLowerCase()}`
                    : ""}
                </div>
              </div>
            </div>
            <button
              style={S.modalClose}
              onClick={() => this.setState({ kpiModal: null })}
            >
              ✕
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "10px 24px",
              borderBottom: "1px solid #f1f5f9",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}>
              CLASS
            </span>
            <select
              style={S.smallSelect}
              value={kpiClass}
              onChange={(e) =>
                this.setState({ kpiClass: e.target.value, kpiSection: "" })
              }
            >
              <option value="">All</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8" }}>
              SECTION
            </span>
            <select
              style={S.smallSelect}
              value={kpiSection}
              onChange={(e) => this.setState({ kpiSection: e.target.value })}
            >
              <option value="">All</option>
              {sectionOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {(kpiClass || kpiSection) && (
              <button
                style={S.ghostBtn}
                onClick={() => this.setState({ kpiClass: "", kpiSection: "" })}
              >
                Clear
              </button>
            )}
          </div>

          <div style={S.idTHead}>
            <span style={{ ...S.idTh, flex: 1 }}>STUDENT</span>
            <span style={{ ...S.idTh, flex: "0 0 110px" }}>ADMISSION</span>
            <span style={{ ...S.idTh, flex: "0 0 150px" }}>
              CLASS · SECTION
            </span>
            <span style={{ ...S.idTh, flex: "0 0 130px", textAlign: "center" }}>
              {kpiModal === "yet" ? "STATUS" : "ATTENDANCE"}
            </span>
          </div>
          <div style={S.modalBody}>
            {list.length === 0 && (
              <div
                style={{
                  padding: "44px 24px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 13,
                }}
              >
                No students in this view.
              </div>
            )}
            {list.map((r, i) => (
              <div
                key={r.admission}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "9px 24px",
                  borderBottom: "1px solid #f1f5f9",
                  background: i % 2 === 0 ? "#fff" : "#fafbfc",
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}
                  >
                    {r.name || `Adm ${r.admission}`}
                  </span>
                  {r.rollNo ? (
                    <span
                      style={{ fontSize: 10, color: "#94a3b8", marginLeft: 6 }}
                    >
                      Roll {r.rollNo}
                    </span>
                  ) : null}
                  {r.checkIn ? (
                    <span
                      style={{ fontSize: 10, color: "#059669", marginLeft: 6 }}
                    >
                      · {formatTime(r.checkIn)}
                    </span>
                  ) : null}
                </span>
                <span
                  style={{
                    flex: "0 0 110px",
                    fontFamily: "monospace",
                    fontSize: 12,
                    color: "#64748b",
                  }}
                >
                  {r.admission}
                </span>
                <span
                  style={{ flex: "0 0 150px", fontSize: 12, color: "#475569" }}
                >
                  {r.className} · {r.sectionName}
                </span>
                <span
                  style={{
                    flex: "0 0 130px",
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {r.registered ? (
                    <>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color:
                            r.attendancePct >= 80
                              ? "#15803d"
                              : r.attendancePct >= 50
                                ? "#b45309"
                                : "#dc2626",
                        }}
                      >
                        {r.attendancePct}%
                      </span>
                      <span style={{ fontSize: 10, color: "#94a3b8" }}>
                        {r.daysPresent}/{r.numDays}d
                        {r.absences > 0 ? ` · ${r.absences} abs` : ""}
                      </span>
                    </>
                  ) : (
                    badge("yet")
                  )}
                </span>
              </div>
            ))}
          </div>
          <div style={S.modalFoot}>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              {list.length} of {inCat.length} shown
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                style={{ ...S.ghostBtn, opacity: list.length === 0 ? 0.5 : 1 }}
                disabled={list.length === 0}
                onClick={() => this.exportKpiList(list, meta, kpiModal)}
              >
                ⬇ Download Excel
              </button>
              <button
                style={S.applyBtn}
                onClick={() => this.setState({ kpiModal: null })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderEmpModal() {
    const { empModal } = this.state;
    if (!empModal) return null;
    const recs = empModal.records;
    const totalDays = recs.length;
    let otCnt = 0,
      lateCnt = 0,
      vlCnt = 0,
      totalMins = 0,
      durCnt = 0;
    recs.forEach((r) => {
      const s = arrivalStatus(r.firstIn);
      if (s.key === "ontime") otCnt++;
      else if (s.key === "late") lateCnt++;
      else if (s.key === "verylate") vlCnt++;
      const m = durToMins(r.duration);
      if (m > 0) {
        totalMins += m;
        durCnt++;
      }
    });
    const avgDurEmp =
      durCnt > 0 ? minsToStr(Math.round(totalMins / durCnt)) : "—";
    const lateTotalCnt = lateCnt + vlCnt;
    const cs = recs.find((r) => r.className);
    const classSection = cs
      ? `${cs.className}${cs.sectionName ? " · " + cs.sectionName : ""}`
      : "";
    return (
      <div
        style={S.modalOverlay}
        onClick={() => this.setState({ empModal: null })}
      >
        <div
          style={{ ...S.modalCard, width: "min(820px, 96vw)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={S.modalHead}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={S.modalAvatar}>👤</div>
              <div>
                <div
                  style={{ fontSize: 18, fontWeight: 800, color: "#1e293b" }}
                >
                  {empModal.name}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                  Adm {empModal.userId} · {empModal.branchName}
                  {classSection ? ` · ${classSection}` : ""}
                  {empModal.employee_type && (
                    <span style={S.typeBadge}>{empModal.employee_type}</span>
                  )}
                </div>
              </div>
            </div>
            <button
              style={S.modalClose}
              onClick={() => this.setState({ empModal: null })}
            >
              ✕
            </button>
          </div>
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #f1f5f9",
              flexShrink: 0,
            }}
          >
            {[
              { label: "Days Present", val: totalDays, color: "#6366f1" },
              { label: "On Time", val: otCnt, color: "#10b981" },
              { label: "Late", val: lateTotalCnt, color: "#f59e0b" },
              { label: "Avg Duration", val: avgDurEmp, color: "#0ea5e9" },
            ].map(({ label, val, color }) => (
              <div
                key={label}
                style={{
                  flex: 1,
                  padding: "14px 20px",
                  borderRight: "1px solid #f1f5f9",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 800, color }}>
                  {val}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  {label}
                </div>
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              padding: "8px 24px",
              background: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              flexShrink: 0,
            }}
          >
            {[
              { label: "DATE", w: "0 0 140px" },
              { label: "CHECK IN", w: "0 0 90px" },
              { label: "CHECK OUT", w: "0 0 90px" },
              { label: "DURATION", w: "0 0 90px" },
              { label: "STATUS", w: "0 0 100px" },
              { label: "PUNCHES", w: "0 0 70px" },
              { label: "TIMELINE", w: 1 },
            ].map(({ label, w }) => (
              <span
                key={label}
                style={{
                  flex: w,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  padding: "0 4px",
                }}
              >
                {label}
              </span>
            ))}
          </div>
          <div style={S.modalBody}>
            {recs.length === 0 && (
              <div
                style={{
                  padding: "48px 24px",
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: 13,
                }}
              >
                No attendance records found.
              </div>
            )}
            {recs.map((r, i) => {
              const status = arrivalStatus(r.firstIn);
              const bar = getBarPos(r.firstIn, r.lastOut);
              const dateObj = new Date(
                ...r.date.split("-").map((v, idx) => (idx === 1 ? +v - 1 : +v)),
              );
              const dayName = dateObj.toLocaleDateString("en-IN", {
                weekday: "short",
              });
              return (
                <div
                  key={r.date}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "9px 24px",
                    borderBottom: "1px solid #f1f5f9",
                    background: i % 2 === 0 ? "#fff" : "#fafbfc",
                  }}
                >
                  <div
                    style={{
                      flex: "0 0 140px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div style={S.modalDateBadge}>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: "#fff",
                          lineHeight: 1,
                        }}
                      >
                        {dateObj.getDate()}
                      </span>
                      <span
                        style={{
                          fontSize: 7,
                          fontWeight: 700,
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        {dateObj.toLocaleDateString("en-IN", {
                          month: "short",
                        })}
                      </span>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                      >
                        {dayName}
                      </div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>
                        {r.date}
                      </div>
                    </div>
                  </div>
                  <span
                    style={{
                      flex: "0 0 90px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#059669",
                      padding: "0 4px",
                    }}
                  >
                    {formatTime(r.firstIn)}
                  </span>
                  <span
                    style={{
                      flex: "0 0 90px",
                      fontSize: 13,
                      fontWeight: r.lastOut ? 700 : 400,
                      color: r.lastOut ? "#dc2626" : "#94a3b8",
                      padding: "0 4px",
                    }}
                  >
                    {r.lastOut ? formatTime(r.lastOut) : "—"}
                  </span>
                  <span
                    style={{
                      flex: "0 0 90px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#475569",
                      padding: "0 4px",
                    }}
                  >
                    {r.duration || "—"}
                  </span>
                  <span style={{ flex: "0 0 100px", padding: "0 4px" }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: 20,
                        padding: "2px 10px",
                        background: status.color + "18",
                        color: status.color,
                      }}
                    >
                      {status.label}
                    </span>
                  </span>
                  <span
                    style={{
                      flex: "0 0 70px",
                      textAlign: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#94a3b8",
                      padding: "0 4px",
                    }}
                  >
                    {r.count}×
                  </span>
                  <div style={{ flex: 1, padding: "0 4px" }}>
                    <div style={S.miniBarTrack}>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: `${((SHIFT_START_MIN - BAR_START) / BAR_RANGE) * 100}%`,
                          width: `${((SHIFT_END_MIN - SHIFT_START_MIN) / BAR_RANGE) * 100}%`,
                          background: "rgba(99,102,241,0.1)",
                        }}
                      />
                      {bar && (
                        <div
                          style={{
                            ...S.miniBarFill,
                            left: bar.left,
                            width: bar.width,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={S.modalFoot}>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              {empModal.branchName} · {totalDays} day
              {totalDays !== 1 ? "s" : ""} in period
            </span>
            <button
              style={S.applyBtn}
              onClick={() => this.setState({ empModal: null })}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderIdModal() {
    const {
      showIdModal,
      idRows,
      idLoading,
      idSaving,
      idSaveError,
      idDeviceName,
      idSerial,
    } = this.state;
    if (!showIdModal) return null;
    return (
      <div
        style={S.modalOverlay}
        onClick={() => this.setState({ showIdModal: false })}
      >
        <div style={S.modalCard} onClick={(e) => e.stopPropagation()}>
          <div style={S.modalHead}>
            <div>
              <div style={S.modalTitle}>Manage IDs — {idDeviceName}</div>
              <div style={S.modalSub}>
                Students auto-link by admission no · classify others as
                faculty/staff · {idSerial}
              </div>
            </div>
            <button
              style={S.modalClose}
              onClick={() => this.setState({ showIdModal: false })}
            >
              ✕
            </button>
          </div>
          {idLoading ? (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 48 }}
            >
              <div style={S.spinner} />
            </div>
          ) : (
            <>
              <div style={S.idTHead}>
                <span style={{ ...S.idTh, flex: "0 0 90px" }}>
                  ADMISSION NO
                </span>
                <span style={{ ...S.idTh, flex: 1 }}>STUDENT / NAME</span>
                <span style={{ ...S.idTh, flex: "0 0 130px" }}>TYPE</span>
                <span
                  style={{ ...S.idTh, flex: "0 0 80px", textAlign: "center" }}
                >
                  STATUS
                </span>
              </div>
              <div style={S.modalBody}>
                {idRows.map((row, i) => (
                  <div
                    key={row.id}
                    style={{
                      ...S.idRow,
                      background: i % 2 === 0 ? "#fff" : "#fafbfc",
                    }}
                  >
                    <span
                      style={{
                        flex: "0 0 90px",
                        fontSize: 14,
                        fontWeight: 800,
                        color: "#4f46e5",
                        fontFamily: "monospace",
                      }}
                    >
                      {row.id}
                    </span>
                    {row.student ? (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#1e293b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.student.name}
                        </div>
                        <div style={{ fontSize: 10.5, color: "#94a3b8" }}>
                          {row.student.className
                            ? `${row.student.className}${row.student.sectionName ? " · " + row.student.sectionName : ""} · `
                            : ""}
                          matched by admission no
                        </div>
                      </div>
                    ) : (
                      <input
                        style={S.idInput}
                        value={row.name}
                        placeholder="Enter faculty / staff name…"
                        onChange={(e) =>
                          this.updateIdRow(row.id, "name", e.target.value)
                        }
                      />
                    )}
                    {row.student ? (
                      <span style={{ flex: "0 0 130px" }}>
                        <span
                          style={{
                            ...S.miniTag,
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            fontSize: 11,
                          }}
                        >
                          Student
                        </span>
                      </span>
                    ) : (
                      <select
                        style={S.idSelect}
                        value={row.type === "employee" ? "faculty" : row.type}
                        onChange={(e) =>
                          this.updateIdRow(row.id, "type", e.target.value)
                        }
                      >
                        <option value="student">Student</option>
                        <option value="faculty">Faculty / Staff</option>
                      </select>
                    )}
                    <span
                      style={{
                        flex: "0 0 80px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {row.student ? (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 12,
                            padding: "3px 9px",
                            background: "#f0fdf4",
                            color: "#15803d",
                          }}
                        >
                          ✓ Linked
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 12,
                            padding: "3px 9px",
                            background:
                              row.status === "saved"
                                ? "#f0fdf4"
                                : row.status === "new"
                                  ? "#eff6ff"
                                  : "#fff7ed",
                            color:
                              row.status === "saved"
                                ? "#15803d"
                                : row.status === "new"
                                  ? "#1d4ed8"
                                  : "#c2410c",
                          }}
                        >
                          {row.status === "saved"
                            ? "Saved"
                            : row.status === "new"
                              ? "New"
                              : "Modified"}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
                {idRows.length === 0 && (
                  <div
                    style={{
                      padding: "36px 24px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: 13,
                    }}
                  >
                    No device IDs found yet.
                  </div>
                )}
                {idRows.length > 0 && (
                  <div
                    style={{
                      padding: "10px 24px",
                      fontSize: 10.5,
                      color: "#94a3b8",
                      borderTop: "1px solid #f1f5f9",
                      lineHeight: 1.5,
                    }}
                  >
                    IDs matching a student by <b>admission number</b> are linked
                    automatically (✓). For an unmatched ID, name it and mark
                    Faculty/Staff — or, if it's a student missing from records,
                    add them in the ERP and they auto-link.
                  </div>
                )}
              </div>
              {idSaveError && (
                <div style={{ ...S.errBar, margin: "0 24px 4px" }}>
                  ⚠ {idSaveError}
                </div>
              )}
              <div style={S.modalFoot}>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>
                  {
                    idRows.filter(
                      (r) => r.status === "new" || r.status === "dirty",
                    ).length
                  }{" "}
                  unsaved change(s)
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    style={S.modalCancelBtn}
                    onClick={() => this.setState({ showIdModal: false })}
                  >
                    Cancel
                  </button>
                  <button
                    style={{
                      ...S.applyBtn,
                      opacity: idSaving ? 0.7 : 1,
                      minWidth: 110,
                    }}
                    onClick={this.saveIdRows}
                    disabled={idSaving}
                  >
                    {idSaving ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  renderDeviceModal() {
    const { deviceModal } = this.state;
    if (!deviceModal) return null;
    const m = deviceModal;
    const valid =
      (m.device_name || "").trim() && (m.device_serial || "").trim();
    return (
      <div
        style={S.modalOverlay}
        onClick={() => this.setState({ deviceModal: null })}
      >
        <div
          style={{ ...S.modalCard, width: "min(540px, 96vw)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={S.modalHead}>
            <div>
              <div style={S.modalTitle}>
                {m.mode === "add" ? "Add Device" : `Edit ${m.device_name}`}
              </div>
              <div style={S.modalSub}>
                {m.is_primary
                  ? "This is the live device — serial is locked."
                  : "Register a biometric device for a branch."}
              </div>
            </div>
            <button
              style={S.modalClose}
              onClick={() => this.setState({ deviceModal: null })}
            >
              ✕
            </button>
          </div>
          <div
            style={{
              padding: "20px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={S.fieldLbl}>Device Name *</label>
                <input
                  style={S.fieldInput}
                  value={m.device_name}
                  placeholder="e.g. Hostel Gate"
                  onChange={(e) =>
                    this.updateDeviceField("device_name", e.target.value)
                  }
                />
              </div>
              <div style={{ flex: "0 0 150px" }}>
                <label style={S.fieldLbl}>Type</label>
                <select
                  style={S.fieldInput}
                  value={m.device_type}
                  onChange={(e) =>
                    this.updateDeviceField("device_type", e.target.value)
                  }
                >
                  {DEVICE_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.icon} {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label style={S.fieldLbl}>Branch *</label>
              <input
                style={S.fieldInput}
                value={m.branch_name}
                placeholder="e.g. Niampet"
                onChange={(e) =>
                  this.updateDeviceField("branch_name", e.target.value)
                }
                list="branch-list"
              />
              <datalist id="branch-list">
                {this.groupBranches().map((b) => (
                  <option key={b.key} value={b.name} />
                ))}
              </datalist>
            </div>
            <div>
              <label style={S.fieldLbl}>Device Serial *</label>
              <input
                style={{
                  ...S.fieldInput,
                  fontFamily: "monospace",
                  opacity: m.is_primary ? 0.6 : 1,
                }}
                value={m.device_serial}
                placeholder="QJT-XXXX-0000"
                disabled={m.is_primary}
                onChange={(e) =>
                  this.updateDeviceField("device_serial", e.target.value)
                }
              />
            </div>
            <div>
              <label style={S.fieldLbl}>Location</label>
              <input
                style={S.fieldInput}
                value={m.location}
                placeholder="Campus, City"
                onChange={(e) =>
                  this.updateDeviceField("location", e.target.value)
                }
              />
            </div>

            <div style={S.govBox}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: "#475569",
                  marginBottom: 8,
                  letterSpacing: "0.4px",
                }}
              >
                PROCUREMENT &amp; APPROVAL
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={S.fieldLbl}>Procured / deployed by</label>
                  <input
                    style={S.fieldInput}
                    value={m.procured_by}
                    onChange={(e) =>
                      this.updateDeviceField("procured_by", e.target.value)
                    }
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.fieldLbl}>Cost borne by</label>
                  <input
                    style={S.fieldInput}
                    value={m.cost_borne_by}
                    onChange={(e) =>
                      this.updateDeviceField("cost_borne_by", e.target.value)
                    }
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                <div style={{ flex: "0 0 170px" }}>
                  <label style={S.fieldLbl}>Approval status</label>
                  <select
                    style={S.fieldInput}
                    value={m.approval_status}
                    onChange={(e) =>
                      this.updateDeviceField("approval_status", e.target.value)
                    }
                  >
                    <option value="pending">⏳ Pending approval</option>
                    <option value="approved">✓ Approved</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.fieldLbl}>Approval reference</label>
                  <input
                    style={S.fieldInput}
                    value={m.approval_ref}
                    placeholder="PO / approval note ref"
                    onChange={(e) =>
                      this.updateDeviceField("approval_ref", e.target.value)
                    }
                  />
                </div>
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: "#94a3b8",
                  marginTop: 8,
                  lineHeight: 1.5,
                }}
              >
                Devices are procured &amp; deployed by Rizee; cost is borne by
                Vignan separately. Prior approval is required before
                procuring/deploying — devices left as “Pending approval” are
                flagged on the dashboard.
              </div>
            </div>
          </div>
          <div style={S.modalFoot}>
            {m.mode === "edit" && !m.is_primary ? (
              <button
                style={{
                  ...S.modalCancelBtn,
                  color: "#dc2626",
                  borderColor: "#fecaca",
                }}
                onClick={() => this.removeDevice({ id: m.id, is_primary: 0 })}
              >
                Remove Device
              </button>
            ) : (
              <span style={{ fontSize: 12, color: "#94a3b8" }}>
                {this.backendDevices
                  ? "Saved to server"
                  : "Saved on this browser"}
              </span>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={S.modalCancelBtn}
                onClick={() => this.setState({ deviceModal: null })}
              >
                Cancel
              </button>
              <button
                style={{ ...S.applyBtn, opacity: valid ? 1 : 0.5 }}
                onClick={this.saveDevice}
                disabled={!valid}
              >
                {m.mode === "add" ? "Add Device" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: { padding: "18px 0 40px" },
  pageHdr: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  brandMark: {
    width: 46,
    height: 46,
    borderRadius: 12,
    background: "linear-gradient(135deg,#6366f1,#4f46e5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    flexShrink: 0,
  },
  pageTitle: { margin: 0, fontWeight: 800, color: "#1e293b", fontSize: 21 },
  pageSub: { margin: "2px 0 0", fontSize: 11.5, color: "#94a3b8" },
  branchFilterLbl: {
    fontSize: 10,
    fontWeight: 800,
    color: "#94a3b8",
    letterSpacing: "0.5px",
    marginBottom: 3,
  },
  branchSelect: {
    border: "1px solid #c7d2fe",
    borderRadius: 9,
    padding: "9px 14px",
    fontSize: 13.5,
    fontWeight: 800,
    color: "#4f46e5",
    background: "#eef2ff",
    outline: "none",
    cursor: "pointer",
    minWidth: 160,
  },
  clockBox: { textAlign: "right" },
  clockTime: {
    fontSize: 26,
    fontWeight: 800,
    color: "#1e293b",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  clockSec: { fontSize: 16, color: "#94a3b8", fontWeight: 700 },
  clockAmpm: { fontSize: 12, color: "#6366f1", fontWeight: 800, marginLeft: 6 },
  clockDate: {
    fontSize: 11.5,
    color: "#94a3b8",
    marginTop: 3,
    fontWeight: 600,
  },

  tabBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 24,
    background: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "12px 20px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
  },
  tab: {
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 9,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 700,
    color: "#64748b",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  tabActive: {
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    color: "#4f46e5",
  },

  pill: {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    padding: "5px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    cursor: "pointer",
  },
  pillActive: {
    background: "#6366f1",
    border: "1px solid #6366f1",
    color: "#fff",
  },
  refreshBtn: {
    width: 34,
    height: 34,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 18,
    cursor: "pointer",
    color: "#6366f1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  markBtn: {
    background: "linear-gradient(135deg,#10b981,#059669)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 12.5,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(16,185,129,0.3)",
  },
  reportWrap: {
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 4px rgba(15,23,42,.07)",
  },

  customRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    background: "#fff",
    borderRadius: 10,
    padding: "10px 16px",
    marginBottom: 12,
    border: "1px solid #e2e8f0",
  },
  customLbl: { fontSize: 12, fontWeight: 700, color: "#475569" },
  dateIn: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
  },
  applyBtn: {
    background: "#6366f1",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "7px 18px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  ghostBtn: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    color: "#475569",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },

  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: 12,
    marginBottom: 14,
  },
  kpiCard: {
    background: "#fff",
    borderRadius: 12,
    padding: "16px 18px",
    boxShadow: "0 1px 4px rgba(15,23,42,.07)",
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  kpiIcon: {
    fontSize: 22,
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  kpiVal: {
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  kpiLabel: { fontSize: 12, fontWeight: 700, color: "#475569", marginTop: 2 },
  kpiSub: { fontSize: 10, color: "#94a3b8", marginTop: 1 },

  card: {
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
    border: "none",
    overflow: "hidden",
    marginBottom: 24,
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    borderBottom: "1px solid #f1f5f9",
    background: "#ffffff",
    flexWrap: "wrap",
    gap: 10,
  },
  cardTitle: { fontSize: 15, fontWeight: 800, color: "#1e293b" },
  cardSub: { fontSize: 11.5, color: "#94a3b8", marginTop: 2 },
  acePill: {
    fontSize: 12,
    fontWeight: 700,
    color: "#4f46e5",
    background: "#eef2ff",
    borderRadius: 20,
    padding: "4px 12px",
  },
  localTag: {
    marginLeft: 8,
    fontSize: 9.5,
    fontWeight: 800,
    color: "#b45309",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 5,
    padding: "1px 6px",
  },
  noticeClose: {
    background: "none",
    border: "none",
    color: "inherit",
    cursor: "pointer",
    float: "right",
    fontSize: 14,
    lineHeight: 1,
  },

  bwHead: {
    display: "flex",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    padding: "0 20px",
  },
  bwh: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "10px 6px",
  },
  bwRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px 20px",
    borderBottom: "1px solid #f1f5f9",
    transition: "background 0.12s",
  },
  bwCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 6px",
    fontSize: 13,
  },
  branchDot: {
    width: 36,
    height: 36,
    borderRadius: 9,
    flexShrink: 0,
    color: "#fff",
    fontWeight: 800,
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textTransform: "uppercase",
  },
  branchName: {
    fontSize: 13.5,
    fontWeight: 800,
    color: "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  branchSerial: {
    fontSize: 10.5,
    color: "#94a3b8",
    fontFamily: "monospace",
    marginTop: 1,
  },
  curBadge: {
    marginLeft: 6,
    fontSize: 8.5,
    fontWeight: 800,
    color: "#15803d",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 5,
    padding: "1px 5px",
    letterSpacing: "0.4px",
    verticalAlign: "middle",
  },
  miniTag: {
    display: "inline-block",
    borderRadius: 14,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 800,
  },

  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 14,
    padding: "3px 10px",
  },
  statusDot2: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },

  deviceBreak: {
    background: "#f8f9ff",
    borderBottom: "1px solid #eef0f7",
    padding: "4px 20px 8px 60px",
  },
  deviceBreakRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    borderBottom: "1px dashed #e8eaf5",
  },

  matrix: { width: "100%", borderCollapse: "collapse", minWidth: 520 },
  mxTh: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "10px 8px",
    textAlign: "center",
    borderBottom: "2px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  mxDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 5,
  },
  mxDateCell: {
    padding: "8px 12px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  mxCell: {
    padding: "6px 8px",
    borderBottom: "1px solid #f1f5f9",
    textAlign: "center",
  },
  mxPersonCell: {
    padding: "8px 12px",
    borderBottom: "1px solid #f1f5f9",
    position: "sticky",
    left: 0,
    background: "#fff",
    minWidth: 180,
  },
  personType: {
    marginLeft: 6,
    fontSize: 9,
    fontWeight: 700,
    color: "#1d4ed8",
    background: "#eff6ff",
    borderRadius: 8,
    padding: "1px 6px",
    textTransform: "capitalize",
  },
  accessChip: {
    border: "none",
    borderRadius: 14,
    padding: "5px 12px",
    fontSize: 11.5,
    fontWeight: 800,
    cursor: "pointer",
    minWidth: 76,
  },

  chartsRow: {
    display: "flex",
    gap: 12,
    padding: "14px 16px",
    flexWrap: "wrap",
    alignItems: "stretch",
  },
  chartCard: {
    background: "#fcfcff",
    borderRadius: 14,
    padding: "14px 16px",
    border: "1px solid #eef0f7",
    minWidth: 0,
  },
  chartHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  chartTitle: { fontSize: 14, fontWeight: 700, color: "#1e293b" },
  chartSub: { fontSize: 11, color: "#94a3b8" },
  chartEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 80,
    color: "#94a3b8",
    fontSize: 13,
  },

  lateRow: { display: "flex", alignItems: "center", gap: 8 },
  lateRank: { fontSize: 11, fontWeight: 800, width: 22, flexShrink: 0 },
  lateName: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lateMeta: {
    fontSize: 9,
    color: "#94a3b8",
    fontFamily: "monospace",
    marginTop: 1,
  },
  lateBarWrap: {
    flex: 1,
    height: 7,
    background: "#f1f5f9",
    borderRadius: 4,
    overflow: "hidden",
  },
  lateBarFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.5s ease",
  },

  tHead: {
    display: "flex",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    borderTop: "1px solid #f1f5f9",
    padding: "0 20px",
  },
  th: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "10px 8px",
    display: "flex",
    alignItems: "center",
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    borderBottom: "1px solid #f1f5f9",
    minHeight: 56,
    cursor: "pointer",
    transition: "background 0.12s",
  },
  td: {
    padding: "10px 8px",
    fontSize: 13,
    display: "flex",
    alignItems: "center",
  },
  dateBadge: {
    width: 38,
    height: 38,
    borderRadius: 9,
    flexShrink: 0,
    background: "linear-gradient(135deg,#6366f1,#4f46e5)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  badgeNum: { fontSize: 15, fontWeight: 800, color: "#fff", lineHeight: 1 },
  badgeMon: {
    fontSize: 8,
    fontWeight: 700,
    color: "rgba(255,255,255,0.75)",
    letterSpacing: "0.3px",
  },
  dateDay: { fontSize: 13, fontWeight: 700, color: "#1e293b" },
  dateFull: { fontSize: 10, color: "#94a3b8", marginTop: 1 },
  presNum: { fontSize: 18, fontWeight: 800, color: "#1e293b" },
  statTag: {
    borderRadius: 20,
    padding: "3px 12px",
    fontSize: 13,
    fontWeight: 800,
  },
  avgTxt: { fontSize: 13, fontWeight: 600, color: "#475569" },
  accBarWrap: {
    height: 8,
    background: "#f1f5f9",
    borderRadius: 4,
    overflow: "hidden",
  },
  accBarFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.4s ease",
  },
  chevron: {
    fontSize: 14,
    color: "#94a3b8",
    transition: "transform 0.2s",
    userSelect: "none",
  },

  detailWrap: { background: "#f0f2ff", borderBottom: "2px solid #e0e4f7" },
  detailHead: {
    display: "flex",
    padding: "6px 20px 5px",
    borderBottom: "1px solid #dde1f5",
  },
  dth: {
    fontSize: 9,
    fontWeight: 700,
    color: "#6366f1",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "0 4px",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    padding: "7px 20px",
    borderBottom: "1px solid #e8eaf5",
  },
  miniBarTrack: {
    position: "relative",
    height: 10,
    background: "#e2e8f0",
    borderRadius: 5,
    overflow: "hidden",
  },
  miniBarFill: {
    position: "absolute",
    top: 0,
    bottom: 0,
    background: "linear-gradient(90deg,#6366f1,#4f46e5)",
    borderRadius: 5,
  },

  centerBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "44px 24px",
  },
  spinner: {
    width: 32,
    height: 32,
    border: "3px solid #e2e8f0",
    borderTopColor: "#6366f1",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  errBar: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 14px",
    color: "#dc2626",
    fontSize: 13,
    fontWeight: 600,
  },

  branchHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px",
    background: "#fafbff",
    borderBottom: "1px solid #eef0f7",
    flexWrap: "wrap",
    gap: 8,
  },
  deviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
    gap: 14,
    padding: 16,
  },
  deviceCard: {
    background: "#fff",
    border: "1px solid #e8ecf3",
    borderRadius: 14,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    boxShadow: "0 1px 4px rgba(15,23,42,.05)",
  },
  deviceIcon: {
    width: 38,
    height: 38,
    borderRadius: 9,
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 19,
    flexShrink: 0,
  },
  serialBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: "#f8fafc",
    border: "1px dashed #d8def0",
    borderRadius: 8,
    padding: "8px 12px",
  },
  govChip: {
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 6,
    padding: "3px 8px",
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
  },
  govBox: {
    background: "#fafbff",
    border: "1px solid #eef0f7",
    borderRadius: 10,
    padding: "14px 16px",
  },
  devStatsRow: { display: "flex", gap: 8 },
  devStat: {
    flex: 1,
    background: "#fafbff",
    borderRadius: 8,
    padding: "8px 4px",
    textAlign: "center",
  },
  devStatVal: {
    fontSize: 18,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
  },
  devStatLbl: { fontSize: 10, color: "#94a3b8", fontWeight: 600, marginTop: 1 },
  devActions: { display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 },

  accessToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 20px",
    borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap",
  },
  searchInput: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
    minWidth: 220,
    width: "100%",
  },
  mapHead: {
    display: "flex",
    background: "#f8fafc",
    borderBottom: "2px solid #e2e8f0",
    padding: "0 20px",
  },
  mapRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 20px",
    borderBottom: "1px solid #f1f5f9",
  },
  searchDrop: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 50,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(15,23,42,0.18)",
    marginTop: 4,
    maxHeight: 260,
    overflowY: "auto",
  },
  searchItem: {
    padding: "8px 12px",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
  },
  bulkRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 20px",
    background: "#fafbff",
    borderBottom: "1px solid #eef0f7",
    flexWrap: "wrap",
  },
  smallSelect: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "7px 10px",
    fontSize: 12.5,
    color: "#1e293b",
    background: "#fff",
    outline: "none",
    cursor: "pointer",
    minWidth: 150,
  },
  legend: {
    display: "flex",
    gap: 18,
    padding: "8px 20px",
    fontSize: 11,
    color: "#64748b",
    flexWrap: "wrap",
    background: "#fcfcff",
  },
  legendDot: {
    display: "inline-block",
    width: 9,
    height: 9,
    borderRadius: "50%",
    marginRight: 5,
    verticalAlign: "middle",
  },

  syncFootNote: {
    fontSize: 11,
    color: "#94a3b8",
    padding: "12px 20px",
    background: "#fafbfc",
    borderTop: "1px solid #f1f5f9",
    lineHeight: 1.5,
  },

  fieldLbl: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    marginBottom: 5,
  },
  fieldInput: {
    width: "100%",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 13,
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    backdropFilter: "blur(3px)",
  },
  modalCard: {
    background: "#fff",
    borderRadius: 18,
    width: "min(680px, 96vw)",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(15,23,42,0.28)",
  },
  modalHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "22px 24px 16px",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
  },
  modalAvatar: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "linear-gradient(135deg,#6366f1,#4f46e5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    flexShrink: 0,
  },
  modalDateBadge: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "linear-gradient(135deg,#6366f1,#4f46e5)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  typeBadge: {
    marginLeft: 8,
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: 10,
    padding: "1px 8px",
    fontWeight: 700,
    textTransform: "capitalize",
  },
  modalTitle: { fontSize: 17, fontWeight: 800, color: "#1e293b" },
  modalSub: { fontSize: 11, color: "#94a3b8", marginTop: 3 },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: 16,
    cursor: "pointer",
    color: "#94a3b8",
    padding: "2px 6px",
    borderRadius: 6,
    lineHeight: 1,
  },
  modalBody: { overflowY: "auto", flex: 1 },
  modalFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    borderTop: "1px solid #f1f5f9",
    background: "#fafbfc",
    borderRadius: "0 0 18px 18px",
    flexShrink: 0,
    gap: 10,
  },
  modalCancelBtn: {
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 18px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    color: "#475569",
  },
  idTHead: {
    display: "flex",
    alignItems: "center",
    padding: "8px 24px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  idTh: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.45px",
  },
  idRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 24px",
    borderBottom: "1px solid #f1f5f9",
  },
  idInput: {
    flex: 1,
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 11px",
    fontSize: 13,
    color: "#1e293b",
    background: "#f8fafc",
    outline: "none",
  },
  idSelect: {
    flex: "0 0 120px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 12,
    color: "#475569",
    background: "#f8fafc",
    outline: "none",
    cursor: "pointer",
  },
};

export default compose(withRouter, withApollo)(AttendanceRegularization);
